/**
 * The AffDice Affiliate, in the affiliate's own language — §2.8a, E37.
 *
 * Three surfaces, in the order the owner named them (E37):
 *
 *   `nav.*`     every tab in the rail
 *   `figure.*`  the label beside a number on the money screens
 *   `refuse.*`  what the portal says when it will not accept something
 *
 * The third is the one that earns the work. A mistranslated column heading is
 * a nuisance; a refusal somebody cannot read is a refusal they route around,
 * and the refusals here are §31.1b's — the ones that stop a wallet address or
 * an IBAN reaching a request log. An affiliate who cannot read "that is a
 * wallet address, not a link" pastes it again.
 *
 * §2.8b: no status, reason code, event type or currency code is in this file.
 * `describeKind()` and `disputeWords()` in `present.ts` turn identifiers into
 * words for display and are a separate job — they translate *from* an
 * identifier rather than translating the identifier, and they stay English
 * until a later batch does the conversion surfaces properly.
 */
import { type Catalogue, type Params, type Translator, tabTranslator } from '@affdice/portal-kit';

export const AFFILIATE_MESSAGES = {
  /* -------------------------------------------------------------- *
   * The rail
   * -------------------------------------------------------------- */
  'nav.dashboard': { en: 'Dashboard', es: 'Panel', 'pt-BR': 'Painel' },
  'nav.links': { en: 'Links', es: 'Enlaces', 'pt-BR': 'Links' },
  'nav.sources': { en: 'Sources', es: 'Fuentes', 'pt-BR': 'Origens' },
  'nav.postbacks': { en: 'Postbacks', es: 'Postbacks', 'pt-BR': 'Postbacks' },
  'nav.terms': { en: 'Terms', es: 'Condiciones', 'pt-BR': 'Condições' },
  // §26.12 (task A8) — declaring media spend on a spend-share deal, and the
  // state of every claim. Beside Terms rather than Commission: it is about
  // what is owed on a deal, not what has already been earned.
  'nav.spend': { en: 'Media spend', es: 'Gasto en medios', 'pt-BR': 'Gasto em mídia' },
  'nav.statement': { en: 'Commission', es: 'Comisión', 'pt-BR': 'Comissão' },
  'nav.statements': { en: 'Statements', es: 'Extractos', 'pt-BR': 'Extratos' },
  'nav.payments': { en: 'Invoices', es: 'Facturas', 'pt-BR': 'Faturas' },
  'nav.history': { en: 'History', es: 'Historial', 'pt-BR': 'Histórico' },
  'nav.disputes': { en: 'Disputes', es: 'Disputas', 'pt-BR': 'Contestações' },
  'nav.players': { en: 'Players', es: 'Jugadores', 'pt-BR': 'Jogadores' },
  // §8.9 — self-service recruiting: a referral link, invitations, and §8.9e's
  // view of who joined through them.
  'nav.subaffiliates': { en: 'Sub-affiliates', es: 'Subafiliados', 'pt-BR': 'Subafiliados' },
  // "Find brands", not "Brands" — see the comment on TABS. Both other
  // languages keep the discovery sense rather than the plain noun.
  'nav.programmes': { en: 'Brands', es: 'Marcas', 'pt-BR': 'Marcas' },
  'nav.support': { en: 'Support', es: 'Soporte', 'pt-BR': 'Suporte' },
  'rail.thisPeriod': { en: 'This period', es: 'Este periodo', 'pt-BR': 'Este período' },
  'nav.account': { en: 'Account', es: 'Cuenta', 'pt-BR': 'Conta' },
  // Headings over runs of the rail's tabs. The dashboard needs none.
  'nav.group.traffic': { en: 'Traffic', es: 'Tráfico', 'pt-BR': 'Tráfego' },
  'nav.group.earnings': { en: 'Earnings', es: 'Ganancias', 'pt-BR': 'Ganhos' },
  'nav.group.network': { en: 'Players and brands', es: 'Jugadores y marcas', 'pt-BR': 'Jogadores e marcas' },

  /* -------------------------------------------------------------- *
   * The labels beside a number
   * -------------------------------------------------------------- */
  /*
   * §30.4d — what this affiliate must reach before a statement is invoiced.
   * Two labels, because the figure has two sources: the minimum their own
   * payment method states, and the operator's general one where it states
   * none. A screen showing one figure without saying which would be telling
   * them a number they cannot check.
   */
  'figure.payout.minimumForMethod': {
    en: 'Minimum for {method}',
    es: 'Mínimo para {method}',
    'pt-BR': 'Mínimo para {method}',
  },
  'figure.payout.minimumGeneral': {
    en: 'Minimum payout',
    es: 'Pago mínimo',
    'pt-BR': 'Pagamento mínimo',
  },
  'figure.impressions': { en: 'Impressions', es: 'Impresiones', 'pt-BR': 'Impressões' },
  'figure.clicks': { en: 'Clicks', es: 'Clics', 'pt-BR': 'Cliques' },
  'brand.unnamed': { en: 'Unnamed brand', es: 'Marca sin nombre', 'pt-BR': 'Marca sem nome' },
  'earnings.title': {
    en: 'Earned so far',
    es: 'Generado hasta ahora',
    'pt-BR': 'Gerado até agora',
  },
  'figure.payableSoFar': {
    en: 'Payable so far',
    es: 'Pagadero hasta ahora',
    'pt-BR': 'Pagável até agora',
  },
  'figure.heldSoFar': { en: 'Held', es: 'Retenido', 'pt-BR': 'Retido' },
  // §30.1a — a period with nothing in it and a period that failed to compute
  // look identical unless one of them says which it is.
  'earnings.none': {
    en: 'Nothing has been earned since the last period closed. Commission appears here as ' +
      'soon as a player qualifies under your terms — whether or not the next period is open yet.',
    es: 'No se ha generado nada desde el cierre del último periodo. La comisión aparece aquí en ' +
      'cuanto un jugador cumple tus condiciones, esté o no abierto ya el siguiente periodo.',
    'pt-BR': 'Nada foi gerado desde o fechamento do último período. A comissão aparece aqui assim ' +
      'que um jogador cumpre os seus termos — esteja ou não o próximo período aberto.',
  },
  'figure.inOpenPeriod': { en: 'In the open period', es: 'En el periodo abierto', 'pt-BR': 'No período aberto' },
  'figure.notInPeriod': { en: 'Not yet in a period', es: 'Aún sin periodo', 'pt-BR': 'Ainda sem período' },
  // Periods are opened by the operator, by hand. Money earned before they
  // open one is still counted and still owed — the run writes it the day a
  // period covering it is opened. Said once, so the figure is not read as
  // one the run refused.
  'earnings.notInPeriodNote': {
    en: 'Not yet in a period: earned on dates your operator has not opened a commission period ' +
      'for. Counted and owed all the same; it moves to Calculated once a period is opened.',
    es: 'Aún sin periodo: generado en fechas para las que tu operador no ha abierto un periodo de ' +
      'comisión. Se cuenta y se adeuda igualmente; pasa a Calculado cuando se abra un periodo.',
    'pt-BR': 'Ainda sem período: gerado em datas para as quais seu operador não abriu um período de ' +
      'comissão. Contado e devido do mesmo modo; passa a Calculado assim que um período for aberto.',
  },
  // §24.2b — who is not earning, and the criterion by name. "Not qualified"
  // with no reason is the support ticket the rule exists to prevent.
  'earnings.notEarning.one': {
    en: '1 player not earning yet — {reason}',
    es: '1 jugador que aún no genera — {reason}',
    'pt-BR': '1 jogador ainda sem gerar — {reason}',
  },
  'earnings.notEarning.other': {
    en: '{n} players not earning yet — {reason}',
    es: '{n} jugadores que aún no generan — {reason}',
    'pt-BR': '{n} jogadores ainda sem gerar — {reason}',
  },
  // The same line when the reason is a condition still to be met, said as
  // awaited: "— KYC approved" alone read as though KYC was already done.
  'earnings.notEarningWaiting.one': {
    en: '1 player not earning yet — waiting for: {reason}',
    es: '1 jugador que aún no genera — falta: {reason}',
    'pt-BR': '1 jogador ainda sem gerar — aguardando: {reason}',
  },
  'earnings.notEarningWaiting.other': {
    en: '{n} players not earning yet — waiting for: {reason}',
    es: '{n} jugadores que aún no generan — falta: {reason}',
    'pt-BR': '{n} jogadores ainda sem gerar — aguardando: {reason}',
  },
  // WP11 — first deposits waiting on a review. Neutral: "under review", never
  // what the review is about.
  'earnings.underReview.one': {
    en: '1 first deposit is under review, so its commission ({amount}) is not in the figures above yet. This notice clears when the review ends.',
    es: '1 primer depósito está en revisión, así que su comisión ({amount}) aún no figura en las cifras de arriba. Este aviso desaparece cuando termine la revisión.',
    'pt-BR': '1 primeiro depósito está em análise, então a comissão dele ({amount}) ainda não está nos valores acima. Este aviso desaparece quando a análise terminar.',
  },
  'earnings.underReview.other': {
    en: '{n} first deposits are under review, so their commission ({amount}) is not in the figures above yet. This notice clears when the review ends.',
    es: '{n} primeros depósitos están en revisión, así que su comisión ({amount}) aún no figura en las cifras de arriba. Este aviso desaparece cuando termine la revisión.',
    'pt-BR': '{n} primeiros depósitos estão em análise, então a comissão deles ({amount}) ainda não está nos valores acima. Este aviso desaparece quando a análise terminar.',
  },
  'earnings.underReview.partial': {
    en: '{n} first deposits are under review. Commission of {amount} on {m} of them is not in the figures above yet. This notice clears when the review ends.',
    es: '{n} primeros depósitos están en revisión. Una comisión de {amount} sobre {m} de ellos aún no figura en las cifras de arriba. Este aviso desaparece cuando termine la revisión.',
    'pt-BR': '{n} primeiros depósitos estão em análise. Uma comissão de {amount} sobre {m} deles ainda não está nos valores acima. Este aviso desaparece quando a análise terminar.',
  },
  'earnings.underReview.noAmount.one': {
    en: '1 first deposit is under review, so its commission is not in the figures above yet. This notice clears when the review ends.',
    es: '1 primer depósito está en revisión, así que su comisión aún no figura en las cifras de arriba. Este aviso desaparece cuando termine la revisión.',
    'pt-BR': '1 primeiro depósito está em análise, então a comissão dele ainda não está nos valores acima. Este aviso desaparece quando a análise terminar.',
  },
  'earnings.underReview.noAmount.other': {
    en: '{n} first deposits are under review, so their commission is not in the figures above yet. This notice clears when the review ends.',
    es: '{n} primeros depósitos están en revisión, así que su comisión aún no figura en las cifras de arriba. Este aviso desaparece cuando termine la revisión.',
    'pt-BR': '{n} primeiros depósitos estão em análise, então a comissão deles ainda não está nos valores acima. Este aviso desaparece quando a análise terminar.',
  },
  'earnings.notEarningNote': {
    en: 'A player earns nothing until every condition of your terms is met. The missing condition ' +
      'is named beside the brand; the terms are on your Terms page.',
    es: 'Un jugador no genera nada hasta cumplir todas las condiciones de tus términos. La condición ' +
      'que falta se indica junto a la marca; los términos están en tu página de Condiciones.',
    'pt-BR': 'Um jogador não gera nada até cumprir todas as condições dos seus termos. A condição ' +
      'que falta é indicada ao lado da marca; os termos estão na sua página de Condições.',
  },
  'reason.noDepositYet': { en: 'no first deposit yet', es: 'aún sin primer depósito', 'pt-BR': 'ainda sem primeiro depósito' },
  'reason.noDeal': { en: 'no terms in force', es: 'sin condiciones en vigor', 'pt-BR': 'sem condições em vigor' },
  'reason.operatorExcluded': {
    en: 'excluded from CPA by the operator',
    es: 'excluido del CPA por el operador',
    'pt-BR': 'excluído do CPA pelo operador',
  },
  'reason.awaitingFxRate': {
    en: 'waiting for an exchange rate to check the minimum deposit',
    es: 'a la espera de un tipo de cambio para comprobar el depósito mínimo',
    'pt-BR': 'aguardando uma taxa de câmbio para verificar o depósito mínimo',
  },
  'earnings.heldUntil': { en: 'held until {date}', es: 'retenido hasta el {date}', 'pt-BR': 'retido até {date}' },
  'earnings.heldUntilRange': {
    en: 'held, releasing {from} – {to}',
    es: 'retenido, se libera del {from} al {to}',
    'pt-BR': 'retido, liberado de {from} a {to}',
  },
  'earnings.noPeriodAmount': {
    en: '{amount} not yet in a period',
    es: '{amount} aún sin periodo',
    'pt-BR': '{amount} ainda sem período',
  },
  'earnings.since': { en: 'Since {date}', es: 'Desde el {date}', 'pt-BR': 'Desde {date}' },
  'earnings.sinceJoining': { en: 'Since you joined', es: 'Desde que te uniste', 'pt-BR': 'Desde que você entrou' },
  'earnings.openPeriod': {
    en: 'Open period {from} – {to}',
    es: 'Periodo abierto {from} – {to}',
    'pt-BR': 'Período aberto {from} – {to}',
  },
  'earnings.noOpenPeriod': {
    en: 'No period open yet',
    es: 'Aún sin periodo abierto',
    'pt-BR': 'Ainda sem período aberto',
  },
  'earnings.pipeline.one': {
    en: '{n} player qualified: this is their acquisition payment only. When the brand next calculates this period it moves to Calculated, with any revenue share they have earned added; nothing for you to do.',
    es: '{n} jugador cumple las condiciones: esto es solo su pago por adquisición. Cuando la marca vuelva a calcular este periodo pasa a Calculado, junto con la participación en ingresos que haya generado; no tienes que hacer nada.',
    'pt-BR': '{n} jogador cumpriu as condições: isto é só o pagamento por aquisição. Quando a marca calcular este período de novo, passa para Calculado, com a participação na receita que ele gerou; você não precisa fazer nada.',
  },
  'earnings.pipeline.other': {
    en: '{n} players qualified: these are their acquisition payments only. When the brand next calculates this period they move to Calculated, with any revenue share they have earned added; nothing for you to do.',
    es: '{n} jugadores cumplen las condiciones: esto es solo su pago por adquisición. Cuando la marca vuelva a calcular este periodo pasan a Calculado, junto con la participación en ingresos que hayan generado; no tienes que hacer nada.',
    'pt-BR': '{n} jogadores cumpriram as condições: isto é só o pagamento por aquisição. Quando a marca calcular este período de novo, passam para Calculado, com a participação na receita que geraram; você não precisa fazer nada.',
  },
  // §24.5 — why a figure the affiliate can see is not a figure they will be
  // paid this period.
  'earnings.heldNote': {
    en: 'Held money is earned and not yet payable. The release date is beside each brand; ' +
      'the hold itself is on your Terms page, and your Commission page lists each held line.',
    es: 'El dinero retenido está generado pero aún no es pagadero. Tu página de ' +
      'Comisiones detalla cada línea retenida con su fecha de liberación.',
    'pt-BR': 'O dinheiro retido está gerado mas ainda não é pagável. A sua página de ' +
      'Comissões lista cada linha retida com a data de liberação.',
  },
  'figure.registrations': { en: 'Registrations', es: 'Registros', 'pt-BR': 'Cadastros' },
  /*
   * §1.4a — "Reporting FTD count and payable FTD count are separate figures
   * and MUST be shown separately." The two labels have to stay obviously
   * different in every language, which is why neither is just "Depósitos".
   */
  'figure.ftds': {
    en: 'First deposits',
    es: 'Primeros depósitos',
    'pt-BR': 'Primeiros depósitos',
  },
  'figure.payableFtds': {
    en: 'Payable deposits',
    es: 'Depósitos pagaderos',
    'pt-BR': 'Depósitos pagáveis',
  },
  // §12.12/§26.11 (task A9) — clicks over impressions (Appendix G.2 `ctr`), a
  // reporting ratio only; see `dashboard()` (screens.ts) for why it reads as
  // a dash rather than 0.00% when nothing was counted.
  'figure.ctr': { en: 'Click-through rate', es: 'Tasa de clics', 'pt-BR': 'Taxa de cliques' },

  /* -------------------------------------------------------------- *
   * Refusals — §11.2a and §31.1b, said before the value exists anywhere
   * -------------------------------------------------------------- */
  'refuse.subId.required': {
    en: 'Name this placement — every link needs at least one label for your list and Sources.',
    es: 'Ponle nombre a esta ubicación: cada enlace necesita al menos una etiqueta para tu lista y para Fuentes.',
    'pt-BR': 'Dê um nome a esta posição — todo link precisa de ao menos um rótulo para a sua lista e para Origens.',
  },
  'refuse.subId.tooLong': {
    en:
      '{length} characters — the limit is {max}. It is refused rather than shortened, ' +
      'because a shortened sub-ID is a different one and your reporting would stop ' +
      'matching ours.',
    es:
      '{length} caracteres: el límite es {max}. Se rechaza en lugar de acortarse, porque ' +
      'un sub-ID acortado es otro distinto y tus informes dejarían de coincidir con los ' +
      'nuestros.',
    'pt-BR':
      '{length} caracteres — o limite é {max}. É recusado em vez de encurtado, porque um ' +
      'sub-ID encurtado é outro e os seus relatórios deixariam de bater com os nossos.',
  },
  'refuse.payout.credentialInLabel': {
    en:
      'That looks like an account number, IBAN or SWIFT code. The platform holds no ' +
      'payment details at all — send those to your operator directly. This box is only ' +
      'a note to yourself, like "Revolut, business".',
    es:
      'Eso parece un número de cuenta, un IBAN o un código SWIFT. La plataforma no guarda ' +
      'ningún dato de pago: envíaselos directamente a tu operador. Esta casilla es solo ' +
      'una nota para ti, como "Revolut, empresa".',
    'pt-BR':
      'Isso parece um número de conta, IBAN ou código SWIFT. A plataforma não guarda ' +
      'nenhum dado de pagamento — envie esses dados diretamente ao seu operador. Este ' +
      'campo é apenas uma anotação sua, como "Revolut, empresa".',
  },
  'refuse.payout.labelTooLong': {
    en: 'Keep it under 60 characters — it is a label, not instructions.',
    es: 'Que no pase de 60 caracteres: es una etiqueta, no unas instrucciones.',
    'pt-BR': 'Mantenha abaixo de 60 caracteres — é um rótulo, não instruções.',
  },
  'refuse.payout.walletAddress': {
    en:
      'That is a wallet address, not a link. The platform stores no address and no key ' +
      'of any kind — put a link here (your billing portal, a shared folder) and send the ' +
      'address itself to your operator directly.',
    es:
      'Eso es una dirección de monedero, no un enlace. La plataforma no almacena ninguna ' +
      'dirección ni ninguna clave — pon aquí un enlace (tu portal de facturación, una ' +
      'carpeta compartida) y envía la dirección directamente a tu operador.',
    'pt-BR':
      'Isso é um endereço de carteira, não um link. A plataforma não armazena nenhum ' +
      'endereço nem nenhuma chave — coloque aqui um link (o seu portal de faturamento, ' +
      'uma pasta compartilhada) e envie o endereço diretamente ao seu operador.',
  },
  'refuse.payout.ibanInLink': {
    en:
      'That link has what looks like an IBAN in it. A query string is still somewhere the ' +
      'platform would be holding it — link to the page, not to the details.',
    es:
      'Ese enlace parece llevar un IBAN dentro. Una cadena de consulta sigue siendo un ' +
      'sitio donde la plataforma lo estaría guardando: enlaza a la página, no a los datos.',
    'pt-BR':
      'Esse link parece ter um IBAN dentro. Uma query string continua sendo um lugar onde ' +
      'a plataforma estaria guardando isso — aponte para a página, não para os dados.',
  },
  'refuse.payout.linkTooLong': {
    en: 'That link is too long — 500 characters at most.',
    es: 'Ese enlace es demasiado largo: 500 caracteres como máximo.',
    'pt-BR': 'Esse link é longo demais — no máximo 500 caracteres.',
  },
  'refuse.payout.httpsOnly': {
    en: 'The link must start with https:// — http and other schemes are not accepted.',
    es: 'El enlace debe empezar por https:// — no se aceptan http ni otros esquemas.',
    'pt-BR': 'O link precisa começar com https:// — http e outros esquemas não são aceitos.',
  },
  'refuse.payout.noDomain': {
    en: 'That link has no domain in it. It should look like https://example.com/…',
    es: 'Ese enlace no tiene dominio. Debería verse como https://example.com/…',
    'pt-BR': 'Esse link não tem domínio. Deveria ser algo como https://example.com/…',
  },
  'refuse.payout.labelWithoutLink': {
    en: 'This names the link — add the link first, or clear this.',
    es: 'Esto le pone nombre al enlace: añade primero el enlace, o borra esto.',
    'pt-BR': 'Isto dá nome ao link — adicione o link primeiro, ou limpe este campo.',
  },
  'refuse.payout.labelIsCredential': {
    en:
      'That is a payment credential, and none may be stored here. Name the link instead ' +
      '— "Our billing portal".',
    es:
      'Eso es una credencial de pago, y aquí no puede guardarse ninguna. Ponle nombre al ' +
      'enlace: "Nuestro portal de facturación".',
    'pt-BR':
      'Isso é uma credencial de pagamento, e nenhuma pode ser guardada aqui. Dê um nome ao ' +
      'link: "Nosso portal de faturamento".',
  },
  'refuse.payout.labelLimit': {
    en: 'Keep it under 60 characters — it names the link, it is not the link.',
    es: 'Que no pase de 60 caracteres: le pone nombre al enlace, no es el enlace.',
    'pt-BR': 'Mantenha abaixo de 60 caracteres — dá nome ao link, não é o link.',
  },

  /* -------------------------------------------------------------- *
   * The language picker — §2.8a, the one control that has to be readable
   * in the language somebody is trying to leave.
   * -------------------------------------------------------------- */
  'language.panel': { en: 'Language', es: 'Idioma', 'pt-BR': 'Idioma' },
  'language.field': { en: 'Write to me in', es: 'Escríbeme en', 'pt-BR': 'Escreva para mim em' },
  'language.hint': {
    en: 'Used for the portal, your statements, your invoices and every message we send.',
    es: 'Se usa en el portal, tus extractos, tus facturas y todos los mensajes que te enviamos.',
    'pt-BR': 'Usado no portal, nos seus extratos, nas suas faturas e em todas as mensagens que enviamos.',
  },
  // Shown only while nothing has been chosen, so it states what is happening
  // rather than offering an action — there is no "no choice" control to
  // return to, which E37 records as open rather than hides.
  'language.default': {
    en: 'You have not chosen a language, so the portal follows your operator’s default ({locale}).',
    es: 'No has elegido idioma, así que el portal sigue el predeterminado de tu operador ({locale}).',
    'pt-BR': 'Você não escolheu um idioma, então o portal segue o padrão do seu operador ({locale}).',
  },
  'language.save': { en: 'Save', es: 'Guardar', 'pt-BR': 'Salvar' },
  'language.saving': { en: 'Saving…', es: 'Guardando…', 'pt-BR': 'Salvando…' },

  // §21.7 — the currency the reports are read in. Worded to answer the
  // question an affiliate actually has about a control with "currency" in its
  // name: does this change what I am paid? It does not (§21.7a, §21.7f).
  'currency.panel': {
    en: 'Report currency',
    es: 'Moneda de los informes',
    'pt-BR': 'Moeda dos relatórios',
  },
  'currency.field': {
    en: 'Show my report figures in',
    es: 'Mostrar las cifras de mis informes en',
    'pt-BR': 'Mostrar os números dos meus relatórios em',
  },
  'currency.original': {
    en: 'Original — as earned, no conversion',
    es: 'Original: tal como se generó, sin conversión',
    'pt-BR': 'Original — como foi gerado, sem conversão',
  },
  'currency.hint': {
    en:
      'Your reports only. It changes nothing you are owed — your statements ' +
      'and invoices keep the amount and currency you are paid in.',
    es:
      'Solo tus informes. No cambia nada de lo que se te debe: tus estados de ' +
      'cuenta y tus facturas mantienen el importe y la moneda en que se te paga.',
    'pt-BR':
      'Só os seus relatórios. Não muda nada do que é devido a você — seus ' +
      'extratos e faturas mantêm o valor e a moeda em que você é pago.',
  },
  'currency.default': {
    en: 'You have not chosen a currency, so your reports follow your operator’s default ({currency}).',
    es: 'No has elegido moneda, así que tus informes siguen el predeterminado de tu operador ({currency}).',
    'pt-BR': 'Você não escolheu uma moeda, então seus relatórios seguem o padrão do seu operador ({currency}).',
  },

  /* -------------------------------------------------------------- *
   * The invitation (§6.14, §6.15) — the screen before there is an account
   *
   * Read by somebody who is not signed in and has no saved language, so it
   * follows the browser's (`preferredShippedLocale` at boot). It is also the
   * first thing an affiliate ever sees of the platform; English here is
   * English at the one moment there is no picker to change it with.
   *
   * The offer's own sentences — what the operator will and will not see, the
   * terms, the restrictions — are composed by api-core and arrive translated
   * or not on the server's terms. They are not in this file because they are
   * not this console's words.
   * -------------------------------------------------------------- */
  'invite.title': { en: 'Invitation', es: 'Invitación', 'pt-BR': 'Convite' },
  'invite.joined': { en: 'Joined', es: 'Te has unido', 'pt-BR': 'Você entrou' },
  'invite.next': { en: 'What happens next', es: 'Qué pasa ahora', 'pt-BR': 'O que acontece agora' },
  'invite.next.newPassword': {
    en: 'Sign in with the email this invitation was sent to and the password you chose.',
    es: 'Inicia sesión con el correo al que se envió esta invitación y la contraseña que elegiste.',
    'pt-BR': 'Entre com o e-mail para o qual este convite foi enviado e a senha que você escolheu.',
  },
  'invite.next.existingPassword': {
    en: 'Sign in with your existing password — this adds a separate profile with this operator.',
    es: 'Inicia sesión con tu contraseña actual: esto añade un perfil aparte con este operador.',
    'pt-BR': 'Entre com sua senha atual — isto adiciona um perfil separado com este operador.',
  },
  'invite.portalUrl': { en: 'Your affiliate portal:', es: 'Tu portal de afiliado:', 'pt-BR': 'Seu portal de afiliado:' },
  // The same two words as the sign-in panel, and deliberately the same message
  // is not reused: this is a link away to another page, not the button on a
  // form, and Portuguese distinguishes them.
  'invite.signIn': { en: 'Sign in', es: 'Iniciar sesión', 'pt-BR': 'Entrar' },
  'brand.logoAlt': { en: '{brand} logo', es: 'Logotipo de {brand}', 'pt-BR': 'Logotipo de {brand}' },
  'invite.headline.brand': { en: '{brand} has invited you', es: '{brand} te ha invitado', 'pt-BR': '{brand} convidou você' },
  'invite.headline.operator': {
    en: '{operator} has invited you',
    es: '{operator} te ha invitado',
    'pt-BR': '{operator} convidou você',
  },
  'invite.openUntil': { en: 'Open until {date}', es: 'Abierta hasta el {date}', 'pt-BR': 'Aberto até {date}' },
  'invite.contractingWith': {
    en: 'Who you would be contracting with',
    es: 'Con quién contratarías',
    'pt-BR': 'Com quem você estaria contratando',
  },
  'invite.programme': {
    en: '{operator}’s affiliate programme',
    es: 'Programa de afiliados de {operator}',
    'pt-BR': 'Programa de afiliados de {operator}',
  },
  'invite.willSee': {
    en: 'What they will see about you',
    es: 'Qué verán sobre ti',
    'pt-BR': 'O que eles verão sobre você',
  },
  'invite.willNotSee': {
    en: 'What they will not see',
    es: 'Qué no verán',
    'pt-BR': 'O que eles não verão',
  },
  'invite.join': { en: 'Join', es: 'Unirte', 'pt-BR': 'Entrar no programa' },
  'invite.field.name': {
    en: 'Your name, as you trade',
    es: 'Tu nombre comercial',
    'pt-BR': 'Seu nome comercial',
  },
  'invite.field.country': { en: 'Country', es: 'País', 'pt-BR': 'País' },
  'invite.field.country.hint': {
    en: 'Two letters, e.g. GB.',
    es: 'Dos letras, p. ej. GB.',
    'pt-BR': 'Duas letras, ex.: GB.',
  },
  'invite.field.password.hint': {
    en: 'This is what you will sign in with.',
    es: 'Con esto iniciarás sesión.',
    'pt-BR': 'É com isto que você vai entrar.',
  },
  // §2.3b — one identity across operators, said before somebody expects to set
  // a second password here.
  'invite.existingAccount': {
    en:
      'You already have an account here, so sign in with the password you use ' +
      'for your other programmes. This adds a separate profile with this ' +
      'operator — nothing is shared between them.',
    es:
      'Ya tienes una cuenta aquí, así que inicia sesión con la contraseña que ' +
      'usas para tus otros programas. Esto añade un perfil aparte con este ' +
      'operador; no se comparte nada entre ellos.',
    'pt-BR':
      'Você já tem uma conta aqui, então entre com a senha que usa nos seus ' +
      'outros programas. Isto adiciona um perfil separado com este operador — ' +
      'nada é compartilhado entre eles.',
  },
  'invite.busy': { en: 'Joining…', es: 'Uniéndote…', 'pt-BR': 'Entrando…' },
  'invite.accept': { en: 'Accept and join', es: 'Aceptar y unirte', 'pt-BR': 'Aceitar e entrar' },
  'invite.accepted': {
    en: "You're in. Sign in with the email this invitation was sent to and the password you chose.",
    es: 'Ya estás dentro. Inicia sesión con el correo al que se envió esta invitación y la contraseña que elegiste.',
    'pt-BR': 'Pronto. Entre com o e-mail para o qual este convite foi enviado e a senha que você escolheu.',
  },
  'invite.closingDeclines': {
    en: 'Closing this page declines nothing — the invitation stays open until it expires.',
    es: 'Cerrar esta página no rechaza nada: la invitación sigue abierta hasta que caduque.',
    'pt-BR': 'Fechar esta página não recusa nada — o convite continua aberto até expirar.',
  },

  /* -------------------------------------------------------------- *
   * Applying to a programme (§6.15) and confirming an address (§6.14)
   *
   * Also pre-session, and reached from a link the operator published — so the
   * reader's browser decides the language and there is nothing saved to
   * consult. `{operator}` is a trading name and is never translated.
   * -------------------------------------------------------------- */
  'apply.loadFailed': {
    en: 'Could not load this programme',
    es: 'No se pudo cargar este programa',
    'pt-BR': 'Não foi possível carregar este programa',
  },
  'apply.loadFailed.panel': { en: 'Nothing to show', es: 'Nada que mostrar', 'pt-BR': 'Nada a mostrar' },
  'apply.closed': {
    en: 'Not taking applications',
    es: 'No admite solicitudes',
    'pt-BR': 'Não está aceitando inscrições',
  },
  'apply.closed.panel': { en: 'Nothing to apply to', es: 'Nada a lo que aplicar', 'pt-BR': 'Nada para se inscrever' },
  // Stands in for a trading name the server did not give, so it reads as a
  // description of the programme rather than as a name.
  'apply.theseBrands': { en: 'These brands', es: 'Estas marcas', 'pt-BR': 'Estas marcas' },
  'apply.sent': { en: 'Check your email', es: 'Revisa tu correo', 'pt-BR': 'Confira seu e-mail' },
  'apply.sent.panel': { en: 'What happens now', es: 'Qué pasa ahora', 'pt-BR': 'O que acontece agora' },
  'apply.sent.fallback': { en: 'Check your email.', es: 'Revisa tu correo.', 'pt-BR': 'Confira seu e-mail.' },
  'apply.title': { en: 'Apply to {operator}', es: 'Solicitar a {operator}', 'pt-BR': 'Inscrever-se em {operator}' },
  'apply.scope': {
    en: 'What they will and will not see',
    es: 'Qué verán y qué no verán',
    'pt-BR': 'O que eles verão e o que não verão',
  },
  'apply.scope.contact': {
    en: 'The contact details you give here.',
    es: 'Los datos de contacto que des aquí.',
    'pt-BR': 'Os dados de contato que você informar aqui.',
  },
  'apply.scope.traffic': {
    en: 'Your traffic, registrations and commission with {operator} only.',
    es: 'Tu tráfico, registros y comisión con {operator} únicamente.',
    'pt-BR': 'Seu tráfego, registros e comissão apenas com {operator}.',
  },
  'apply.scope.notOthers': {
    en: 'Not any other operator you work with, or how many.',
    es: 'Ningún otro operador con el que trabajes, ni cuántos.',
    'pt-BR': 'Nenhum outro operador com quem você trabalha, nem quantos.',
  },
  'apply.scope.notHistory': {
    en: 'Not your history, standing, tier or risk score anywhere else.',
    es: 'Ni tu historial, tu situación, tu nivel o tu puntuación de riesgo en ningún otro sitio.',
    'pt-BR': 'Nem seu histórico, sua situação, seu nível ou sua pontuação de risco em qualquer outro lugar.',
  },
  'apply.panel': { en: 'Apply', es: 'Solicitar', 'pt-BR': 'Inscrever-se' },
  'apply.field.email.hint': {
    en: 'We send a link here to confirm the address.',
    es: 'Enviamos un enlace aquí para confirmar la dirección.',
    'pt-BR': 'Enviamos um link para cá para confirmar o endereço.',
  },
  'apply.field.phone': { en: 'Phone (optional)', es: 'Teléfono (opcional)', 'pt-BR': 'Telefone (opcional)' },
  'apply.field.phone.hint': {
    en: 'If provided, we also send an SMS verification code.',
    es: 'Si lo das, también enviamos un código de verificación por SMS.',
    'pt-BR': 'Se informado, também enviamos um código de verificação por SMS.',
  },
  'apply.busy': { en: 'Sending…', es: 'Enviando…', 'pt-BR': 'Enviando…' },
  'apply.submit': { en: 'Apply', es: 'Solicitar', 'pt-BR': 'Inscrever-se' },
  // §6.5 — said before the button, because somebody who reads applying as
  // joining reads the review period as a rejection.
  'apply.notAcceptance': {
    en:
      'Applying is not the same as being accepted. {operator} reviews every ' +
      'application and decides separately — you will hear either way. Until ' +
      'they do, you can sign in and see your profile, but there are no ' +
      'tracking links and nothing is earning.',
    es:
      'Solicitar no es lo mismo que ser aceptado. {operator} revisa cada ' +
      'solicitud y decide por separado; te avisarán en cualquier caso. Hasta ' +
      'entonces puedes iniciar sesión y ver tu perfil, pero no hay enlaces de ' +
      'seguimiento y no se está generando nada.',
    'pt-BR':
      'Inscrever-se não é o mesmo que ser aceito. {operator} analisa cada ' +
      'inscrição e decide separadamente — você será avisado de todo jeito. Até ' +
      'lá, você pode entrar e ver seu perfil, mas não há links de rastreamento e ' +
      'nada está gerando ganhos.',
  },
  // §8.9b — shown when the URL carries a recruiter's `ref`. The response to
  // applying is identical either way (§6.15), so this is the only thing on
  // the page that differs.
  'apply.referral.note': {
    en: "You're joining through a partner's referral link.",
    es: 'Te estás uniendo a través del enlace de referido de un socio.',
    'pt-BR': 'Você está entrando por meio do link de indicação de um parceiro.',
  },

  'verify.confirmed': { en: 'Address confirmed', es: 'Dirección confirmada', 'pt-BR': 'Endereço confirmado' },
  'verify.invalid': { en: 'That link is not valid', es: 'Ese enlace no es válido', 'pt-BR': 'Esse link não é válido' },
  'verify.next': { en: 'What happens next', es: 'Qué pasa ahora', 'pt-BR': 'O que acontece agora' },
  'verify.whatToDo': { en: 'What to do', es: 'Qué hacer', 'pt-BR': 'O que fazer' },
  // Opening the link confirms nothing by itself: mail scanners open links
  // before the person does, and confirming can approve the account.
  'verify.title': { en: 'Confirm your email', es: 'Confirma tu correo electrónico', 'pt-BR': 'Confirme seu e-mail' },
  'verify.explain': {
    en: 'You asked to join an affiliate programme with this address. Confirm it is yours to finish your application.',
    es: 'Solicitaste unirte a un programa de afiliados con esta dirección. Confirma que es tuya para completar tu solicitud.',
    'pt-BR': 'Você pediu para participar de um programa de afiliados com este endereço. Confirme que ele é seu para concluir sua inscrição.',
  },
  'verify.button': { en: 'Confirm my email', es: 'Confirmar mi correo', 'pt-BR': 'Confirmar meu e-mail' },
  'verify.confirming': { en: 'Confirming…', es: 'Confirmando…', 'pt-BR': 'Confirmando…' },
  // A link lasts 24 hours. The answer to asking for another is the same
  // whatever the link was, so the page cannot say more than the server does.
  'verify.resend': { en: 'Send me a new link', es: 'Envíame un enlace nuevo', 'pt-BR': 'Envie-me um novo link' },
  'verify.resending': { en: 'Sending…', es: 'Enviando…', 'pt-BR': 'Enviando…' },
  // A password chosen when applying is not kept until the address is proven,
  // so the person confirming types it again here, or chooses another.
  'verify.password.title': { en: 'Your password', es: 'Tu contraseña', 'pt-BR': 'Sua senha' },
  'verify.password.hint': {
    en: 'At least 12 characters. The same one you chose when you applied is fine.',
    es: 'Al menos 12 caracteres. Puedes usar la misma que elegiste al solicitar.',
    'pt-BR': 'Pelo menos 12 caracteres. Pode ser a mesma que você escolheu ao se inscrever.',
  },
  'verify.password.button': {
    en: 'Confirm and sign in', es: 'Confirmar e iniciar sesión', 'pt-BR': 'Confirmar e entrar',
  },
  'verify.continue': { en: 'Go to my account', es: 'Ir a mi cuenta', 'pt-BR': 'Ir para minha conta' },
  'account.unconfirmed': {
    en: 'Your email address is not confirmed yet. The link we sent works for 24 hours; if it has expired, ask for a new one.',
    es: 'Tu dirección de correo aún no está confirmada. El enlace que enviamos vale 24 horas; si ha caducado, pide uno nuevo.',
    'pt-BR': 'Seu endereço de e-mail ainda não foi confirmado. O link que enviamos vale 24 horas; se expirou, peça um novo.',
  },
  'account.resendConfirmation': {
    en: 'Resend confirmation email',
    es: 'Reenviar correo de confirmación',
    'pt-BR': 'Reenviar e-mail de confirmação',
  },

  /* -------------------------------------------------------------- *
   * Identifiers turned into words — §42.7, §33.4a, §6.3g
   *
   * The job E37 deferred. `AFFILIATE_STATUS_CHANGED` and `COMMISSION_FIGURE`
   * are constants and stay constants on the wire (§2.8b); what a screen prints
   * *for* one is a sentence, and a sentence in a language the reader does not
   * have is §33.4a's "our system says so" with an extra step.
   *
   * Every one of these has a fallback at its call site — the raw code, or a
   * generic line — and those fallbacks are deliberately not translated: a code
   * this build has never seen should look like a code in every language.
   * -------------------------------------------------------------- */
  'history.AFFILIATE_APPROVED': {
    en: 'Your application was approved',
    es: 'Tu solicitud fue aprobada',
    'pt-BR': 'Sua inscrição foi aprovada',
  },
  'history.AFFILIATE_REJECTED': {
    en: 'Your application was declined',
    es: 'Tu solicitud fue rechazada',
    'pt-BR': 'Sua inscrição foi recusada',
  },
  'history.AFFILIATE_STATUS_CHANGED': {
    en: 'Your account status changed',
    es: 'El estado de tu cuenta cambió',
    'pt-BR': 'O status da sua conta mudou',
  },
  'history.DEAL_PROPOSED': {
    en: 'New terms were proposed to you',
    es: 'Se te propusieron nuevas condiciones',
    'pt-BR': 'Novas condições foram propostas a você',
  },
  'history.DEAL_ACCEPTED': {
    en: 'Terms were accepted',
    es: 'Se aceptaron las condiciones',
    'pt-BR': 'As condições foram aceitas',
  },
  'history.DEAL_VERSION_ACCEPTED': {
    en: 'A new version of your terms took effect',
    es: 'Entró en vigor una nueva versión de tus condiciones',
    'pt-BR': 'Entrou em vigor uma nova versão das suas condições',
  },
  'history.ADJUSTMENT_RAISED': {
    en: 'An adjustment was raised on your account',
    es: 'Se registró un ajuste en tu cuenta',
    'pt-BR': 'Um ajuste foi lançado na sua conta',
  },
  'history.ADJUSTMENT_APPROVED': {
    en: 'An adjustment was approved',
    es: 'Se aprobó un ajuste',
    'pt-BR': 'Um ajuste foi aprovado',
  },
  'history.INVOICE_APPROVED': {
    en: 'An invoice was approved for payment',
    es: 'Se aprobó una factura para su pago',
    'pt-BR': 'Uma fatura foi aprovada para pagamento',
  },
  'history.SETTLEMENT_MARKED': {
    en: 'The operator recorded a payment',
    es: 'El operador registró un pago',
    'pt-BR': 'O operador registrou um pagamento',
  },
  'history.SETTLEMENT_UNMARKED': {
    en: 'The operator withdrew a payment claim',
    es: 'El operador retiró un pago que había declarado',
    'pt-BR': 'O operador retirou um pagamento que havia declarado',
  },
  'history.MANAGER_ASSIGNED': {
    en: 'Your account manager changed',
    es: 'Tu gestor de cuenta cambió',
    'pt-BR': 'Seu gerente de conta mudou',
  },
  'history.unknown': {
    en: 'A change was recorded on your account',
    es: 'Se registró un cambio en tu cuenta',
    'pt-BR': 'Uma mudança foi registrada na sua conta',
  },
  // §42.1 — the reason is required when the change is made, so it exists.
  // Saying none was given is different from saying nothing.
  'history.noReason': {
    en: 'No reason was recorded',
    es: 'No se registró ningún motivo',
    'pt-BR': 'Nenhum motivo foi registrado',
  },
  'history.by.system': { en: 'Automatically', es: 'Automáticamente', 'pt-BR': 'Automaticamente' },
  'history.by.operator': { en: 'Your operator', es: 'Tu operador', 'pt-BR': 'Seu operador' },

  // §30.4 — the absence of an invoice is information, not a failure.
  'statement.carriedForward': {
    en: 'Carried forward — below the payout minimum',
    es: 'Arrastrado: por debajo del mínimo de pago',
    'pt-BR': 'Transportado — abaixo do mínimo de pagamento',
  },
  'statement.superseded': {
    en: 'Replaced by a later version',
    es: 'Reemplazado por una versión posterior',
    'pt-BR': 'Substituído por uma versão posterior',
  },
  'statement.correction': { en: 'Correction', es: 'Corrección', 'pt-BR': 'Correção' },
  'statement.issued': { en: 'Issued', es: 'Emitido', 'pt-BR': 'Emitido' },

  // §31.1a — never "Paid" on our own account. The operator says so; we did not
  // see it, and the sentence has to keep saying whose claim it is.
  'payment.operatorMarkedPaid': {
    en: 'Operator marked paid on {date}',
    es: 'El operador lo marcó como pagado el {date}',
    'pt-BR': 'O operador marcou como pago em {date}',
  },
  'payment.caveat': {
    en:
      'The platform does not process payments and cannot confirm the transfer. ' +
      'This is the operator’s record, not ours.',
    es:
      'La plataforma no procesa pagos y no puede confirmar la transferencia. ' +
      'Este es el registro del operador, no el nuestro.',
    'pt-BR':
      'A plataforma não processa pagamentos e não pode confirmar a transferência. ' +
      'Este é o registro do operador, não o nosso.',
  },
  'payment.overdue.one': {
    en: 'Outstanding — {n} day past due',
    es: 'Pendiente: {n} día de retraso',
    'pt-BR': 'Em aberto — {n} dia de atraso',
  },
  'payment.overdue.other': {
    en: 'Outstanding — {n} days past due',
    es: 'Pendiente: {n} días de retraso',
    'pt-BR': 'Em aberto — {n} dias de atraso',
  },
  'payment.awaiting': { en: 'Awaiting payment', es: 'A la espera de pago', 'pt-BR': 'Aguardando pagamento' },

  // §6.3g — the state, and what it means for the money. The *keys* the switch
  // reads are the server's English words and are untouched; only what is shown
  // moves.
  'status.active': { en: 'Active', es: 'Activa', 'pt-BR': 'Ativa' },
  'status.active.meaning': {
    en: 'Your links are live and commission is accruing normally.',
    es: 'Tus enlaces están activos y la comisión se acumula con normalidad.',
    'pt-BR': 'Seus links estão ativos e a comissão está sendo acumulada normalmente.',
  },
  'status.underReview': { en: 'Under Review', es: 'En revisión', 'pt-BR': 'Em análise' },
  // §6.3a is emphatic that suspension is a review and not a verdict. Both
  // translations keep "held, not lost" as two halves of one clause.
  'status.underReview.meaning': {
    en:
      'Your existing players keep earning and that commission is held, not lost — ' +
      'a review is not a decision. Your account manager can tell you what is ' +
      'being looked at.',
    es:
      'Tus jugadores actuales siguen generando y esa comisión queda retenida, no ' +
      'perdida: una revisión no es una decisión. Tu gestor de cuenta puede decirte ' +
      'qué se está revisando.',
    'pt-BR':
      'Seus jogadores atuais continuam gerando e essa comissão fica retida, não ' +
      'perdida — uma análise não é uma decisão. Seu gerente de conta pode dizer o ' +
      'que está sendo analisado.',
  },
  'status.ended': { en: 'Ended', es: 'Finalizada', 'pt-BR': 'Encerrada' },
  'status.ended.meaning': {
    en:
      'No new commission is accruing. Commission already earned and unpaid is not ' +
      'removed by this — whether it is payable is a separate decision your ' +
      'operator records.',
    es:
      'No se está acumulando comisión nueva. La comisión ya generada y no pagada no ' +
      'desaparece por esto: si es pagadera es una decisión aparte que registra tu ' +
      'operador.',
    'pt-BR':
      'Nenhuma comissão nova está sendo acumulada. A comissão já gerada e não paga ' +
      'não é removida por isso — se ela é pagável é uma decisão à parte que seu ' +
      'operador registra.',
  },
  'status.unknown.meaning': {
    en: 'Ask your account manager what this means for your account.',
    es: 'Pregunta a tu gestor de cuenta qué significa esto para tu cuenta.',
    'pt-BR': 'Pergunte ao seu gerente de conta o que isso significa para a sua conta.',
  },

  'link.revoked': {
    en:
      'This link no longer accepts new clicks. Traffic already sent through it ' +
      'keeps its attribution — replace it wherever it is still live.',
    es:
      'Este enlace ya no acepta clics nuevos. El tráfico que ya pasó por él ' +
      'conserva su atribución: reemplázalo allí donde siga publicado.',
    'pt-BR':
      'Este link não aceita mais cliques novos. O tráfego que já passou por ele ' +
      'mantém a atribuição — substitua-o onde ele ainda estiver no ar.',
  },

  /* §31.1 — the payout methods, in names an affiliate recognises. */
  'payout.method.BANK_TRANSFER': {
    en: 'Bank transfer',
    es: 'Transferencia bancaria',
    'pt-BR': 'Transferência bancária',
  },
  'payout.method.EWALLET': { en: 'E-wallet', es: 'Monedero electrónico', 'pt-BR': 'Carteira digital' },
  'payout.method.CRYPTO': { en: 'Cryptocurrency', es: 'Criptomoneda', 'pt-BR': 'Criptomoeda' },
  'payout.method.CHEQUE': { en: 'Cheque', es: 'Cheque', 'pt-BR': 'Cheque' },
  'payout.method.OTHER': {
    en: 'Something else — agreed with your operator',
    es: 'Otra cosa, acordada con tu operador',
    'pt-BR': 'Outra coisa — combinada com seu operador',
  },

  /* Ch. 33 — what a dispute is about, and where it has got to. */
  'dispute.ATTRIBUTION': {
    en: 'A player attributed elsewhere',
    es: 'Un jugador atribuido a otro',
    'pt-BR': 'Um jogador atribuído a outro',
  },
  'dispute.COMMISSION_FIGURE': {
    en: 'A commission figure',
    es: 'Una cifra de comisión',
    'pt-BR': 'Um valor de comissão',
  },
  'dispute.DEDUCTION': { en: 'A deduction', es: 'Una deducción', 'pt-BR': 'Uma dedução' },
  'dispute.CLAWBACK': { en: 'A clawback', es: 'Una recuperación', 'pt-BR': 'Um estorno' },
  'dispute.HOLD': { en: 'An amount held', es: 'Un importe retenido', 'pt-BR': 'Um valor retido' },
  'dispute.SETTLEMENT': {
    en: 'A payment marked as made',
    es: 'Un pago marcado como realizado',
    'pt-BR': 'Um pagamento marcado como feito',
  },
  'dispute.STATUS_CHANGE': {
    en: 'A change to the account',
    es: 'Un cambio en la cuenta',
    'pt-BR': 'Uma mudança na conta',
  },
  'dispute.RAISED': {
    en: 'Raised — not yet reviewed',
    es: 'Presentada: aún sin revisar',
    'pt-BR': 'Aberta — ainda não analisada',
  },
  // Item 4 — the single-dispute endpoint's word for the same not-yet-reviewed
  // state the list calls RAISED.
  'dispute.OPEN': {
    en: 'Raised — not yet reviewed',
    es: 'Presentada: aún sin revisar',
    'pt-BR': 'Aberta — ainda não analisada',
  },
  'dispute.UNDER_REVIEW': { en: 'Being looked into', es: 'En estudio', 'pt-BR': 'Sendo analisada' },
  'dispute.NEEDS_INFO': {
    en: 'Waiting on more information from you',
    es: 'A la espera de más información tuya',
    'pt-BR': 'Aguardando mais informações suas',
  },
  // §33.9 — left the internal process for external mediation.
  'dispute.ESCALATED': {
    en: 'Escalated to external mediation',
    es: 'Escalada a mediación externa',
    'pt-BR': 'Encaminhada para mediação externa',
  },
  'dispute.ACCEPTED': { en: 'Accepted', es: 'Aceptada', 'pt-BR': 'Aceita' },
  'dispute.DECLINED': { en: 'Declined', es: 'Rechazada', 'pt-BR': 'Recusada' },
  'dispute.RESOLVED': { en: 'Resolved', es: 'Resuelta', 'pt-BR': 'Resolvida' },
  'dispute.REOPENED': { en: 'Reopened', es: 'Reabierta', 'pt-BR': 'Reaberta' },
  'dispute.CLOSED': { en: 'Closed', es: 'Cerrada', 'pt-BR': 'Encerrada' },

  /* -------------------------------------------------------------- *
   * The screens themselves — what a figure is, and why it is there
   * -------------------------------------------------------------- */

  // §1.4a — the gap between reported and payable FTDs, named. Two numbers side
  // by side with no explanation read as an error.
  'dash.ftdGap.one': {
    en: '{n} first deposit is not payable yet. A first deposit is counted as soon as it happens; it becomes payable once it meets every condition of your deal and its hold period ends.',
    es: '{n} primer depósito todavía no es pagadero. Un primer depósito cuenta en cuanto ocurre; pasa a ser pagadero cuando cumple todas las condiciones de tu acuerdo y termina su periodo de retención.',
    'pt-BR': '{n} primeiro depósito ainda não é pagável. Um primeiro depósito conta assim que acontece; passa a ser pagável quando cumpre todas as condições do seu acordo e o período de retenção termina.',
  },
  'dash.ftdGap.other': {
    en: '{n} first deposits are not payable yet. A first deposit is counted as soon as it happens; it becomes payable once it meets every condition of your deal and its hold period ends.',
    es: '{n} primeros depósitos todavía no son pagaderos. Un primer depósito cuenta en cuanto ocurre; pasa a ser pagadero cuando cumple todas las condiciones de tu acuerdo y termina su periodo de retención.',
    'pt-BR': '{n} primeiros depósitos ainda não são pagáveis. Um primeiro depósito conta assim que acontece; passa a ser pagável quando cumpre todas as condições do seu acordo e o período de retenção termina.',
  },

  /* §33.4a — why a statement line is on the statement, in a sentence. */
  'line.acquisition': {
    en: 'A player you sent qualified under your acquisition terms.',
    es: 'Un jugador que enviaste calificó según tus condiciones de adquisición.',
    'pt-BR': 'Um jogador que você enviou qualificou segundo suas condições de aquisição.',
  },
  'line.lead': {
    en: 'A lead you sent met the terms.',
    es: 'Un registro que enviaste cumplió las condiciones.',
    'pt-BR': 'Um lead que você enviou cumpriu as condições.',
  },
  'line.revshare': {
    en: 'Your share of what the operator earned, after the deductions in your terms.',
    es: 'Tu parte de lo que ganó el operador, tras las deducciones de tus condiciones.',
    'pt-BR': 'Sua parte do que o operador ganhou, após as deduções das suas condições.',
  },
  'line.subOverride': {
    en: 'Your share of what an affiliate you introduced earned.',
    es: 'Tu parte de lo que ganó un afiliado que presentaste.',
    'pt-BR': 'Sua parte do que ganhou um afiliado que você indicou.',
  },
  'line.flat': {
    en: 'A fixed amount agreed in your terms.',
    es: 'Un importe fijo acordado en tus condiciones.',
    'pt-BR': 'Um valor fixo acordado nas suas condições.',
  },
  // §28.5 — the placeholder is used only when no reason was recorded; the
  // recorded reason itself is the operator's own words and is not translated.
  'line.adjustment': {
    en: 'A correction the operator made, with their reason recorded.',
    es: 'Una corrección que hizo el operador, con su motivo registrado.',
    'pt-BR': 'Uma correção que o operador fez, com o motivo registrado.',
  },
  'line.clawback.ground': {
    en: 'Clawed back — {ground}.',
    es: 'Recuperado: {ground}.',
    'pt-BR': 'Estornado — {ground}.',
  },
  'line.clawback': {
    en: 'Money taken back — usually a reversed deposit or a voided player.',
    es: 'Dinero recuperado: normalmente un depósito revertido o un jugador anulado.',
    'pt-BR': 'Dinheiro retomado — geralmente um depósito revertido ou um jogador anulado.',
  },
  'line.carryover': {
    en: 'A negative period being carried into the next one, or paid off by this one.',
    es: 'Un periodo negativo que se arrastra al siguiente, o que este salda.',
    'pt-BR': 'Um período negativo sendo transportado para o próximo, ou quitado por este.',
  },
  // A carryover line's sign says which way the deficit moved: below zero it
  // deepened and carries into the next period; above zero this period took
  // some of it back — earned back, or cleared by a reset (a reset is also a
  // positive movement, which is why "repaid" does not stand alone).
  'line.carryover.deficit': {
    en: 'A negative period carried into the next one.',
    es: 'Un periodo negativo que se arrastra al siguiente.',
    'pt-BR': 'Um período negativo transportado para o próximo.',
  },
  'line.carryover.repaid': {
    en: 'This period reduced the deficit carried in: your earnings paid part of it back, or a reset cleared it.',
    es: 'Este periodo redujo el déficit arrastrado: tus ganancias saldaron parte de él, o un reinicio lo canceló.',
    'pt-BR': 'Este período reduziu o déficit transportado: seus ganhos quitaram parte dele, ou uma redefinição o zerou.',
  },
  // §24.9f — its own line, never blended into the commission it rides beside.
  'line.bonusPayout.one': {
    en:
      'A bonus on top of your commission: a player you sent made a qualifying ' +
      'first deposit within {days} day of being credited to you — {value}.',
    es:
      'Un extra sobre tu comisión: un jugador que enviaste hizo un primer depósito ' +
      'que califica dentro de {days} día desde que se te acreditó — {value}.',
    'pt-BR':
      'Um bônus sobre a sua comissão: um jogador que você enviou fez um primeiro ' +
      'depósito qualificado dentro de {days} dia após ser creditado a você — {value}.',
  },
  'line.bonusPayout.other': {
    en:
      'A bonus on top of your commission: a player you sent made a qualifying ' +
      'first deposit within {days} days of being credited to you — {value}.',
    es:
      'Un extra sobre tu comisión: un jugador que enviaste hizo un primer depósito ' +
      'que califica dentro de {days} días desde que se te acreditó — {value}.',
    'pt-BR':
      'Um bônus sobre a sua comissão: um jogador que você enviou fez um primeiro ' +
      'depósito qualificado dentro de {days} dias após ser creditado a você — {value}.',
  },
  'line.bonusPayout.percentage': {
    en: '{rate}% of {basis}',
    es: '{rate}% de {basis}',
    'pt-BR': '{rate}% de {basis}',
  },
  'line.bonusPayout.flat': {
    en: 'a flat amount from your terms',
    es: 'un importe fijo de tus condiciones',
    'pt-BR': 'um valor fixo das suas condições',
  },
  'line.unknown': {
    en: 'See your Terms page for how this is calculated.',
    es: 'Consulta tu página de Condiciones para ver cómo se calcula esto.',
    'pt-BR': 'Veja sua página de Condições para saber como isto é calculado.',
  },

  /* §33.4a — the line types, in the affiliate's language rather than ours. */
  'kind.CPA': { en: 'Per acquisition', es: 'Por adquisición', 'pt-BR': 'Por aquisição' },
  'kind.RECURRING_CPA': {
    en: 'Per acquisition, recurring',
    es: 'Por adquisición, recurrente',
    'pt-BR': 'Por aquisição, recorrente',
  },
  'kind.CPL': { en: 'Per lead', es: 'Por registro', 'pt-BR': 'Por lead' },
  'kind.CPI': { en: 'Per install', es: 'Por instalación', 'pt-BR': 'Por instalação' },
  'kind.REVSHARE': { en: 'Revenue share', es: 'Reparto de ingresos', 'pt-BR': 'Participação na receita' },
  'kind.HYBRID_CPA': {
    en: 'Hybrid — acquisition part',
    es: 'Híbrido: parte de adquisición',
    'pt-BR': 'Híbrido — parte de aquisição',
  },
  'kind.HYBRID_RS': {
    en: 'Hybrid — revenue share part',
    es: 'Híbrido: parte de reparto de ingresos',
    'pt-BR': 'Híbrido — parte de participação na receita',
  },
  'kind.SUB_OVERRIDE': {
    en: 'Sub-affiliate override',
    es: 'Comisión por subafiliado',
    'pt-BR': 'Comissão por subafiliado',
  },
  'kind.FLAT': { en: 'Flat fee', es: 'Tarifa fija', 'pt-BR': 'Taxa fixa' },
  'kind.FEE': { en: 'Fee', es: 'Tarifa', 'pt-BR': 'Taxa' },
  'kind.ADJUSTMENT': { en: 'Adjustment', es: 'Ajuste', 'pt-BR': 'Ajuste' },
  // §20.8 — a clawback is money being taken back, and the word for that is
  // "clawback". Softening it in any language is how a negative line becomes a
  // dispute nobody can explain the origin of.
  'kind.CLAWBACK': { en: 'Clawback', es: 'Recuperación', 'pt-BR': 'Estorno' },
  'kind.CARRYOVER': { en: 'Carried forward', es: 'Arrastrado', 'pt-BR': 'Transportado' },
  'kind.CARRYOVER.deficit': { en: 'Deficit carried forward', es: 'Déficit arrastrado', 'pt-BR': 'Déficit transportado' },
  'kind.CARRYOVER.repaid': { en: 'Deficit repaid or cleared', es: 'Déficit saldado o cancelado', 'pt-BR': 'Déficit quitado ou zerado' },
  'kind.BONUS_PAYOUT': { en: 'Bonus payout', es: 'Pago de bonificación', 'pt-BR': 'Pagamento de bônus' },
  /*
   * A line written when a campaign goal fires (0085). It has been payable
   * since that migration and has never had a name: `describeKind` fell
   * through to the code, so an affiliate's own commission screen listed
   * "GOAL" — in capitals, in every language — beside the money it paid them.
   */
  'kind.GOAL': { en: 'Campaign goal', es: 'Objetivo de campaña', 'pt-BR': 'Meta da campanha' },

  /*
   * §30.10, §31.1a — a statement line's status, in the words the statement
   * PDF uses (statement-pdf.ts LINE_STATE; check-boundaries holds the two
   * together). PAID is the operator's claim, so it reads "Marked paid".
   */
  'lineState.PENDING': { en: 'Pending', es: 'Pendiente', 'pt-BR': 'Pendente' },
  'lineState.EARNED': { en: 'Earned', es: 'Ganada', 'pt-BR': 'Ganha' },
  'lineState.HELD': { en: 'Held', es: 'Retenida', 'pt-BR': 'Retida' },
  'lineState.PAYABLE': { en: 'Payable', es: 'Pagadera', 'pt-BR': 'A pagar' },
  'lineState.PAID': { en: 'Marked paid', es: 'Marcada como pagada', 'pt-BR': 'Marcada como paga' },
  'lineState.REVERSED': { en: 'Reversed', es: 'Revertida', 'pt-BR': 'Revertida' },
  'lineState.VOID': { en: 'Void', es: 'Anulada', 'pt-BR': 'Anulada' },

  /* §20.3a — where the revenue figure came from. */
  'statement.provenance': {
    en:
      'Revenue figures are reported by the brand. The platform applies your ' +
      'commission terms to them and does not calculate revenue itself.',
    es:
      'Las cifras de ingresos las reporta la marca. La plataforma les aplica tus ' +
      'condiciones de comisión y no calcula los ingresos por su cuenta.',
    'pt-BR':
      'Os valores de receita são informados pela marca. A plataforma aplica suas ' +
      'condições de comissão sobre eles e não calcula a receita por conta própria.',
  },

  /* §31.1 — said once, plainly, on the screen that is about money. */
  'payments.disclaimer': {
    en:
      'Payments are made by the operator directly to you. The platform records ' +
      'what is owed and what the operator reports having paid; it holds no ' +
      'payment details and moves no money.',
    es:
      'Los pagos los hace el operador directamente a ti. La plataforma registra lo ' +
      'que se debe y lo que el operador declara haber pagado; no guarda ningún dato ' +
      'de pago y no mueve dinero.',
    'pt-BR':
      'Os pagamentos são feitos pelo operador diretamente a você. A plataforma ' +
      'registra o que é devido e o que o operador declara ter pago; ela não guarda ' +
      'nenhum dado de pagamento e não movimenta dinheiro.',
  },

  /* E27 — per-player reporting is off, and that is a level of detail. */
  'players.cohortOnly': {
    en:
      'Your operator reports at cohort level rather than per player. Your totals ' +
      'are unaffected — this changes the level of detail, not the commission.',
    es:
      'Tu operador informa por cohortes en lugar de por jugador. Tus totales no ' +
      'cambian: esto cambia el nivel de detalle, no la comisión.',
    'pt-BR':
      'Seu operador informa por coorte em vez de por jogador. Seus totais não ' +
      'mudam — isto muda o nível de detalhe, não a comissão.',
  },

  /* §11.1a — why there is nothing recognisable in the URL. */
  'links.opacity': {
    en:
      'Your links carry no readable account or campaign id. That is deliberate: a ' +
      'link that spelled them out would let anyone holding it enumerate the ' +
      "operator's affiliates, and let them alter it. Tracking is unaffected — the " +
      'token identifies you exactly.',
    es:
      'Tus enlaces no llevan ningún identificador legible de cuenta ni de campaña. ' +
      'Es deliberado: un enlace que los deletreara permitiría a cualquiera que lo ' +
      'tuviera enumerar los afiliados del operador, y alterarlo. El seguimiento no ' +
      'se ve afectado: el token te identifica con exactitud.',
    'pt-BR':
      'Seus links não carregam nenhum identificador legível de conta ou campanha. ' +
      'Isso é proposital: um link que os soletrasse deixaria qualquer um que o ' +
      'tivesse enumerar os afiliados do operador, e alterá-lo. O rastreamento não é ' +
      'afetado — o token identifica você exatamente.',
  },

  /* The Terms screen's two standing explanations. */
  'terms.whereNumbersComeFrom': {
    en:
      'The figures behind these lines come from your operator, not from us. They ' +
      'report the revenue your players generated and the costs against it — bonuses ' +
      'and free bets, cashback, game-provider and payment fees, gaming duty, ' +
      'chargebacks — for each day and each brand. We apply only the lines your ' +
      'agreed terms include, in the order shown, and never add one that is not ' +
      'listed here. If a figure looks wrong, raise it on the statement it appeared ' +
      'on and the underlying daily numbers are pulled into the dispute with it.',
    es:
      'Las cifras detrás de estas líneas vienen de tu operador, no de nosotros. Él ' +
      'informa los ingresos que generaron tus jugadores y los costes contra ellos ' +
      '— bonos y apuestas gratis, cashback, comisiones del proveedor de juego y de ' +
      'pago, impuesto del juego, contracargos — para cada día y cada marca. Nosotros ' +
      'aplicamos solo las líneas que incluyen tus condiciones acordadas, en el orden ' +
      'mostrado, y nunca añadimos una que no figure aquí. Si una cifra parece ' +
      'incorrecta, plantéalo en el estado de cuenta en el que apareció y los números ' +
      'diarios subyacentes entran en la disputa con ella.',
    'pt-BR':
      'Os valores por trás destas linhas vêm do seu operador, não de nós. Ele ' +
      'informa a receita que seus jogadores geraram e os custos sobre ela — bônus e ' +
      'apostas grátis, cashback, taxas do provedor de jogo e de pagamento, imposto ' +
      'sobre jogos, chargebacks — para cada dia e cada marca. Nós aplicamos apenas ' +
      'as linhas que as suas condições acordadas incluem, na ordem mostrada, e nunca ' +
      'acrescentamos uma que não esteja listada aqui. Se um valor parecer errado, ' +
      'levante isso no extrato em que ele apareceu e os números diários por trás ' +
      'entram na contestação junto com ele.',
  },
  'terms.whereRulesLive': {
    en:
      'These are the terms that pay you, brand by brand: your own deals, and the ' +
      'standard payout of any campaign you take part in where that payout is what ' +
      'applies to you. Each campaign below says which one pays you there. A tracking ' +
      'link only records who sent the traffic, so creating one never changes what you ' +
      'earn. What is shown here is what is in force.',
    es:
      'Estas son las condiciones que te pagan, marca por marca: tus propios acuerdos y ' +
      'el pago estándar de cualquier campaña en la que participas cuando es ese pago el ' +
      'que se te aplica. Cada campaña indica cuál te paga allí. Un enlace de seguimiento ' +
      'solo registra quién envió el tráfico, así que crear uno nunca cambia lo que ' +
      'ganas. Lo que se muestra aquí es lo que está en vigor.',
    'pt-BR':
      'Estas são as condições que pagam você, marca por marca: seus próprios acordos e ' +
      'o pagamento padrão de qualquer campanha de que você participa quando é esse ' +
      'pagamento que se aplica a você. Cada campanha indica qual paga você lá. Um link ' +
      'de rastreamento só registra quem enviou o tráfego, então criar um nunca muda o ' +
      'que você ganha. O que aparece aqui é o que está em vigor.',
  },
  // §6.16/§6.16a — composed client-side (not sent as server prose) so it
  // reads in the affiliate's own language, the same as whereRulesLive above.
  'terms.agencyNote.one': {
    en:
      'This is an agency account with 1 sub-account. All commission is paid to this ' +
      'account, on this one statement — a sub-account is a reporting label for your ' +
      'own traffic, never a separate payee.',
    es:
      'Esta es una cuenta de agencia con 1 subcuenta. Toda la comisión se paga a esta ' +
      'cuenta, en este único extracto: una subcuenta es una etiqueta de informes para ' +
      'tu propio tráfico, nunca un beneficiario aparte.',
    'pt-BR':
      'Esta é uma conta de agência com 1 subconta. Toda a comissão é paga a esta conta, ' +
      'neste único extrato — uma subconta é um rótulo de relatório para o seu próprio ' +
      'tráfego, nunca um beneficiário separado.',
  },
  'terms.agencyNote.other': {
    en:
      'This is an agency account with {n} sub-accounts. All commission is paid to this ' +
      'account, on this one statement — a sub-account is a reporting label for your ' +
      'own traffic, never a separate payee.',
    es:
      'Esta es una cuenta de agencia con {n} subcuentas. Toda la comisión se paga a esta ' +
      'cuenta, en este único extracto: una subcuenta es una etiqueta de informes para ' +
      'tu propio tráfico, nunca un beneficiario aparte.',
    'pt-BR':
      'Esta é uma conta de agência com {n} subcontas. Toda a comissão é paga a esta ' +
      'conta, neste único extrato — uma subconta é um rótulo de relatório para o seu ' +
      'próprio tráfego, nunca um beneficiário separado.',
  },

  /* §24.2 — the ten criteria, in words rather than the ref table's codes. */
  'criterion.DEPOSIT_SETTLED': { en: 'Deposit settled', es: 'Depósito liquidado', 'pt-BR': 'Depósito liquidado' },
  'criterion.MIN_DEPOSIT': {
    en: 'Minimum deposit amount',
    es: 'Importe mínimo de depósito',
    'pt-BR': 'Valor mínimo de depósito',
  },
  'criterion.KYC_APPROVED': { en: 'KYC approved', es: 'KYC aprobado', 'pt-BR': 'KYC aprovado' },
  'criterion.MIN_TURNOVER': { en: 'Minimum turnover', es: 'Volumen mínimo de apuestas', 'pt-BR': 'Movimentação mínima' },
  'criterion.MIN_BETS': {
    en: 'Minimum number of bets',
    es: 'Número mínimo de apuestas',
    'pt-BR': 'Número mínimo de apostas',
  },
  'criterion.MIN_DAYS_ACTIVE': {
    en: 'Minimum days active',
    es: 'Días mínimos de actividad',
    'pt-BR': 'Dias mínimos de atividade',
  },
  'criterion.NOT_REVERSED': {
    en: 'Deposit not reversed within window',
    es: 'Depósito no revertido dentro del plazo',
    'pt-BR': 'Depósito não revertido dentro do prazo',
  },
  'criterion.NO_FRAUD_FLAG': { en: 'No fraud flag', es: 'Sin marca de fraude', 'pt-BR': 'Sem marcação de fraude' },
  'criterion.GEO_ELIGIBLE': { en: 'Geo eligible', es: 'País admitido', 'pt-BR': 'País elegível' },
  'criterion.PRODUCT_ELIGIBLE': { en: 'First activity on a covered product', es: 'Primera actividad en un producto cubierto', 'pt-BR': 'Primeira atividade em um produto coberto' },
  'criterion.NOT_SELF_EXCLUDED': {
    en: 'Not self-excluded before qualification',
    es: 'Sin autoexclusión antes de calificar',
    'pt-BR': 'Sem autoexclusão antes de qualificar',
  },

  /* §24.9c — the bonus basis, in words rather than a code. */
  'bonusBasis.DEPOSIT': {
    en: 'the qualifying first deposit',
    es: 'el primer depósito que califica',
    'pt-BR': 'o primeiro depósito qualificado',
  },
  'bonusBasis.COMMISSION': {
    en: 'your own commission for that player',
    es: 'tu propia comisión por ese jugador',
    'pt-BR': 'sua própria comissão por aquele jogador',
  },
  'bonusBasis.NGR': { en: "that player's Commissionable NGR", es: 'el NGR comisionable de ese jugador', 'pt-BR': 'o NGR comissionável daquele jogador' },
  'bonusBasis.fallback': {
    en: 'the basis in your terms',
    es: 'la base de tus condiciones',
    'pt-BR': 'a base das suas condições',
  },

  /* The Terms screen's condition rows. */
  'terms.perPlayer': { en: 'Per qualifying player', es: 'Por jugador que califica', 'pt-BR': 'Por jogador qualificado' },
  'terms.revshare': { en: 'Revenue share', es: 'Reparto de ingresos', 'pt-BR': 'Participação na receita' },
  'terms.revshare.value': { en: '{rate}% of {basis}', es: '{rate}% de {basis}', 'pt-BR': '{rate}% de {basis}' },
  // §26.12/§8.10 (task A8) — this deal's condition line, and its optional cap.
  'terms.spendShare': { en: 'Spend share', es: 'Reparto de gasto', 'pt-BR': 'Participação no gasto' },
  'terms.spendShare.value': {
    en: '{rate}% of approved spend',
    es: '{rate}% del gasto aprobado',
    'pt-BR': '{rate}% do gasto aprovado',
  },
  'terms.spendShare.cap': {
    en: 'Period spend cap',
    es: 'Tope de gasto por período',
    'pt-BR': 'Limite de gasto por período',
  },
  'terms.recurringTerm': {
    en: 'Keeps paying for',
    es: 'Sigue pagando durante',
    'pt-BR': 'Continua pagando por',
  },
  'terms.perFtd': {
    en: 'Per qualified first deposit',
    es: 'Por primer depósito calificado',
    'pt-BR': 'Por primeiro depósito qualificado',
  },
  'terms.bonusPayout.one': {
    en: 'Bonus payout, within {days} day of attribution',
    es: 'Pago de bonificación, dentro de {days} día desde la atribución',
    'pt-BR': 'Pagamento de bônus, dentro de {days} dia após a atribuição',
  },
  'terms.bonusPayout.other': {
    en: 'Bonus payout, within {days} days of attribution',
    es: 'Pago de bonificación, dentro de {days} días desde la atribución',
    'pt-BR': 'Pagamento de bônus, dentro de {days} dias após a atribuição',
  },
  'terms.required': { en: 'Required', es: 'Obligatorio', 'pt-BR': 'Obrigatório' },
  'terms.requiredStandard': {
    en: 'Required — standard check',
    es: 'Obligatorio: verificación estándar',
    'pt-BR': 'Obrigatório — verificação padrão',
  },
  'terms.switchedOff': {
    en: 'Switched off for this deal',
    es: 'Desactivado para este acuerdo',
    'pt-BR': 'Desligado para este acordo',
  },
  'terms.minDeposit': {
    en: 'Minimum deposit to qualify',
    es: 'Depósito mínimo para calificar',
    'pt-BR': 'Depósito mínimo para qualificar',
  },
  'terms.timeToQualify': { en: 'Time to qualify', es: 'Plazo para calificar', 'pt-BR': 'Prazo para qualificar' },
  'terms.timeToQualify.value.one': {
    en: '{days} day from registration',
    es: '{days} día desde el registro',
    'pt-BR': '{days} dia a partir do registro',
  },
  'terms.timeToQualify.value.other': {
    en: '{days} days from registration',
    es: '{days} días desde el registro',
    'pt-BR': '{days} dias a partir do registro',
  },
  // §24.3b — the brand counts the deadline from the first deposit.
  'terms.timeToQualify.fromDeposit.one': {
    en: '{days} day from the first deposit',
    es: '{days} día desde el primer depósito',
    'pt-BR': '{days} dia a partir do primeiro depósito',
  },
  'terms.timeToQualify.fromDeposit.other': {
    en: '{days} days from the first deposit',
    es: '{days} días desde el primer depósito',
    'pt-BR': '{days} dias a partir do primeiro depósito',
  },
  // §24.5b — the hold is usually why the answer to "when will I be paid?" is
  // "not yet", so it is a row of its own.
  'terms.held': { en: 'Held before payable', es: 'Retenido antes de ser pagadero', 'pt-BR': 'Retido antes de ser pagável' },
  'terms.held.brand.one': {
    en: '{days} day after qualifying — set by the brand',
    es: '{days} día después de calificar — fijado por la marca',
    'pt-BR': '{days} dia após qualificar — definido pela marca',
  },
  'terms.held.brand.other': {
    en: '{days} days after qualifying — set by the brand',
    es: '{days} días después de calificar — fijado por la marca',
    'pt-BR': '{days} dias após qualificar — definido pela marca',
  },
  'terms.held.operator.one': {
    en: '{days} day after qualifying — your operator’s standard',
    es: '{days} día después de calificar — el estándar de tu operador',
    'pt-BR': '{days} dia após qualificar — o padrão do seu operador',
  },
  'terms.held.operator.other': {
    en: '{days} days after qualifying — your operator’s standard',
    es: '{days} días después de calificar — el estándar de tu operador',
    'pt-BR': '{days} dias após qualificar — o padrão do seu operador',
  },
  'terms.held.default.one': {
    en: '{days} day after qualifying — the platform’s standard hold',
    es: '{days} día después de calificar — la retención estándar de la plataforma',
    'pt-BR': '{days} dia após qualificar — a retenção padrão da plataforma',
  },
  'terms.held.default.other': {
    en: '{days} days after qualifying — the platform’s standard hold',
    es: '{days} días después de calificar — la retención estándar de la plataforma',
    'pt-BR': '{days} dias após qualificar — a retenção padrão da plataforma',
  },
  'terms.held.value.one': {
    en: '{days} day after qualifying',
    es: '{days} día después de calificar',
    'pt-BR': '{days} dia após qualificar',
  },
  'terms.held.value.other': {
    en: '{days} days after qualifying',
    es: '{days} días después de calificar',
    'pt-BR': '{days} dias após qualificar',
  },
  'terms.tiers': { en: 'Tiers', es: 'Niveles', 'pt-BR': 'Níveis' },
  'terms.tiers.retroactive': {
    en: 'Retroactive — reaching a tier applies its rate to the whole period',
    es: 'Retroactivo: alcanzar un nivel aplica su tasa a todo el periodo',
    'pt-BR': 'Retroativo — alcançar um nível aplica a taxa dele a todo o período',
  },
  'terms.tiers.marginal': {
    en: 'Marginal — each tier’s rate applies only to the volume inside it',
    es: 'Marginal: la tasa de cada nivel se aplica solo al volumen dentro de él',
    'pt-BR': 'Marginal — a taxa de cada nível se aplica só ao volume dentro dele',
  },
  'terms.geoScope': { en: 'Geo scope', es: 'Alcance geográfico', 'pt-BR': 'Abrangência geográfica' },
  'terms.productScope': { en: 'Product scope', es: 'Alcance de productos', 'pt-BR': 'Abrangência de produtos' },
  'terms.accepted': {
    en: 'Accepted by the {side} on {date}',
    es: 'Aceptado por {side} el {date}',
    'pt-BR': 'Aceito por {side} em {date}',
  },
  'terms.inForce': { en: 'In force', es: 'En vigor', 'pt-BR': 'Em vigor' },

  /* §7.8 — commercial terms section headings and row labels */
  'terms.payout': { en: 'Payout', es: 'Pago', 'pt-BR': 'Pagamento' },
  'terms.negativeBalance': { en: 'Negative balance', es: 'Saldo negativo', 'pt-BR': 'Saldo negativo' },
  'terms.formula': { en: 'Formula', es: 'Fórmula', 'pt-BR': 'Fórmula' },
  'terms.minimumPayout': { en: 'Minimum payout', es: 'Pago mínimo', 'pt-BR': 'Pagamento mínimo' },
  // §0.5b — the level a figure resolved from, read last in "Monthly · Payable
  // 15 days after the period closes · Platform default". A short tag after a
  // middot, capitalised like the parts before it, not a clause.
  'terms.source.DEAL': { en: 'Set in your deal', es: 'Fijado en tu acuerdo', 'pt-BR': 'Definido no seu acordo' },
  'terms.source.BRAND': { en: 'Brand default', es: 'Predeterminado de la marca', 'pt-BR': 'Padrão da marca' },
  'terms.source.OPERATOR': { en: 'Operator default', es: 'Predeterminado del operador', 'pt-BR': 'Padrão do operador' },
  'terms.source.PLATFORM': { en: 'Platform default', es: 'Predeterminado de la plataforma', 'pt-BR': 'Padrão da plataforma' },
  'terms.trafficRules': { en: 'Traffic rules', es: 'Reglas de tráfico', 'pt-BR': 'Regras de tráfego' },

  /* §7.8a/§31.2c — payout frequency and settlement terms */
  'terms.payout.frequency.DAILY': { en: 'Daily', es: 'Diaria', 'pt-BR': 'Diária' },
  'terms.payout.frequency.TWICE_A_WEEK': { en: 'Twice a week', es: 'Dos veces a la semana', 'pt-BR': 'Duas vezes por semana' },
  'terms.payout.frequency.WEEKLY': { en: 'Weekly', es: 'Semanal', 'pt-BR': 'Semanal' },
  'terms.payout.frequency.ON_REQUEST': { en: 'On request', es: 'Bajo solicitud', 'pt-BR': 'Sob demanda' },
  'terms.payout.frequency.TWICE_A_MONTH': { en: 'Twice a month', es: 'Dos veces al mes', 'pt-BR': 'Duas vezes por mês' },
  'terms.payout.frequency.THRICE_A_MONTH': { en: 'Three times a month', es: 'Tres veces al mes', 'pt-BR': 'Três vezes por mês' },
  'terms.payout.frequency.MONTHLY': { en: 'Monthly', es: 'Mensual', 'pt-BR': 'Mensal' },
  'terms.payout.frequency.ONCE_IN_SIX_WEEKS': { en: 'Once every six weeks', es: 'Una vez cada seis semanas', 'pt-BR': 'Uma vez a cada seis semanas' },
  'terms.payout.frequency.ONCE_IN_TWO_MONTHS': { en: 'Once every two months', es: 'Una vez cada dos meses', 'pt-BR': 'Uma vez a cada dois meses' },
  'terms.payout.frequency.FORTNIGHTLY': { en: 'Fortnightly', es: 'Quincenal', 'pt-BR': 'Quinzenal' },
  'terms.payout.frequency.QUARTERLY': { en: 'Quarterly', es: 'Trimestral', 'pt-BR': 'Trimestral' },
  'terms.payout.settlement.one': {
    en: 'Payable {days} day after the period closes',
    es: 'Pagadero {days} día después de que se cierre el periodo',
    'pt-BR': 'Pagável {days} dia após o período fechar',
  },
  'terms.payout.settlement.other': {
    en: 'Payable {days} days after the period closes',
    es: 'Pagadero {days} días después de que se cierre el periodo',
    'pt-BR': 'Pagável {days} dias após o período fechar',
  },

  /* §7.8b/§27.2b — negative balance policies */
  'terms.negativeBalance.NONE': {
    en: 'No negative balance — a reversal clears your account to zero',
    es: 'Sin saldo negativo: una reversión deja tu cuenta a cero',
    'pt-BR': 'Sem saldo negativo — uma reversão deixa sua conta a zero',
  },
  'terms.negativeBalance.CARRY_FORWARD': {
    en: 'Negative balance carries forward to the next period',
    es: 'El saldo negativo se arrastra al próximo periodo',
    'pt-BR': 'Saldo negativo é carregado para o próximo período',
  },
  'terms.negativeBalance.RESET_ANNUALLY': {
    en: 'Negative balance is reset at the end of each year',
    es: 'El saldo negativo se reinicia al final de cada año',
    'pt-BR': 'Saldo negativo é redefinido no final de cada ano',
  },
  'terms.negativeBalance.PARTIAL_RESET': {
    en: 'Negative balance recovers at {pct}% per period, starting next period',
    es: 'El saldo negativo se recupera al {pct}% por periodo, a partir del próximo',
    'pt-BR': 'Saldo negativo se recupera em {pct}% por período, começando no próximo',
  },
  'terms.negativeBalance.RESET_ON_REQUEST': {
    en: 'Negative balance resets on request to your operator',
    es: 'El saldo negativo se reinicia bajo solicitud a tu operador',
    'pt-BR': 'Saldo negativo é redefinido sob solicitação ao seu operador',
  },

  /* §7.8c/§20.4 — the administrative fee and deduction stack items */
  'terms.adminFee': { en: 'Admin fee', es: 'Tarifa administrativa', 'pt-BR': 'Taxa administrativa' },
  'deductionItem.BONUS_COST': { en: 'Bonus cost', es: 'Coste de bonificación', 'pt-BR': 'Custo de bônus' },
  'deductionItem.FREE_BET_COST': { en: 'Free bet cost', es: 'Coste de apuesta gratuita', 'pt-BR': 'Custo de aposta grátis' },
  'deductionItem.CASHBACK_LOYALTY': { en: 'Cashback/Loyalty', es: 'Reembolso/Lealtad', 'pt-BR': 'Cashback/Lealdade' },
  'deductionItem.JACKPOT_CONTRIBUTION': { en: 'Jackpot contribution', es: 'Aportación a bote', 'pt-BR': 'Contribuição de jackpot' },
  'deductionItem.GAME_PROVIDER_FEES': { en: 'Game provider fees', es: 'Tarifas de proveedor de juegos', 'pt-BR': 'Taxas do fornecedor de jogos' },
  'deductionItem.PAYMENT_FEES': { en: 'Payment fees', es: 'Tarifas de pago', 'pt-BR': 'Taxas de pagamento' },
  'deductionItem.GAMING_DUTY': { en: 'Gaming duty', es: 'Impuesto sobre juegos', 'pt-BR': 'Taxa de jogo' },
  'deductionItem.CHARGEBACKS': { en: 'Chargebacks', es: 'Contracargos', 'pt-BR': 'Estornos' },
  'deductionItem.ADMINISTRATIVE_FEE': { en: 'Administrative fee', es: 'Tarifa administrativa', 'pt-BR': 'Taxa administrativa' },
  'deductionItem.PLATFORM_LICENCE_FEES': { en: 'Platform licence fees', es: 'Tarifas de licencia de plataforma', 'pt-BR': 'Taxas de licença de plataforma' },
  'deductionItem.NEGOTIATED': { en: 'Other (agreed)', es: 'Otro (acordado)', 'pt-BR': 'Outro (acordado)' },

  /* §7.8e — cookie lifetime and attribution window */
  'terms.cookieLifetime': { en: 'Cookie lifetime', es: 'Duración de cookie', 'pt-BR': 'Duração do cookie' },
  'terms.cookieLifetime.value.one': {
    en: '{days} day',
    es: '{days} día',
    'pt-BR': '{days} dia',
  },
  'terms.cookieLifetime.value.other': {
    en: '{days} days',
    es: '{days} días',
    'pt-BR': '{days} dias',
  },
  'terms.attributionWindow': { en: 'Attribution window', es: 'Ventana de atribución', 'pt-BR': 'Janela de atribuição' },
  'terms.attributionWindow.value.one': {
    en: '{days} day from click to registration',
    es: '{days} día desde clic hasta registro',
    'pt-BR': '{days} dia de clique até registro',
  },
  'terms.attributionWindow.value.other': {
    en: '{days} days from click to registration',
    es: '{days} días desde clic hasta registro',
    'pt-BR': '{days} dias de clique até registro',
  },

  /* §7.8c/§20.10b — revenue bases */
  'basis.TURNOVER': { en: 'Turnover (stakes)', es: 'Volumen de apuestas', 'pt-BR': 'Movimentação de apostas' },
  'terms.formula.turnover': {
    en: 'Paid on turnover (stakes) — no deduction stack applies',
    es: 'Se paga sobre el volumen de apuestas: no se aplica la pila de deducciones',
    'pt-BR': 'Pago sobre movimentação de apostas — nenhuma pilha de dedução se aplica',
  },

  /* §7.8f/§7.14 — traffic rules and channel/practice labels */
  'terms.trafficRules.allowed': { en: 'Allowed', es: 'Permitido', 'pt-BR': 'Permitido' },
  'terms.trafficRules.forbidden': { en: 'Forbidden', es: 'Prohibido', 'pt-BR': 'Proibido' },
  'terms.trafficRules.byAgreement': { en: 'By agreement', es: 'Por acuerdo', 'pt-BR': 'Por acordo' },
  'terms.trafficRules.notStated': { en: 'Not stated', es: 'No indicado', 'pt-BR': 'Não indicado' },
  'terms.trafficRules.branded': { en: 'Branded traffic', es: 'Tráfico marcado', 'pt-BR': 'Tráfego de marca' },
  'terms.trafficRules.crossBranded': { en: 'Cross-branded traffic', es: 'Tráfico multimarca', 'pt-BR': 'Tráfego multimarca' },
  'terms.trafficRules.incentivised': { en: 'Incentivised traffic', es: 'Tráfico incentivado', 'pt-BR': 'Tráfego incentivado' },

  /* Traffic channel labels */
  'trafficChannel.SEO': { en: 'SEO', es: 'SEO', 'pt-BR': 'SEO' },
  'trafficChannel.PPC': { en: 'PPC', es: 'PPC', 'pt-BR': 'PPC' },
  'trafficChannel.SOCIAL': { en: 'Social media', es: 'Redes sociales', 'pt-BR': 'Redes sociais' },
  'trafficChannel.STREAMING': { en: 'Streaming', es: 'Transmisión en directo', 'pt-BR': 'Streaming' },
  'trafficChannel.TELEGRAM': { en: 'Telegram', es: 'Telegram', 'pt-BR': 'Telegram' },
  'trafficChannel.EMAIL': { en: 'Email', es: 'Correo electrónico', 'pt-BR': 'Email' },
  'trafficChannel.APP': { en: 'App marketing', es: 'Marketing de aplicación', 'pt-BR': 'Marketing de app' },
  'trafficChannel.MEDIA_BUYING': { en: 'Media buying', es: 'Compra de medios', 'pt-BR': 'Compra de mídia' },
  'trafficChannel.COUPON': { en: 'Coupon sites', es: 'Sitios de cupones', 'pt-BR': 'Sites de cupom' },
  'trafficChannel.COMMUNITY': { en: 'Community', es: 'Comunidad', 'pt-BR': 'Comunidade' },
  'trafficChannel.INCENTIVISED': { en: 'Incentivised', es: 'Incentivado', 'pt-BR': 'Incentivado' },
  'trafficChannel.ASA': { en: 'ASA', es: 'ASA', 'pt-BR': 'ASA' },
  'trafficChannel.ASO': { en: 'ASO', es: 'ASO', 'pt-BR': 'ASO' },
  'trafficChannel.FB_ADS': { en: 'Facebook Ads', es: 'Facebook Ads', 'pt-BR': 'Facebook Ads' },
  'trafficChannel.GOOGLE_ADS': { en: 'Google Ads', es: 'Google Ads', 'pt-BR': 'Google Ads' },
  'trafficChannel.INFLUENCE': { en: 'Influencer', es: 'Influencer', 'pt-BR': 'Influenciador' },
  'trafficChannel.NATIVE_ADS': { en: 'Native ads', es: 'Anuncios nativos', 'pt-BR': 'Anúncios nativos' },
  'trafficChannel.POPS': { en: 'Pop-ups', es: 'Ventanas emergentes', 'pt-BR': 'Pop-ups' },
  'trafficChannel.PUSH': { en: 'Push notifications', es: 'Notificaciones push', 'pt-BR': 'Notificações push' },
  'trafficChannel.SEM': { en: 'SEM', es: 'SEM', 'pt-BR': 'SEM' },
  'trafficChannel.SMS': { en: 'SMS', es: 'SMS', 'pt-BR': 'SMS' },
  'trafficChannel.TIKTOK': { en: 'TikTok', es: 'TikTok', 'pt-BR': 'TikTok' },
  'trafficChannel.TWITCH_ADS': { en: 'Twitch Ads', es: 'Twitch Ads', 'pt-BR': 'Twitch Ads' },
  'trafficChannel.UAC': { en: 'UAC', es: 'UAC', 'pt-BR': 'UAC' },
  'trafficChannel.UNITY': { en: 'Unity Ads', es: 'Unity Ads', 'pt-BR': 'Unity Ads' },
  'trafficChannel.YOUTUBE_ADS': { en: 'YouTube Ads', es: 'YouTube Ads', 'pt-BR': 'YouTube Ads' },
  'trafficChannel.DOORWAY': { en: 'Doorway pages', es: 'Páginas de entrada', 'pt-BR': 'Páginas de entrada' },
  'trafficChannel.SCHEME_TRAFFIC': { en: 'Scheme traffic', es: 'Tráfico de esquema', 'pt-BR': 'Tráfego de esquema' },
  'trafficChannel.CFT': { en: 'CFT', es: 'CFT', 'pt-BR': 'CFT' },
  'trafficChannel.DIRECT': { en: 'Direct', es: 'Directo', 'pt-BR': 'Direto' },
  'trafficChannel.IN_PAGE': { en: 'In-page', es: 'En página', 'pt-BR': 'Na página' },
  'trafficChannel.VIDEO': { en: 'Video', es: 'Video', 'pt-BR': 'Vídeo' },
  'trafficChannel.BANNER': { en: 'Banner', es: 'Banner', 'pt-BR': 'Banner' },
  'trafficChannel.DISPLAY': { en: 'Display', es: 'Display', 'pt-BR': 'Display' },
  'trafficChannel.TELEGRAM_MINI_APPS': { en: 'Telegram Mini Apps', es: 'Aplicaciones Mini de Telegram', 'pt-BR': 'Telegram Mini Apps' },
  'trafficChannel.REDDIT': { en: 'Reddit', es: 'Reddit', 'pt-BR': 'Reddit' },

  /* Traffic practice labels */
  'trafficPractice.BRAND_BIDDING': { en: 'Brand bidding', es: 'Puja por marca', 'pt-BR': 'Licitação de marca' },
  'trafficPractice.CROSS_BRAND_BIDDING': { en: 'Cross-brand bidding', es: 'Puja multimarca', 'pt-BR': 'Licitação multimarca' },
  'trafficPractice.TRADEMARK_DOMAINS': { en: 'Trademark domains', es: 'Dominios de marca registrada', 'pt-BR': 'Domínios de marca registrada' },
  'trafficPractice.ADULT_ADJACENCY': { en: 'Adult adjacent content', es: 'Contenido adyacente de adultos', 'pt-BR': 'Conteúdo adjacente para adultos' },
  'trafficPractice.MINORS_PLACEMENT': { en: 'Placement near minors content', es: 'Ubicación cerca de contenido para menores', 'pt-BR': 'Colocação perto de conteúdo infantil' },
  'trafficPractice.UNSOLICITED_MESSAGING': { en: 'Unsolicited messaging', es: 'Mensajería no solicitada', 'pt-BR': 'Mensagens não solicitadas' },
  'trafficPractice.FORCED_CLICKS': { en: 'Forced clicks', es: 'Clics forzados', 'pt-BR': 'Cliques forçados' },
  'trafficPractice.MISSTATED_CLAIMS': { en: 'Misstated or deceptive claims', es: 'Afirmaciones inexactas o engañosas', 'pt-BR': 'Alegações imprecisas ou enganosas' },

  /* §6.6/§6.6a/§6.6b (task A7a) — the affiliate's own declared-channels editor. */
  'account.section.trafficSources': { en: 'Traffic sources', es: 'Fuentes de tráfico', 'pt-BR': 'Origens de tráfego' },
  'trafficSources.intro': {
    en: 'The channels you promote from. This is a contractual statement, not a survey: traffic from a channel you have not declared is a breach of your terms.',
    es: 'Los canales desde los que promocionas. Es una declaración contractual, no una encuesta: el tráfico de un canal que no has declarado incumple tus condiciones.',
    'pt-BR': 'Os canais pelos quais você promove. É uma declaração contratual, não uma pesquisa: tráfego de um canal não declarado é uma violação dos seus termos.',
  },
  'trafficSources.empty': {
    en: 'No channels declared yet.',
    es: 'Aún no se declaró ningún canal.',
    'pt-BR': 'Nenhum canal declarado ainda.',
  },
  'trafficSources.addChannel': { en: 'Channel', es: 'Canal', 'pt-BR': 'Canal' },
  'trafficSources.propertyUrl': { en: 'Property (optional)', es: 'Propiedad (opcional)', 'pt-BR': 'Propriedade (opcional)' },
  'trafficSources.propertyUrl.hint': {
    en: 'The page you actually promote from, if you have one to show.',
    es: 'La página desde la que realmente promocionas, si tienes una para mostrar.',
    'pt-BR': 'A página pela qual você realmente promove, se tiver uma para mostrar.',
  },
  'trafficSources.add': { en: 'Add channel', es: 'Añadir canal', 'pt-BR': 'Adicionar canal' },
  'trafficSources.remove': { en: 'Remove', es: 'Quitar', 'pt-BR': 'Remover' },
  'trafficSources.save': { en: 'Save channels', es: 'Guardar canales', 'pt-BR': 'Salvar canais' },
  'trafficSources.declaredOn': { en: 'Declared {date}', es: 'Declarado el {date}', 'pt-BR': 'Declarado em {date}' },
  'trafficSources.verifiedOn': {
    en: 'Verified by your account manager on {date}',
    es: 'Verificado por tu gestor de cuenta el {date}',
    'pt-BR': 'Verificado pelo seu gerente de conta em {date}',
  },
  'trafficSources.notYetVerified': { en: 'Not yet verified', es: 'Aún no verificado', 'pt-BR': 'Ainda não verificado' },
  'trafficSources.noneOffered': {
    en: 'Every channel is already on your list.',
    es: 'Ya has añadido todos los canales disponibles.',
    'pt-BR': 'Todos os canais já estão na sua lista.',
  },

  /* Additional terms page items */
  'terms.flatFee': { en: 'Flat fee', es: 'Tarifa fija', 'pt-BR': 'Taxa fixa' },
  'terms.perClick': { en: 'Per valid click', es: 'Por clic válido', 'pt-BR': 'Por clique válido' },
  'terms.periodCap': { en: 'Cap per month', es: 'Límite por mes', 'pt-BR': 'Limite por mês' },
  'terms.periodCap.value.one': {
    en: 'Up to {count} click',
    es: 'Hasta {count} clic',
    'pt-BR': 'Até {count} clique',
  },
  'terms.periodCap.value.other': {
    en: 'Up to {count} clicks',
    es: 'Hasta {count} clics',
    'pt-BR': 'Até {count} cliques',
  },
  'terms.perThousandImpressions': {
    en: 'Per thousand impressions',
    es: 'Por mil impresiones',
    'pt-BR': 'Por mil impressões',
  },
  // §26.11/§26.11a — the CPM cap's own value text, apart from CPC's above:
  // an impression is not a click (§12.12, §26.11b), so the cap must say
  // what it counts.
  'terms.periodCap.impressions.value.one': {
    en: 'Up to {count} impression',
    es: 'Hasta {count} impresión',
    'pt-BR': 'Até {count} impressão',
  },
  'terms.periodCap.impressions.value.other': {
    en: 'Up to {count} impressions',
    es: 'Hasta {count} impresiones',
    'pt-BR': 'Até {count} impressões',
  },
  'terms.perLead': { en: 'Per lead', es: 'Por registro', 'pt-BR': 'Por lead' },

  /* §39.6 — the dimensions, named as the affiliate thinks of them. */
  'dim.link_label': { en: 'Link label (stored)', es: 'Etiqueta del enlace (guardada)', 'pt-BR': 'Rótulo do link (salvo)' },
  'dim.sub_id': { en: 'Your label ({slot})', es: 'Tu etiqueta ({slot})', 'pt-BR': 'Seu rótulo ({slot})' },
  'dim.creative': { en: 'Creative', es: 'Creatividad', 'pt-BR': 'Criativo' },
  'dim.referrer': { en: 'Referrer', es: 'Referente', 'pt-BR': 'Referenciador' },
  'dim.campaign': { en: 'Campaign', es: 'Campaña', 'pt-BR': 'Campanha' },
  'dim.landing_page': { en: 'Landing page', es: 'Página de destino', 'pt-BR': 'Página de destino' },
  'dailyActivity.title': { en: 'Day by day', es: 'Día a día', 'pt-BR': 'Dia a dia' },
  'dailyActivity.lead': {
    en: 'Each of your players on each day, on the operator’s accounting day. A lifetime total says whether a player is valuable; this says when they stopped.',
    es: 'Cada uno de tus jugadores por día, según el día contable del operador. Un total de por vida dice si un jugador es valioso; esto dice cuándo dejó de serlo.',
    'pt-BR': 'Cada um dos seus jogadores por dia, no dia contábil do operador. Um total vitalício diz se um jogador é valioso; isto diz quando ele parou.',
  },
  'dailyActivity.empty': {
    en: 'No activity in this period.',
    es: 'Sin actividad en este periodo.',
    'pt-BR': 'Nenhuma atividade neste período.',
  },
  'col.turnover': { en: 'Staked', es: 'Apostado', 'pt-BR': 'Apostado' },
  'col.wins': { en: 'Won', es: 'Ganado', 'pt-BR': 'Ganho' },
  'col.ggr': { en: 'GGR', es: 'GGR', 'pt-BR': 'GGR' },
  'op.cohortMonth': { en: 'Day', es: 'Día', 'pt-BR': 'Dia' },
  'excludedClicks.open.one': {
    en: 'Show the click excluded on {day}',
    es: 'Ver el clic excluido el {day}',
    'pt-BR': 'Ver o clique excluído em {day}',
  },
  'excludedClicks.open.other': {
    en: 'Show the {n} clicks excluded on {day}',
    es: 'Ver los {n} clics excluidos el {day}',
    'pt-BR': 'Ver os {n} cliques excluídos em {day}',
  },
  'excludedClicks.title': { en: 'Excluded clicks · {day}', es: 'Clics excluidos · {day}', 'pt-BR': 'Cliques excluídos · {day}' },
  'excludedClicks.lead': {
    en: 'Clicks on this day (UTC, as the table counts days) that were not counted, newest first, and why each was left out.',
    es: 'Clics de este día (UTC, como cuenta los días la tabla) que no se contaron, del más reciente al más antiguo, y por qué se excluyó cada uno.',
    'pt-BR': 'Cliques deste dia (UTC, como a tabela conta os dias) que não foram contados, do mais recente ao mais antigo, e por que cada um ficou de fora.',
  },
  'excludedClicks.why.DUPLICATE': {
    en: 'A repeat click is the same visitor on the same link again within the deduplication window; only the first is counted.',
    es: 'Un clic repetido es el mismo visitante en el mismo enlace dentro de la ventana de deduplicación; solo se cuenta el primero.',
    'pt-BR': 'Um clique repetido é o mesmo visitante no mesmo link dentro da janela de deduplicação; só o primeiro é contado.',
  },
  'excludedClicks.colCampaign': { en: 'Campaign · placement', es: 'Campaña · ubicación', 'pt-BR': 'Campanha · posicionamento' },
  'excludedClicks.colFrom': { en: 'Country · device', es: 'País · dispositivo', 'pt-BR': 'País · dispositivo' },
  'excludedClicks.colTimeUtc': { en: 'Time (UTC)', es: 'Hora (UTC)', 'pt-BR': 'Hora (UTC)' },
  'excludedClicks.colWhy': { en: 'Why excluded', es: 'Motivo de exclusión', 'pt-BR': 'Motivo da exclusão' },
  'excludedClicks.empty': {
    en: 'No excluded clicks on this day.',
    es: 'No hay clics excluidos este día.',
    'pt-BR': 'Nenhum clique excluído neste dia.',
  },
  'excludedClicks.more': {
    en: 'Showing the latest {n}. Sources → Click log lists every click.',
    es: 'Se muestran los {n} más recientes. Fuentes → Registro de clics los lista todos.',
    'pt-BR': 'Mostrando os {n} mais recentes. Fontes → Registro de cliques lista todos.',
  },
  'clickLog.title': { en: 'Click log', es: 'Registro de clics', 'pt-BR': 'Registro de cliques' },
  'clickLog.country.hint': {
    en: 'Two-letter country code, as the click recorded it.',
    es: 'Código de país de dos letras, tal como lo registró el clic.',
    'pt-BR': 'Código de país de duas letras, como o clique registrou.',
  },
  'clickLog.page': { en: 'Page {page}', es: 'Página {page}', 'pt-BR': 'Página {page}' },
  'clickLog.lead': {
    en: 'Your individual clicks, newest first. Every other traffic report is a count, and a count cannot say why one particular click did not turn into anything.',
    es: 'Tus clics individuales, del más reciente al más antiguo. Los demás informes de tráfico son recuentos, y un recuento no puede decir por qué un clic concreto no se convirtió en nada.',
    'pt-BR': 'Seus cliques individuais, do mais recente ao mais antigo. Os demais relatórios de tráfego são contagens, e uma contagem não diz por que um clique específico não virou nada.',
  },
  'clickLog.empty': {
    en: 'No clicks in this period.',
    es: 'Sin clics en este periodo.',
    'pt-BR': 'Nenhum clique neste período.',
  },
  'clickLog.colWhen': { en: 'When', es: 'Cuándo', 'pt-BR': 'Quando' },
  'clickLog.colPlacement': { en: 'Placement', es: 'Ubicación', 'pt-BR': 'Posicionamento' },
  'clickLog.colWhere': { en: 'Country', es: 'País', 'pt-BR': 'País' },
  'clickLog.colWhat': { en: 'Device', es: 'Dispositivo', 'pt-BR': 'Dispositivo' },
  'clickLog.colOutcome': { en: 'Outcome', es: 'Resultado', 'pt-BR': 'Resultado' },
  'clickLog.outcome.ATTRIBUTED': {
    en: 'Became a player',
    es: 'Se convirtió en jugador',
    'pt-BR': 'Tornou-se jogador',
  },
  'clickLog.outcome.NO_PLAYER': {
    en: 'Counted, no player yet',
    es: 'Contado, aún sin jugador',
    'pt-BR': 'Contado, ainda sem jogador',
  },
  'clickLog.outcome.REFUSED': { en: 'Refused', es: 'Rechazado', 'pt-BR': 'Recusado' },
  'dim.day': { en: 'Day', es: 'Día', 'pt-BR': 'Dia' },
  'dim.brand': { en: 'Brand', es: 'Marca', 'pt-BR': 'Marca' },
  'dim.product': { en: 'Product', es: 'Producto', 'pt-BR': 'Produto' },
  'dim.geo': { en: 'Country / region', es: 'País / región', 'pt-BR': 'País / região' },
  'dim.device': { en: 'Device', es: 'Dispositivo', 'pt-BR': 'Dispositivo' },
  'dim.os': { en: 'Operating system', es: 'Sistema operativo', 'pt-BR': 'Sistema operacional' },
  'dim.browser': { en: 'Browser', es: 'Navegador', 'pt-BR': 'Navegador' },

  'perf.noLabel': { en: '(no label set)', es: '(sin etiqueta)', 'pt-BR': '(sem rótulo)' },
  'map.title': { en: 'Clicks by country', es: 'Clics por país', 'pt-BR': 'Cliques por país' },
  'map.clicks.one': { en: '{n} click', es: '{n} clic', 'pt-BR': '{n} clique' },
  'map.clicks.other': { en: '{n} clicks', es: '{n} clics', 'pt-BR': '{n} cliques' },
  'perf.noCountry': { en: '(country not recorded)', es: '(país no registrado)', 'pt-BR': '(país não registrado)' },
  'perf.botWarning': {
    en: '{share}% of this source was classified as automated. Worth checking before you spend more on it.',
    es: 'El {share}% de esta fuente se clasificó como automatizado. Conviene revisarlo antes de gastar más en ella.',
    'pt-BR': '{share}% desta origem foi classificada como automatizada. Vale conferir antes de gastar mais nela.',
  },
  'perf.unlabelled.labels': {
    en: 'Only traffic with a label appears here. Name each placement when you create a link under Links.',
    es: 'Aquí solo aparece el tráfico con etiqueta. Nombra cada ubicación cuando crees un enlace en Enlaces.',
    'pt-BR': 'Aqui só aparece o tráfego com rótulo. Nomeie cada posição ao criar um link em Links.',
  },
  'perf.unlabelled.creative': {
    en: 'Only clicks that carried ?cr= on the tracking link appear here. Attach a creative id when you share a banner link.',
    es: 'Aquí solo aparecen los clics que llevaban ?cr= en el enlace de seguimiento. Añade un id de creatividad cuando compartas un enlace de banner.',
    'pt-BR': 'Aqui só aparecem os cliques que levaram ?cr= no link de rastreamento. Inclua um id de criativo ao compartilhar um link de banner.',
  },
  'perf.unlabelled.referrer': {
    en: 'Only clicks with a referrer header appear here.',
    es: 'Aquí solo aparecen los clics con cabecera de referente.',
    'pt-BR': 'Aqui só aparecem os cliques com cabeçalho de referenciador.',
  },
  'perf.unlabelled.geo': {
    en:
      'A click whose origin could not be placed shows as “(country not recorded)”. ' +
      'Where a state or province was known it follows the country name.',
    es:
      'Un clic cuyo origen no pudo ubicarse aparece como «(país no registrado)». ' +
      'Cuando se conocía el estado o la provincia, sigue al nombre del país.',
    'pt-BR':
      'Um clique cuja origem não pôde ser localizada aparece como “(país não registrado)”. ' +
      'Quando o estado ou a província era conhecido, ele segue o nome do país.',
  },
  'perf.unlabelled.other': {
    en:
      'Traffic with no label shows as “(no label set)”. Add labels when you create ' +
      'a link and this breaks down by placement instead.',
    es:
      'El tráfico sin etiqueta aparece como «(sin etiqueta)». Añade etiquetas cuando ' +
      'crees un enlace y esto se desglosará por ubicación.',
    'pt-BR':
      'O tráfego sem rótulo aparece como “(sem rótulo)”. Adicione rótulos ao criar um ' +
      'link e isto passa a ser detalhado por posição.',
  },

  /* §39.9 — the CSV of the table on screen. Its header is words, so it moves. */
  'csv.value': { en: 'Value', es: 'Valor', 'pt-BR': 'Valor' },
  'csv.total': { en: 'Total', es: 'Total', 'pt-BR': 'Total' },
  'csv.referenceId': { en: 'Reference ID', es: 'ID de referencia', 'pt-BR': 'ID de referência' },
  'export.referenceIds': {
    en: 'Add reference IDs',
    es: 'Añadir IDs de referencia',
    'pt-BR': 'Adicionar IDs de referência',
  },
  /* What a download carries, beyond the rows themselves. */
  'export.formatted': {
    en: 'Group the numbers',
    es: 'Agrupar los números',
    'pt-BR': 'Agrupar os números',
  },
  'export.totals': {
    en: 'Add a totals row',
    es: 'Añadir una fila de totales',
    'pt-BR': 'Adicionar uma linha de totais',
  },
  'csv.countedClicks': { en: 'Counted clicks', es: 'Clics contados', 'pt-BR': 'Cliques contados' },
  'csv.excluded': { en: 'Excluded', es: 'Excluidos', 'pt-BR': 'Excluídos' },
  'csv.whyExcluded': { en: 'Why excluded', es: 'Motivo de exclusión', 'pt-BR': 'Motivo da exclusão' },
  'csv.automatedShare': { en: 'Automated share', es: 'Proporción automatizada', 'pt-BR': 'Proporção automatizada' },

  'held.noReleaseDate': {
    en: 'Release date not yet set',
    es: 'Fecha de liberación aún sin fijar',
    'pt-BR': 'Data de liberação ainda não definida',
  },

  /* §39.13 — sampling, said in terms of what it does. */
  'sampling.rate': {
    en: '{rate}% of qualifying first deposits earn no commission',
    es: 'El {rate}% de los primeros depósitos que califican no genera comisión',
    'pt-BR': '{rate}% dos primeiros depósitos qualificados não geram comissão',
  },
  'sampling.none': {
    en: 'None — every qualifying first deposit earns',
    es: 'Ninguno: todos los primeros depósitos que califican generan comisión',
    'pt-BR': 'Nenhum — todo primeiro depósito qualificado gera comissão',
  },
  'sampling.chip': { en: '{rate}% not paid', es: '{rate}% sin pagar', 'pt-BR': '{rate}% não pago' },
  'sampling.allPaid': {
    en: 'Every qualifying conversion in this period was paid.',
    es: 'Todas las conversiones que calificaron en este periodo se pagaron.',
    'pt-BR': 'Todas as conversões qualificadas neste período foram pagas.',
  },

  /* ================================================================ *
   * The AffDice Affiliate's own screens
   *
   * Everything below is what E37 recorded as still English: the tables, the
   * forms, the buttons, the empty states and the sentences that explain a
   * figure. A picker that moved the rail and left these is the control §2.8f
   * says is worse than none.
   *
   * Column headings are grouped by what they name rather than by the screen
   * that shows them — "Brand" is one word in three languages however many
   * tables print it, and duplicating it per screen is how two of them end up
   * translated differently.
   * ================================================================ */

  /* ---------------- the filter bar and its ranges ---------------- */
  'filter.period': { en: 'Period', es: 'Periodo', 'pt-BR': 'Período' },
  // Item 9 — the rail's brand selector is inside the phone nav sheet, so a
  // filtered page otherwise reads as the whole account there.
  'filter.brandOnly': { en: '{brand} only', es: 'Solo {brand}', 'pt-BR': 'Só {brand}' },
  'filter.showAll': { en: 'Show all', es: 'Mostrar todo', 'pt-BR': 'Mostrar tudo' },
  'filter.from': { en: 'From', es: 'Desde', 'pt-BR': 'De' },
  'filter.to': { en: 'To', es: 'Hasta', 'pt-BR': 'Até' },
  /*
   * Said rather than left implicit: while one date is missing the report is
   * still showing the window the console opened on, and two half-filled boxes
   * above it would otherwise read as having been honoured.
   */
  'filter.needBothDates': {
    en: 'Pick both dates — showing the last 30 days until you do',
    es: 'Elige ambas fechas — se muestran los últimos 30 días hasta entonces',
    'pt-BR': 'Escolha as duas datas — mostrando os últimos 30 dias até lá',
  },
  'range.7': { en: 'Last 7 days', es: 'Últimos 7 días', 'pt-BR': 'Últimos 7 dias' },
  'range.30': { en: 'Last 30 days', es: 'Últimos 30 días', 'pt-BR': 'Últimos 30 dias' },
  'range.90': { en: 'Last 90 days', es: 'Últimos 90 días', 'pt-BR': 'Últimos 90 dias' },
  'range.365': { en: 'Last 12 months', es: 'Últimos 12 meses', 'pt-BR': 'Últimos 12 meses' },
  /* The calendar windows, which are what a statement can be reconciled against. */
  'range.today': { en: 'Today', es: 'Hoy', 'pt-BR': 'Hoje' },
  'range.yesterday': { en: 'Yesterday', es: 'Ayer', 'pt-BR': 'Ontem' },
  'range.thisMonth': { en: 'This month', es: 'Este mes', 'pt-BR': 'Este mês' },
  'range.lastMonth': { en: 'Last month', es: 'El mes pasado', 'pt-BR': 'Mês passado' },
  'range.thisYear': { en: 'This year', es: 'Este año', 'pt-BR': 'Este ano' },
  'range.custom': { en: 'Chosen dates', es: 'Fechas elegidas', 'pt-BR': 'Datas escolhidas' },

  /* ---------------- words that name a column, anywhere ---------------- */
  'col.day': { en: 'Day', es: 'Día', 'pt-BR': 'Dia' },
  'col.date': { en: 'Date', es: 'Fecha', 'pt-BR': 'Data' },
  'col.brand': { en: 'Brand', es: 'Marca', 'pt-BR': 'Marca' },
  'col.campaign': { en: 'Campaign', es: 'Campaña', 'pt-BR': 'Campanha' },
  'col.label': { en: 'Label', es: 'Etiqueta', 'pt-BR': 'Rótulo' },
  'col.link': { en: 'Link', es: 'Enlace', 'pt-BR': 'Link' },
  'col.impressions': { en: 'Impressions', es: 'Impresiones', 'pt-BR': 'Impressões' },
  /* §12.7 — distinct visitors, and clicks per visitor. */
  'col.reach': { en: 'Visitors', es: 'Visitantes', 'pt-BR': 'Visitantes' },
  'col.frequency': { en: 'Clicks each', es: 'Clics por persona', 'pt-BR': 'Cliques por pessoa' },
  'col.clicks': { en: 'Clicks', es: 'Clics', 'pt-BR': 'Cliques' },
  'col.share': { en: 'Share', es: 'Cuota', 'pt-BR': 'Participação' },
  'col.ctr': { en: 'CTR', es: 'CTR', 'pt-BR': 'CTR' },
  'detailedActivity.title': {
    en: 'Detailed activity', es: 'Actividad detallada', 'pt-BR': 'Atividade detalhada',
  },
  'detailedActivity.lead': {
    en: 'Everything that happened on a day, on one row — traffic, deposits and what you earned, without joining three reports by hand.',
    es: 'Todo lo que ocurrió en un día, en una fila: tráfico, depósitos y lo que ganaste, sin unir tres informes a mano.',
    'pt-BR': 'Tudo o que aconteceu em um dia, em uma linha: tráfego, depósitos e o que você ganhou, sem juntar três relatórios à mão.',
  },
  'detailedActivity.currencyNote': {
    en: 'More than one currency is shown. Each row is one day in one currency, and the money columns do not add up down the page.',
    es: 'Se muestra más de una moneda. Cada fila es un día en una moneda, y las columnas de dinero no suman hacia abajo.',
    'pt-BR': 'Mais de uma moeda é exibida. Cada linha é um dia em uma moeda, e as colunas de dinheiro não somam na vertical.',
  },
  'detailedActivity.empty': {
    en: 'Nothing happened in this range.',
    es: 'No ocurrió nada en este rango.',
    'pt-BR': 'Nada aconteceu neste intervalo.',
  },
  'col.signups': { en: 'Signups', es: 'Registros', 'pt-BR': 'Cadastros' },
  'col.ftds': { en: 'FTDs', es: 'FTD', 'pt-BR': 'FTDs' },
  'nav.reports': { en: 'Reports', es: 'Informes', 'pt-BR': 'Relatórios' },
  'reports.group.activity': { en: 'Activity', es: 'Actividad', 'pt-BR': 'Atividade' },
  'reports.group.customer': {
    en: 'Customer reports', es: 'Informes de clientes', 'pt-BR': 'Relatórios de clientes',
  },
  'reports.group.traffic': {
    en: 'Traffic reports', es: 'Informes de tráfico', 'pt-BR': 'Relatórios de tráfego',
  },
  'reports.customerActivity': {
    en: 'Customer activity', es: 'Actividad de clientes', 'pt-BR': 'Atividade de clientes',
  },
  'reports.customerDaily': {
    en: 'Customer daily activity',
    es: 'Actividad diaria de clientes',
    'pt-BR': 'Atividade diária de clientes',
  },
  'reports.customerRegistrations': {
    en: 'Customer registrations', es: 'Registros de clientes', 'pt-BR': 'Cadastros de clientes',
  },
  'reports.device': { en: 'Device report', es: 'Informe de dispositivos', 'pt-BR': 'Relatório de dispositivos' },
  'reports.media': { en: 'Media report', es: 'Informe de medios', 'pt-BR': 'Relatório de mídia' },
  'reports.countries': { en: 'Countries report', es: 'Informe de países', 'pt-BR': 'Relatório de países' },
  'reports.referrer': { en: 'Referrer report', es: 'Informe de referentes', 'pt-BR': 'Relatório de referenciadores' },
  'reports.clicksLog': { en: 'Clicks log', es: 'Registro de clics', 'pt-BR': 'Registro de cliques' },
  'reports.payload': { en: 'Payload report', es: 'Informe de payload', 'pt-BR': 'Relatório de payload' },
  'reports.funnel': { en: 'Funnel report', es: 'Informe de embudo', 'pt-BR': 'Relatório de funil' },
  'col.player.clicks': { en: 'Clicks', es: 'Clics', 'pt-BR': 'Cliques' },
  'col.player.turnover_minor': { en: 'Turnover', es: 'Volumen', 'pt-BR': 'Volume' },
  'col.player.ngr_minor': { en: 'Commissionable NGR', es: 'NGR comisionable', 'pt-BR': 'NGR comissionável' },
  'col.player.player_ref': { en: 'Customer', es: 'Cliente', 'pt-BR': 'Cliente' },
  'col.player.registered_at': { en: 'Joined', es: 'Registro', 'pt-BR': 'Cadastro' },
  'col.player.first_deposit_at': { en: 'First deposit', es: 'Primer depósito', 'pt-BR': 'Primeiro depósito' },
  'col.player.deposits_minor': { en: 'Deposits', es: 'Depósitos', 'pt-BR': 'Depósitos' },
  'col.player.withdrawals_minor': { en: 'Withdrawals', es: 'Retiros', 'pt-BR': 'Saques' },
  'col.player.ggr_minor': { en: 'Gross revenue', es: 'Ingresos brutos', 'pt-BR': 'Receita bruta' },
  'col.player.commission_minor': { en: 'Commission', es: 'Comisión', 'pt-BR': 'Comissão' },
  'col.player.currency': { en: 'Currency', es: 'Moneda', 'pt-BR': 'Moeda' },
  'col.player.country': { en: 'Country', es: 'País', 'pt-BR': 'País' },
  'col.player.join_country': { en: 'Join country', es: 'País de registro', 'pt-BR': 'País de cadastro' },
  'col.player.referring_url': { en: 'Referring URL', es: 'URL de referencia', 'pt-BR': 'URL de referência' },
  'col.player.payload': { en: 'Payload', es: 'Payload', 'pt-BR': 'Payload' },
  'col.player.media': { en: 'Media', es: 'Medio', 'pt-BR': 'Mídia' },
  'col.player.channel': { en: 'Channel', es: 'Canal', 'pt-BR': 'Canal' },
  'col.player.brand': { en: 'Brand', es: 'Marca', 'pt-BR': 'Marca' },
  'col.player.landing_page': { en: 'Landing page', es: 'Página de destino', 'pt-BR': 'Página de destino' },
  'col.player.campaign_group': { en: 'Campaign group', es: 'Grupo de campañas', 'pt-BR': 'Grupo de campanhas' },
  'sources.addColumns': {
    en: 'Group by more', es: 'Agrupar por más', 'pt-BR': 'Agrupar por mais',
  },
  'col.chargebacks': { en: 'Chargebacks', es: 'Contracargos', 'pt-BR': 'Estornos' },
  'col.adjustedNetDeposits': {
    en: 'Net deposits', es: 'Depósitos netos', 'pt-BR': 'Depósitos líquidos',
  },
  'col.income': { en: 'Income', es: 'Ingresos', 'pt-BR': 'Receita' },
  'col.currency': { en: 'Currency', es: 'Moneda', 'pt-BR': 'Moeda' },
  'clicksImpressions.empty': {
    en: 'No impressions or clicks in this range.',
    es: 'No hay impresiones ni clics en este rango.',
    'pt-BR': 'Nenhuma impressão ou clique neste intervalo.',
  },
  'clicksImpressions.title': {
    en: 'Clicks & impressions', es: 'Clics e impresiones', 'pt-BR': 'Cliques e impressões',
  },
  'clicksImpressions.lead': {
    en: 'Impressions and clicks per day, split by brand rather than summed — so you can see which brand is converting the traffic you buy.',
    es: 'Impresiones y clics por día, separados por marca en lugar de sumados, para ver qué marca convierte el tráfico que compras.',
    'pt-BR': 'Impressões e cliques por dia, separados por marca em vez de somados, para ver qual marca converte o tráfego que você compra.',
  },
  'clicksImpressions.totalImpressions': {
    en: 'Total impressions', es: 'Impresiones totales', 'pt-BR': 'Impressões totais',
  },
  'clicksImpressions.totalClicks': { en: 'Total clicks', es: 'Clics totales', 'pt-BR': 'Cliques totais' },
  'clicksImpressions.overallReach': { en: 'Overall reach', es: 'Alcance total', 'pt-BR': 'Alcance total' },
  'clicksImpressions.overallCtr': { en: 'Overall CTR', es: 'CTR total', 'pt-BR': 'CTR total' },
  'quarantine.title': { en: 'Quarantine', es: 'Cuarentena', 'pt-BR': 'Quarentena' },
  'quarantine.lead': {
    en:
      'A player who is behind over their whole life with the brand is held out of revenue share ' +
      'until they catch up. Their losses do not count against your other players, and their ' +
      'revenue does not count for you until they are back above the line.',
    es:
      'Un jugador que está en negativo durante toda su vida con la marca queda fuera del reparto ' +
      'de ingresos hasta que se recupere. Sus pérdidas no cuentan contra tus otros jugadores, y ' +
      'sus ingresos no cuentan para ti hasta que vuelva a estar por encima del umbral.',
    'pt-BR':
      'Um jogador que está negativo ao longo de toda a sua vida com a marca fica de fora da ' +
      'divisão de receita até se recuperar. As perdas dele não contam contra os seus outros ' +
      'jogadores, e a receita dele não conta para você até que volte a ficar acima do limite.',
  },
  'quarantine.empty': {
    en: 'No player is behind. Everybody you brought is counting towards revenue share.',
    es: 'Ningún jugador está en negativo. Todos los que trajiste cuentan para el reparto de ingresos.',
    'pt-BR': 'Nenhum jogador está negativo. Todos que você trouxe contam para a divisão de receita.',
  },
  'quarantine.colPlayer': { en: 'Player', es: 'Jugador', 'pt-BR': 'Jogador' },
  'quarantine.colLifetime': { en: 'Lifetime', es: 'Histórico', 'pt-BR': 'Histórico' },
  'quarantine.colShortfall': { en: 'Left to close', es: 'Falta por cubrir', 'pt-BR': 'Falta cobrir' },
  'filter.allCampaigns': { en: 'All campaigns', es: 'Todas las campañas', 'pt-BR': 'Todas as campanhas' },
  'filter.campaignGroup': { en: 'Campaign group', es: 'Grupo de campañas', 'pt-BR': 'Grupo de campanhas' },
  'filter.allCampaignGroups': {
    en: 'All campaign groups', es: 'Todos los grupos', 'pt-BR': 'Todos os grupos',
  },
  'filter.allCountries': { en: 'All countries', es: 'Todos los países', 'pt-BR': 'Todos os países' },
  'col.excluded': { en: 'Excluded', es: 'Excluidos', 'pt-BR': 'Excluídos' },
  'col.registrations': { en: 'Registrations', es: 'Registros', 'pt-BR': 'Registros' },
  'col.deposits': { en: 'Deposits', es: 'Depósitos', 'pt-BR': 'Depósitos' },
  'col.period': { en: 'Period', es: 'Periodo', 'pt-BR': 'Período' },
  'col.type': { en: 'Type', es: 'Tipo', 'pt-BR': 'Tipo' },
  'col.amount': { en: 'Amount', es: 'Importe', 'pt-BR': 'Valor' },
  'col.state': { en: 'State', es: 'Estado', 'pt-BR': 'Estado' },
  'col.status': { en: 'Status', es: 'Estado', 'pt-BR': 'Status' },
  'col.because': { en: 'Because', es: 'Motivo', 'pt-BR': 'Motivo' },
  'col.player': { en: 'Player', es: 'Jugador', 'pt-BR': 'Jogador' },
  'col.created': { en: 'Created', es: 'Creado', 'pt-BR': 'Criado' },
  'col.value': { en: 'Value', es: 'Valor', 'pt-BR': 'Valor' },
  'col.automatedShare': { en: 'Automated share', es: 'Proporción automatizada', 'pt-BR': 'Proporção automatizada' },
  'col.by': { en: 'By', es: 'Por', 'pt-BR': 'Por' },
  'col.document': { en: 'Document', es: 'Documento', 'pt-BR': 'Documento' },
  'col.due': { en: 'Due', es: 'Vence', 'pt-BR': 'Vence' },
  'col.total': { en: 'Total', es: 'Total', 'pt-BR': 'Total' },
  'col.settled': { en: 'Settled', es: 'Liquidado', 'pt-BR': 'Liquidado' },
  'col.invoice': { en: 'Invoice', es: 'Factura', 'pt-BR': 'Fatura' },
  'col.statement': { en: 'Statement', es: 'Estado de cuenta', 'pt-BR': 'Extrato' },
  'col.earned': { en: 'Earned', es: 'Generado', 'pt-BR': 'Gerado' },
  'col.payable': { en: 'Payable', es: 'Pagadero', 'pt-BR': 'Pagável' },
  // §30.2a — the difference between earned and payable, stated rather than
  // left to be inferred from a shortfall.
  'col.held': { en: 'Held', es: 'Retenido', 'pt-BR': 'Retido' },
  'col.carriedForward': { en: 'Carried forward', es: 'Arrastrado', 'pt-BR': 'Transportado' },
  // §24.2 — qualified acquisition the commission run has not priced yet. Named
  // as its own column rather than folded into the earned figure, because the
  // two answer different questions: one is a ledger row, the other is what the
  // next run will write.
  'col.pipeline': { en: 'Qualified, not yet calculated', es: 'Cumple, aún sin calcular', 'pt-BR': 'Cumpriu, ainda não calculado' },
  'col.computed': { en: 'Calculated', es: 'Calculado', 'pt-BR': 'Calculado' },
  'col.name': { en: 'Name', es: 'Nombre', 'pt-BR': 'Nome' },
  'col.country': { en: 'Country', es: 'País', 'pt-BR': 'País' },
  'col.joined': { en: 'Joined', es: 'Se unió', 'pt-BR': 'Entrou' },
  'account.geos': { en: 'GEOs', es: 'GEOs', 'pt-BR': 'GEOs' },
  'account.noGeos': {
    en: 'None recorded by your operator yet',
    es: 'Tu operador aún no ha registrado ninguno',
    'pt-BR': 'Seu operador ainda não registrou nenhum',
  },
  'col.attribution': { en: 'Attribution', es: 'Atribución', 'pt-BR': 'Atribuição' },
  'col.company': { en: 'Company', es: 'Empresa', 'pt-BR': 'Empresa' },
  'col.code': { en: 'Code', es: 'Código', 'pt-BR': 'Código' },
  'col.validFrom': { en: 'Valid from', es: 'Válido desde', 'pt-BR': 'Válido de' },
  'col.validTo': { en: 'Valid to', es: 'Válido hasta', 'pt-BR': 'Válido até' },
  'col.language': { en: 'Language', es: 'Idioma', 'pt-BR': 'Idioma' },
  'col.asset': { en: 'Asset', es: 'Recurso', 'pt-BR': 'Arquivo' },
  'col.attempts': { en: 'Attempts', es: 'Intentos', 'pt-BR': 'Tentativas' },
  'col.delivered': { en: 'Delivered', es: 'Entregado', 'pt-BR': 'Entregue' },
  'col.event': { en: 'Event', es: 'Evento', 'pt-BR': 'Evento' },
  'col.subId': { en: 'Sub-ID', es: 'Sub-ID', 'pt-BR': 'Sub-ID' },
  'col.valid': { en: 'Valid', es: 'Válidos', 'pt-BR': 'Válidos' },
  'col.bot': { en: 'Bot', es: 'Bot', 'pt-BR': 'Bot' },
  'col.duplicate': { en: 'Duplicate', es: 'Duplicado', 'pt-BR': 'Duplicado' },
  'col.geo': { en: 'Geo', es: 'Geo', 'pt-BR': 'Geo' },
  'col.device': { en: 'Device', es: 'Dispositivo', 'pt-BR': 'Dispositivo' },
  'col.targeting': { en: 'Targeting', es: 'Segmentación', 'pt-BR': 'Segmentação' },
  'col.cohort': { en: 'Cohort', es: 'Cohorte', 'pt-BR': 'Coorte' },
  'col.players': { en: 'Players', es: 'Jugadores', 'pt-BR': 'Jogadores' },
  'col.firstDeposits': { en: 'First deposits', es: 'Primeros depósitos', 'pt-BR': 'Primeiros depósitos' },
  'col.failingCriterion': { en: 'Failing criterion', es: 'Criterio no cumplido', 'pt-BR': 'Critério não cumprido' },
  'col.linkTitle': { en: 'Link title', es: 'Título del enlace', 'pt-BR': 'Título do link' },
  'col.howCounted': { en: 'How it was counted', es: 'Cómo se calculó', 'pt-BR': 'Como foi calculado' },
  'statements.breakdown': { en: 'Line by line', es: 'Línea por línea', 'pt-BR': 'Linha por linha' },
  'statements.breakdownHint': {
    en: 'Every commission line behind this statement: the player, the campaign and placement that brought them, and the arithmetic. The lines add up to Earned, less anything brought forward from earlier statements.',
    es: 'Cada línea de comisión detrás de este estado de cuenta: el jugador, la campaña y la ubicación que lo trajeron, y el cálculo. Las líneas suman lo Ganado, menos lo traído de estados de cuenta anteriores.',
    'pt-BR': 'Cada linha de comissão por trás deste extrato: o jogador, a campanha e a colocação que o trouxeram, e o cálculo. As linhas somam o Ganho, menos o que veio de extratos anteriores.',
  },
  'statements.broughtForward': {
    en: 'Of the earned figure, {amount} was brought forward from earlier statements and not yet paid.',
    es: 'De lo ganado, {amount} se trajo de estados de cuenta anteriores y aún no se ha pagado.',
    'pt-BR': 'Do valor ganho, {amount} veio de extratos anteriores e ainda não foi pago.',
  },
  'statements.minimumNotChecked': {
    en: 'Minimum payout not checked: no exchange rate was published to compare it with this statement\'s currency, so the statement was issued without it.',
    es: 'Pago mínimo no comprobado: no se publicó un tipo de cambio para compararlo con la moneda de este estado de cuenta, así que se emitió sin él.',
    'pt-BR': 'Pagamento mínimo não verificado: não foi publicada uma taxa de câmbio para compará-lo com a moeda deste extrato, então ele foi emitido sem isso.',
  },
  'statements.noLines': {
    en: 'No commission lines in this period — nothing qualified.',
    es: 'Sin líneas de comisión en este periodo: nada calificó.',
    'pt-BR': 'Sem linhas de comissão neste período — nada se qualificou.',
  },
  // The statement worked deal by deal, from the brand's figures to the
  // payable. NGR is GGR less bonus; Commissionable NGR is what is left after
  // the administrative fee and any other agreed deduction — the figure the
  // RevShare rate is applied to.
  'calc.panel': { en: 'How this statement was calculated', es: 'Cómo se calculó este extracto', 'pt-BR': 'Como este extrato foi calculado' },
  'calc.hint': {
    en: 'Each deal, worked from the figures the brand reported to the amount on this statement, in the order your terms apply them. The last block takes the deals to the statement’s own Earned, Held and Payable.',
    es: 'Cada contrato, desde las cifras que informó la marca hasta el importe de este extracto, en el orden en que las aplican tus condiciones. El último bloque lleva los contratos a lo Generado, Retenido y Pagadero del propio extracto.',
    'pt-BR': 'Cada contrato, das cifras que a marca informou até o valor deste extrato, na ordem em que suas condições as aplicam. O último bloco leva os contratos ao Gerado, Retido e Pagável do próprio extrato.',
  },
  'calc.deal.noDeal': { en: 'Not under a deal', es: 'Fuera de un contrato', 'pt-BR': 'Fora de um contrato' },
  'calc.deal.version': { en: 'version {version}', es: 'versión {version}', 'pt-BR': 'versão {version}' },
  'calc.deal.withoutLines': {
    en: 'A deal with no commission this period',
    es: 'Un contrato sin comisión en este periodo',
    'pt-BR': 'Um contrato sem comissão neste período',
  },
  'calc.revshare.title': { en: 'RevShare', es: 'RevShare', 'pt-BR': 'RevShare' },
  'calc.revshare.titleProduct': { en: 'RevShare — {product}', es: 'RevShare — {product}', 'pt-BR': 'RevShare — {product}' },
  'calc.ggr': { en: 'GGR', es: 'GGR', 'pt-BR': 'GGR' },
  'calc.ngr': { en: 'NGR', es: 'NGR', 'pt-BR': 'NGR' },
  'calc.commissionableNgr': { en: 'Commissionable NGR', es: 'NGR comisionable', 'pt-BR': 'NGR comissionável' },
  'calc.deduction.rate': { en: '{item} ({rate})', es: '{item} ({rate})', 'pt-BR': '{item} ({rate})' },
  'calc.adminFee.carried': {
    en: 'Administrative fee carried from earlier periods',
    es: 'Tarifa administrativa arrastrada de periodos anteriores',
    'pt-BR': 'Taxa administrativa trazida de períodos anteriores',
  },
  'calc.offset': {
    en: 'Loss on your other products set against this one',
    es: 'Pérdida de tus otros productos compensada con este',
    'pt-BR': 'Perda dos seus outros produtos compensada com este',
  },
  'calc.revshareRate': { en: 'RevShare rate', es: 'Porcentaje de RevShare', 'pt-BR': 'Percentual de RevShare' },
  'calc.revshareRate.tiered': {
    en: 'By tier — see your terms',
    es: 'Por tramos — consulta tus condiciones',
    'pt-BR': 'Por faixas — veja suas condições',
  },
  'calc.revshareCommission': { en: 'RevShare commission', es: 'Comisión de RevShare', 'pt-BR': 'Comissão de RevShare' },
  'calc.noRevshareLine': {
    en: 'No RevShare was earned this period: nothing was left to pay a share of after the deductions and any deficit carried from earlier periods.',
    es: 'No se generó RevShare en este periodo: no quedó nada sobre lo que pagar un porcentaje tras las deducciones y cualquier déficit arrastrado de periodos anteriores.',
    'pt-BR': 'Nenhum RevShare foi gerado neste período: não sobrou nada sobre o que pagar um percentual depois das deduções e de qualquer déficit trazido de períodos anteriores.',
  },
  'calc.carryoverNote': {
    en: 'moves the carried deficit; not paid and not charged',
    es: 'mueve el déficit arrastrado; no se paga ni se cobra',
    'pt-BR': 'move o déficit trazido; não é pago nem cobrado',
  },
  'calc.cpa.title': { en: 'CPA', es: 'CPA', 'pt-BR': 'CPA' },
  'calc.cpaRate': { en: 'CPA rate', es: 'Importe de CPA', 'pt-BR': 'Valor do CPA' },
  'calc.criteria': { en: 'Qualifying criteria', es: 'Criterios de calificación', 'pt-BR': 'Critérios de qualificação' },
  'calc.qualified': { en: 'Qualified', es: 'Calificados', 'pt-BR': 'Qualificados' },
  'calc.notQualified': { en: 'Not qualified', es: 'No calificados', 'pt-BR': 'Não qualificados' },
  'calc.notYetDecided': { en: 'Not decided yet', es: 'Aún sin decidir', 'pt-BR': 'Ainda não decididos' },
  'calc.outcomesAssessed': {
    en: 'First deposits in this period that earned no CPA, counted as assessed on {date}.',
    es: 'Primeros depósitos de este periodo que no generaron CPA, contados según la evaluación del {date}.',
    'pt-BR': 'Primeiros depósitos deste período que não geraram CPA, contados conforme a avaliação de {date}.',
  },
  'calc.outcomesUnavailable': {
    en: 'The players who did not qualify could not be counted just now.',
    es: 'Ahora mismo no se pudo contar a los jugadores que no calificaron.',
    'pt-BR': 'Não foi possível contar agora os jogadores que não se qualificaram.',
  },
  'calc.cpaCommission': { en: 'CPA commission', es: 'Comisión de CPA', 'pt-BR': 'Comissão de CPA' },
  'calc.other.title': { en: 'Other commission', es: 'Otras comisiones', 'pt-BR': 'Outras comissões' },
  'calc.adjustments.title': { en: 'Adjustments and reversals', es: 'Ajustes y reversiones', 'pt-BR': 'Ajustes e reversões' },
  'calc.dealTotal': { en: 'Total for this deal', es: 'Total de este contrato', 'pt-BR': 'Total deste contrato' },
  'calc.notCounted': {
    en: '{state} — not in this statement’s total',
    es: '{state} — fuera del total de este extracto',
    'pt-BR': '{state} — fora do total deste extrato',
  },
  'calc.otherCurrency': {
    en: 'In {currency} — on that currency’s statement, not in this one’s total',
    es: 'En {currency} — en el extracto de esa moneda, fuera del total de este',
    'pt-BR': 'Em {currency} — no extrato dessa moeda, fora do total deste',
  },
  'calc.col.runningTotal': { en: 'Running total', es: 'Saldo acumulado', 'pt-BR': 'Saldo acumulado' },
  'calc.recon.title': { en: 'From the deals to the payable', es: 'De los contratos a lo pagadero', 'pt-BR': 'Dos contratos ao pagável' },
  'calc.recon.period': {
    en: 'Commission on this period’s lines',
    es: 'Comisión de las líneas de este periodo',
    'pt-BR': 'Comissão das linhas deste período',
  },
  'calc.recon.broughtForward': {
    en: 'Brought forward from earlier statements',
    es: 'Traído de extractos anteriores',
    'pt-BR': 'Trazido de extratos anteriores',
  },
  'calc.recon.mismatch': {
    en: 'This period’s lines have changed since the statement was issued — a restatement or a reversal — so they no longer add up to the issued Earned. The issued figures stand; the difference is settled on a later statement.',
    es: 'Las líneas de este periodo han cambiado desde que se emitió el extracto —una reexpresión o una reversión—, así que ya no suman lo Generado emitido. Las cifras emitidas se mantienen; la diferencia se liquida en un extracto posterior.',
    'pt-BR': 'As linhas deste período mudaram desde a emissão do extrato — uma reapresentação ou uma reversão —, então não somam mais o Gerado emitido. Os valores emitidos permanecem; a diferença é acertada em um extrato posterior.',
  },
  'statements.pdf': { en: 'PDF', es: 'PDF', 'pt-BR': 'PDF' },
  /* §8.2 — which recruit an override was earned on, appended to the
     arithmetic rather than given a column of its own: the line already has
     three dashes where a player, campaign and link would be. */
  'statementLine.earnedOn': {
    en: '{how}, earned on {affiliate}',
    es: '{how}, generado por {affiliate}',
    'pt-BR': '{how}, gerado por {affiliate}',
  },
  'statementLine.earnedOnAlone': {
    en: 'Earned on {affiliate}',
    es: 'Generado por {affiliate}',
    'pt-BR': 'Gerado por {affiliate}',
  },
  'statementLine.share': { en: '{rate} of {basis} {amount}', es: '{rate} de {basis} {amount}', 'pt-BR': '{rate} de {basis} {amount}' },
  'statementLine.perAcquisition': {
    en: 'one-off {amount} per qualified acquisition',
    es: '{amount} único por adquisición calificada',
    'pt-BR': '{amount} único por aquisição qualificada',
  },
  'dash.card.openLabel': {
    en: 'Opens the detail behind this figure.',
    es: 'Abre el detalle detrás de esta cifra.',
    'pt-BR': 'Abre o detalhe por trás deste número.',
  },
  'dash.clicks.popupTitle': {
    en: 'Every click behind this figure',
    es: 'Cada clic detrás de esta cifra',
    'pt-BR': 'Cada clique por trás deste número',
  },
  'dash.registrations.popupTitle': {
    en: 'Registrations by brand and country',
    es: 'Registros por marca y país',
    'pt-BR': 'Cadastros por marca e país',
  },
  'dash.registrations.none': {
    en: 'Nobody registered in this period.',
    es: 'Nadie se registró en este período.',
    'pt-BR': 'Ninguém se cadastrou neste período.',
  },
  'dash.popup.capped': {
    en: 'Showing the most recent {n}. The clicks report has the rest.',
    es: 'Mostrando los {n} más recientes. El informe de clics tiene el resto.',
    'pt-BR': 'Mostrando os {n} mais recentes. O relatório de cliques tem o restante.',
  },
  'col.clickCountry': { en: 'Click from', es: 'Clic desde', 'pt-BR': 'Clique de' },
  'col.registeredIn': { en: 'Registered in', es: 'Registrado en', 'pt-BR': 'Cadastrado em' },
  'col.countryUnknown': {
    en: 'Not recorded', es: 'Sin registrar', 'pt-BR': 'Não registrado',
  },
  'attributed.yes': { en: 'Yes', es: 'Sí', 'pt-BR': 'Sim' },
  'attributed.yesMany.one': { en: 'Yes, {n} player', es: 'Sí, {n} jugador', 'pt-BR': 'Sim, {n} jogador' },
  'attributed.yesMany.other': { en: 'Yes, {n} players', es: 'Sí, {n} jugadores', 'pt-BR': 'Sim, {n} jogadores' },
  'attributed.no': { en: 'No', es: 'No', 'pt-BR': 'Não' },
  'col.attributed': {
    en: 'Won a player', es: 'Ganó un jugador', 'pt-BR': 'Ganhou um jogador',
  },
  'col.attributed.tip': {
    en: 'Yes when this click brought you a player who registered and was credited to you. A click can be counted and still bring no player.',
    es: 'Sí cuando este clic te trajo un jugador que se registró y se te acreditó. Un clic puede contarse y aun así no traer ningún jugador.',
    'pt-BR': 'Sim quando este clique trouxe um jogador que se cadastrou e foi creditado a você. Um clique pode ser contado e ainda assim não trazer nenhum jogador.',
  },
  'chart.grain.day': { en: 'Day by day', es: 'Día a día', 'pt-BR': 'Dia a dia' },
  'chart.grain.month': { en: 'By month', es: 'Por mes', 'pt-BR': 'Por mês' },
  'chart.grain.year': { en: 'By year', es: 'Por año', 'pt-BR': 'Por ano' },
  'dash.chart.title': {
    en: 'Clicks and registrations, day by day',
    es: 'Clics y registros, día a día',
    'pt-BR': 'Cliques e cadastros, dia a dia',
  },
  'dash.chart.registrations': {
    en: 'Registrations', es: 'Registros', 'pt-BR': 'Cadastros',
  },
  'dash.chart.empty': {
    en: 'No clicks and no registrations in this period.',
    es: 'Sin clics ni registros en este período.',
    'pt-BR': 'Sem cliques nem cadastros neste período.',
  },
  'qualification.noLink': {
    en: 'No link — the player came through a promo code or a server-to-server click.',
    es: 'Sin enlace: el jugador llegó con un código promocional o un clic servidor a servidor.',
    'pt-BR': 'Sem link — o jogador veio por um código promocional ou um clique servidor a servidor.',
  },
  'col.cpaPaid': { en: 'CPA paid', es: 'CPA pagado', 'pt-BR': 'CPA pago' },
  'col.recouped': { en: 'Recouped', es: 'Recuperado', 'pt-BR': 'Recuperado' },
  'col.outstanding': { en: 'Outstanding', es: 'Pendiente', 'pt-BR': 'Em aberto' },
  'col.about': { en: 'About', es: 'Sobre', 'pt-BR': 'Sobre' },
  'col.raised': { en: 'Raised', es: 'Presentada', 'pt-BR': 'Aberta' },
  'col.whatHappened': { en: 'What happened', es: 'Qué pasó', 'pt-BR': 'O que aconteceu' },
  'col.reasonGiven': { en: 'Reason given', es: 'Motivo indicado', 'pt-BR': 'Motivo informado' },
  'col.whatWeWereTold': { en: 'What we were told', es: 'Lo que nos dijeron', 'pt-BR': 'O que nos disseram' },
  'col.whyHeld': { en: 'Why held', es: 'Motivo de la retención', 'pt-BR': 'Motivo da retenção' },
  'col.release': { en: 'Release', es: 'Liberación', 'pt-BR': 'Liberação' },
  'col.rate': { en: 'Rate', es: 'Tasa', 'pt-BR': 'Taxa' },
  'col.conversions': { en: 'Conversions', es: 'Conversiones', 'pt-BR': 'Conversões' },
  'col.yourStatus': { en: 'Your status', es: 'Tu estado', 'pt-BR': 'Seu status' },
  'col.brands': { en: 'Brands', es: 'Marcas', 'pt-BR': 'Marcas' },

  /* ---------------- campaigns you can ask for (§10.11) ---------------- */
  'discover.panel': {
    en: 'Campaigns you can ask for',
    es: 'Campañas que puedes solicitar',
    'pt-BR': 'Campanhas que você pode solicitar',
  },
  'campaign.runsIn': { en: 'Runs in', es: 'Se ejecuta en', 'pt-BR': 'Roda em' },
  'campaign.products': { en: 'Products', es: 'Productos', 'pt-BR': 'Produtos' },
  // §10.11a — asking, not joining: nothing here carries traffic until somebody
  // accepts. Both translations keep it a request.
  'discover.asking': { en: 'Asking…', es: 'Solicitando…', 'pt-BR': 'Solicitando…' },
  'discover.request': { en: 'Request access', es: 'Solicitar acceso', 'pt-BR': 'Solicitar acesso' },

  /* ---------------- campaign offers ---------------- */
  'offers.panel': { en: 'Campaign offers', es: 'Ofertas de campaña', 'pt-BR': 'Ofertas de campanha' },
  'offers.note': {
    en: 'Your operator invited you to this campaign. Review the scope above before you accept.',
    es: 'Tu operador te invitó a esta campaña. Revisa el alcance de arriba antes de aceptar.',
    'pt-BR': 'Seu operador convidou você para esta campanha. Confira o escopo acima antes de aceitar.',
  },
  'offers.accepting': { en: 'Accepting…', es: 'Aceptando…', 'pt-BR': 'Aceitando…' },
  'offers.accept': { en: 'Accept', es: 'Aceptar', 'pt-BR': 'Aceitar' },
  'offers.decline': { en: 'Decline', es: 'Rechazar', 'pt-BR': 'Recusar' },

  /* ---------------- brand invitations ---------------- */
  'invitations.panel': { en: 'Brand invitations', es: 'Invitaciones de marca', 'pt-BR': 'Convites de marca' },
  'invitations.allBrands': { en: 'All brands', es: 'Todas las marcas', 'pt-BR': 'Todas as marcas' },
  'invitations.sent': { en: 'Sent', es: 'Enviada', 'pt-BR': 'Enviado' },
  'invitations.expires': { en: 'Expires', es: 'Caduca', 'pt-BR': 'Expira' },
  // §6.14c — what Accept does, said before it is pressed. The panel used to
  // send the reader to their inbox for a link; the answer is given here now,
  // so what the button changes is the thing that needs saying.
  'invitations.note': {
    en: 'Accepting adds this brand to your account — its campaigns and links become available straight away.',
    es: 'Al aceptar, esta marca se añade a tu cuenta: sus campañas y enlaces quedan disponibles de inmediato.',
    'pt-BR': 'Ao aceitar, esta marca é adicionada à sua conta — suas campanhas e links ficam disponíveis na hora.',
  },

  /* ---------------- the dashboard ---------------- */
  'dash.title': { en: 'Overview', es: 'Resumen', 'pt-BR': 'Visão geral' },
  'dash.commissionInRange': {
    en: 'Commission in this period', es: 'Comisión en este periodo', 'pt-BR': 'Comissão neste período',
  },
  'dash.commissionInRange.none': {
    en: 'Nothing earned in these dates.', es: 'Nada ganado en estas fechas.', 'pt-BR': 'Nada ganho nestas datas.',
  },
  'dash.filter.campaign': { en: 'Campaign', es: 'Campaña', 'pt-BR': 'Campanha' },
  'dash.filter.allCampaigns': { en: 'All campaigns', es: 'Todas las campañas', 'pt-BR': 'Todas as campanhas' },
  'dash.filter.subId': { en: 'Sub-ID', es: 'Sub-ID', 'pt-BR': 'Sub-ID' },
  'dash.filter.subIdValue': { en: 'Sub-ID value', es: 'Valor del sub-ID', 'pt-BR': 'Valor do sub-ID' },
  'dash.sections': { en: 'Overview sections', es: 'Secciones del resumen', 'pt-BR': 'Seções da visão geral' },
  'dash.section.traffic': { en: 'Traffic', es: 'Tráfico', 'pt-BR': 'Tráfego' },
  'col.when': { en: 'When', es: 'Cuándo', 'pt-BR': 'Quando' },
  'dash.section.paid': { en: 'Getting paid', es: 'Cobros', 'pt-BR': 'Recebimentos' },
  'dash.paid.panel': { en: 'Getting paid', es: 'Cobros', 'pt-BR': 'Recebimentos' },
  'dash.paid.awaiting': { en: 'Awaiting payment', es: 'Pendiente de pago', 'pt-BR': 'Aguardando pagamento' },
  'dash.paid.last': { en: 'Last payment', es: 'Último pago', 'pt-BR': 'Último pagamento' },
  'dash.paid.none': {
    en: 'Nothing is awaiting payment.',
    es: 'No hay nada pendiente de pago.',
    'pt-BR': 'Não há nada aguardando pagamento.',
  },
  'dash.paid.noMethod': {
    en: 'You have not chosen how to be paid yet — set it in Account, or an invoice has nowhere to go.',
    es: 'Aún no has elegido cómo cobrar: configúralo en Cuenta, o una factura no tendrá adónde ir.',
    'pt-BR': 'Você ainda não escolheu como receber — configure em Conta, ou uma fatura não terá para onde ir.',
  },
  /* What is in this console, and what is switched off for this account. */
  'dash.section.guide': { en: 'What’s here', es: 'Qué hay aquí', 'pt-BR': 'O que tem aqui' },
  'dash.guide.panel': { en: 'Every part of this console', es: 'Cada parte de esta consola', 'pt-BR': 'Cada parte deste console' },
  'dash.guide.lead': {
    en: 'One line each, so you can find the thing you are looking for without opening all of them.',
    es: 'Una línea por cada uno, para que encuentres lo que buscas sin abrirlos todos.',
    'pt-BR': 'Uma linha para cada, para você achar o que procura sem abrir todos.',
  },
  'dash.guide.off.panel': {
    en: 'Switched off for your account',
    es: 'Desactivado en tu cuenta',
    'pt-BR': 'Desligado na sua conta',
  },
  'dash.guide.off.none': {
    en: 'Nothing. Everything this console offers is available to you.',
    es: 'Nada. Todo lo que ofrece esta consola está disponible para ti.',
    'pt-BR': 'Nada. Tudo o que este console oferece está disponível para você.',
  },
  'dash.guide.off.api': {
    en: 'API access — credentials for reading your own reports from your own systems. Your operator turns this on per affiliate. Ask them if you want it; the page for it is Account → API access.',
    es: 'Acceso por API: credenciales para leer tus informes desde tus propios sistemas. Tu operador lo activa por afiliado. Pídeselo si lo quieres; la página está en Cuenta → Acceso por API.',
    'pt-BR': 'Acesso por API — credenciais para ler seus relatórios a partir dos seus sistemas. Seu operador liga isso por afiliado. Peça se quiser; a página fica em Conta → Acesso por API.',
  },
  'guide.links': {
    en: 'Your tracking links, the banners to put them on, code to paste, QR codes, and your own grouping of them.',
    es: 'Tus enlaces de seguimiento, los banners donde ponerlos, el código para pegar, códigos QR y tu propia agrupación.',
    'pt-BR': 'Seus links de rastreamento, os banners, o código para colar, QR codes e seu próprio agrupamento.',
  },
  'guide.campaigns': {
    en: 'The campaigns you are on, what each pays, and the ones you could apply to.',
    es: 'Las campañas en las que estás, lo que paga cada una y a cuáles puedes postularte.',
    'pt-BR': 'As campanhas em que você está, quanto cada uma paga e as que você pode pedir para entrar.',
  },
  'guide.sources': {
    en: 'Where your clicks came from — by label, banner, country, device — and which ones did not count.',
    es: 'De dónde vinieron tus clics (etiqueta, banner, país, dispositivo) y cuáles no contaron.',
    'pt-BR': 'De onde vieram seus cliques — rótulo, banner, país, aparelho — e quais não contaram.',
  },
  'guide.postbacks': {
    en: 'Tell your own systems when something happens: register an address per event and test it.',
    es: 'Avisa a tus sistemas cuando algo ocurra: registra una dirección por evento y pruébala.',
    'pt-BR': 'Avise seus sistemas quando algo acontecer: registre um endereço por evento e teste.',
  },
  'guide.terms': {
    en: 'What you are paid and on what — every figure your deal actually states.',
    es: 'Qué te pagan y por qué: cada cifra que dice tu acuerdo.',
    'pt-BR': 'O que você recebe e por quê — cada número que o seu acordo diz.',
  },
  'guide.spend': {
    en: 'Declare what you spent, when your deal shares it.',
    es: 'Declara lo que gastaste, cuando tu acuerdo lo comparte.',
    'pt-BR': 'Declare o que você gastou, quando o seu acordo divide isso.',
  },
  'guide.statement': {
    en: 'This period’s commission, line by line, and how each line was worked out.',
    es: 'La comisión de este periodo, línea a línea, y cómo se calculó cada una.',
    'pt-BR': 'A comissão deste período, linha a linha, e como cada uma foi calculada.',
  },
  'guide.statements': {
    en: 'One statement per period, including the periods that earned nothing.',
    es: 'Un extracto por periodo, incluidos los periodos sin ganancias.',
    'pt-BR': 'Um extrato por período, incluindo os períodos sem ganhos.',
  },
  'guide.payments': {
    en: 'Invoices raised, what is awaiting payment, and what your operator says it has paid.',
    es: 'Facturas emitidas, qué está pendiente de pago y qué dice tu operador que ha pagado.',
    'pt-BR': 'Faturas emitidas, o que está aguardando pagamento e o que seu operador diz ter pago.',
  },
  'guide.history': {
    en: 'Everything that changed your money, who changed it and why.',
    es: 'Todo lo que cambió tu dinero, quién lo cambió y por qué.',
    'pt-BR': 'Tudo o que mudou o seu dinheiro, quem mudou e por quê.',
  },
  'guide.disputes': {
    en: 'Disagree with a figure — a deduction, a clawback, an amount held — with evidence and an answer.',
    es: 'Discrepa de una cifra (una deducción, una reversión, un importe retenido) con pruebas y respuesta.',
    'pt-BR': 'Conteste um valor — uma dedução, um estorno, um valor retido — com provas e resposta.',
  },
  'guide.players': {
    en: 'The players credited to you, whether each has qualified, and how cohorts are doing.',
    es: 'Los jugadores atribuidos a ti, si cada uno ha calificado y cómo van las cohortes.',
    'pt-BR': 'Os jogadores creditados a você, se cada um qualificou e como estão os grupos.',
  },
  'guide.subaffiliates': {
    en: 'People you referred, what they earn you, and the invitations you have out.',
    es: 'Personas que referiste, lo que te generan y las invitaciones enviadas.',
    'pt-BR': 'Pessoas que você indicou, o que elas rendem e os convites enviados.',
  },
  'guide.programmes': {
    en: 'Brands you do not work yet, and applying to one.',
    es: 'Marcas con las que aún no trabajas, y cómo postularte.',
    'pt-BR': 'Marcas com que você ainda não trabalha, e como se candidatar.',
  },
  'guide.support': {
    en: 'Ask your operator anything that is not a disagreement about money.',
    es: 'Pregunta a tu operador cualquier cosa que no sea un desacuerdo sobre dinero.',
    'pt-BR': 'Pergunte ao seu operador qualquer coisa que não seja discordância sobre dinheiro.',
  },
  'guide.account': {
    en: 'Your details, password, authenticator app, recent sign-ins, emails we sent, how you want to be paid, and API access.',
    es: 'Tus datos, contraseña, app de autenticación, inicios de sesión recientes, correos enviados, cómo quieres cobrar y el acceso por API.',
    'pt-BR': 'Seus dados, senha, app autenticador, entradas recentes, e-mails enviados, como você quer receber e o acesso por API.',
  },
  'dash.section.news': { en: 'News', es: 'Novedades', 'pt-BR': 'Novidades' },
  'dash.section.notes': { en: 'From your manager', es: 'De tu gestor', 'pt-BR': 'Do seu gerente' },
  'dash.notes.panel': { en: 'Notes from your manager', es: 'Notas de tu gestor', 'pt-BR': 'Notas do seu gerente' },
  'dash.notes.empty': {
    en: 'Nothing has been written to you yet.',
    es: 'Todavía no te han escrito nada.',
    'pt-BR': 'Ainda não escreveram nada para você.',
  },
  'dash.news.panel': { en: 'From your operator', es: 'De tu operador', 'pt-BR': 'Do seu operador' },
  'dash.news.badge': { en: 'New', es: 'Nuevo', 'pt-BR': 'Novo' },
  'dash.news.new': { en: '{n} new', es: '{n} nuevas', 'pt-BR': '{n} novas' },
  'dash.news.empty': {
    en: 'Nothing announced yet.',
    es: 'Nada anunciado todavía.',
    'pt-BR': 'Nada anunciado ainda.',
  },
  'dash.section.visits': { en: 'Recent visits', es: 'Visitas recientes', 'pt-BR': 'Visitas recentes' },
  'dash.visits.panel': { en: 'Recent visits', es: 'Visitas recientes', 'pt-BR': 'Visitas recentes' },
  'dash.visits.empty': {
    en: 'No clicks arrived in this window.',
    es: 'No llegó ningún clic en este periodo.',
    'pt-BR': 'Nenhum clique chegou neste período.',
  },
  'dash.section.discrepancy': {
    en: 'Click discrepancy',
    es: 'Discrepancia de clics',
    'pt-BR': 'Divergência de cliques',
  },
  'dash.traffic.panel': { en: 'Traffic', es: 'Tráfico', 'pt-BR': 'Tráfego' },
  'dash.traffic.days.one': { en: '{n} day', es: '{n} día', 'pt-BR': '{n} dia' },
  'dash.traffic.days.other': { en: '{n} days', es: '{n} días', 'pt-BR': '{n} dias' },
  'dash.traffic.empty': {
    en: 'No traffic in this period.',
    es: 'No hubo tráfico en este periodo.',
    'pt-BR': 'Não houve tráfego neste período.',
  },
  'dash.aside': {
    en: 'Offers and invitations',
    es: 'Ofertas e invitaciones',
    'pt-BR': 'Ofertas e convites',
  },

  /* ---------------- creating a link (§11.1, §11.2) ---------------- */
  // §10.12a — being approved by an operator is not being on every campaign,
  // and saying so beats an empty box.
  'link.noCampaigns': {
    en:
      'You are not on any campaigns yet. Being approved by your operator is not ' +
      'the same as being added to a campaign — your account manager can add you.',
    es:
      'Todavía no estás en ninguna campaña. Que tu operador te haya aprobado no es ' +
      'lo mismo que estar añadido a una campaña: tu gestor de cuenta puede añadirte.',
    'pt-BR':
      'Você ainda não está em nenhuma campanha. Ter sido aprovado pelo seu operador ' +
      'não é o mesmo que ter sido adicionado a uma campanha — seu gerente de conta ' +
      'pode adicionar você.',
  },
  'link.field.campaign': { en: 'Campaign', es: 'Campaña', 'pt-BR': 'Campanha' },
  // Item 7 — the collapsed summary over where it runs, its targeting, goals
  // and what it pays, so "Create link" is not below the fold by default.
  'link.terms.summary': {
    en: 'Where it runs and what it pays',
    es: 'Dónde se ejecuta y qué paga',
    'pt-BR': 'Onde roda e o que paga',
  },
  'campaign.osTargeting': { en: 'OS targeting', es: 'Segmentación por SO', 'pt-BR': 'Segmentação por SO' },
  'campaign.deviceTargeting': {
    en: 'Device targeting',
    es: 'Segmentación por dispositivo',
    'pt-BR': 'Segmentação por dispositivo',
  },
  'campaign.goals': { en: 'Goals', es: 'Objetivos', 'pt-BR': 'Objetivos' },
  'campaign.window': { en: 'Attribution window', es: 'Ventana de atribución', 'pt-BR': 'Janela de atribuição' },
  // Beside "live since", because the two together are the campaign's life and
  // either one alone invites the reader to guess the other. The attribution
  // window sits on the same panel and its default is 30 days, which has been
  // misread as an end date when no end date was shown.
  'campaign.runsUntil': { en: 'Runs until', es: 'Vigente hasta', 'pt-BR': 'Vigente até' },
  'campaign.runsUntil.open': {
    en: 'No end date set', es: 'Sin fecha de fin', 'pt-BR': 'Sem data de término',
  },
  'campaign.window.one': { en: '{n} day', es: '{n} día', 'pt-BR': '{n} dia' },
  'campaign.window.other': { en: '{n} days', es: '{n} días', 'pt-BR': '{n} dias' },
  'campaign.sampling': { en: 'Conversion sampling', es: 'Muestreo de conversiones', 'pt-BR': 'Amostragem de conversões' },
  'campaign.anchor': { en: 'Postclick anchor', es: 'Anclaje tras el clic', 'pt-BR': 'Âncora pós-clique' },
  'campaign.anchor.goal': { en: 'From a goal', es: 'Desde un objetivo', 'pt-BR': 'A partir de um objetivo' },
  'campaign.anchor.click': { en: 'From the click', es: 'Desde el clic', 'pt-BR': 'A partir do clique' },
  'campaign.liveSince': { en: 'Live since', es: 'Activa desde', 'pt-BR': 'No ar desde' },
  'campaign.trafficback': { en: 'Trafficback', es: 'Trafficback', 'pt-BR': 'Trafficback' },
  'link.field.domain': { en: 'Tracking domain', es: 'Dominio de seguimiento', 'pt-BR': 'Domínio de rastreamento' },
  'link.field.domain.hint': {
    en: 'Which verified hostname this link uses. The cookie is scoped to this domain.',
    es: 'Qué nombre de host verificado usa este enlace. La cookie está limitada a este dominio.',
    'pt-BR': 'Qual hostname verificado este link usa. O cookie fica restrito a este domínio.',
  },
  'link.field.domain.default': { en: '{domain} (default)', es: '{domain} (predeterminado)', 'pt-BR': '{domain} (padrão)' },
  'link.field.label': {
    en: 'Label this placement — required',
    es: 'Etiqueta esta ubicación — obligatorio',
    'pt-BR': 'Rotule esta posição — obrigatório',
  },
  'link.field.label.hint': {
    en:
      'For your own reporting. If you run three Facebook ads, label them and your ' +
      'reports break down by label. We store it, report on it, and never read it — ' +
      'it changes nothing about what you earn.',
    es:
      'Es para tus propios informes. Si tienes tres anuncios en Facebook, etiquétalos ' +
      'y tus informes se desglosarán por etiqueta. La guardamos, la reportamos y nunca ' +
      'la leemos: no cambia nada de lo que ganas.',
    'pt-BR':
      'É para os seus próprios relatórios. Se você roda três anúncios no Facebook, ' +
      'rotule-os e seus relatórios passam a ser detalhados por rótulo. Nós guardamos, ' +
      'reportamos e nunca lemos — isso não muda nada do que você ganha.',
  },
  'link.field.label.placeholder': {
    en: 'e.g. facebook-video-ad',
    es: 'p. ej. anuncio-video-facebook',
    'pt-BR': 'ex.: anuncio-video-facebook',
  },
  'link.field.extraLabel': { en: 'Extra label {n}', es: 'Etiqueta extra {n}', 'pt-BR': 'Rótulo extra {n}' },
  'link.addLabel': {
    en: '+ Add another label ({n} left)',
    es: '+ Añadir otra etiqueta (quedan {n})',
    'pt-BR': '+ Adicionar outro rótulo (restam {n})',
  },
  'link.creating': { en: 'Creating…', es: 'Creando…', 'pt-BR': 'Criando…' },
  'link.create': { en: 'Create link', es: 'Crear enlace', 'pt-BR': 'Criar link' },
  'link.new': { en: 'Your new link', es: 'Tu nuevo enlace', 'pt-BR': 'Seu novo link' },
  // §6.16 — only shown to an agency account, when creating a link.
  'link.field.subAccount': { en: 'Sub-account', es: 'Subcuenta', 'pt-BR': 'Subconta' },
  'link.field.subAccount.hint': {
    en: 'Which of your own sub-accounts this traffic is filed under, for your own reporting.',
    es: 'Bajo cuál de tus propias subcuentas se registra este tráfico, para tus propios informes.',
    'pt-BR': 'Sob qual das suas próprias subcontas este tráfego é registrado, para os seus próprios relatórios.',
  },
  'link.field.subAccount.none': { en: 'None', es: 'Ninguna', 'pt-BR': 'Nenhuma' },

  /*
   * §11.9 — the channel this link is filed under. The hint carries the whole
   * of §11.9a and has to: the affiliate is declaring where they will send
   * traffic from, and nothing here may read as the platform having detected
   * it. "You tell us" rather than "we saw".
   */
  'link.field.channel': { en: 'Traffic channel', es: 'Canal de tráfico', 'pt-BR': 'Canal de tráfego' },
  'link.field.channel.hint': {
    en: 'How you file this link’s traffic, in your own words — never something we detect. It can decide what this link is paid.',
    es: 'Cómo registras el tráfico de este enlace, en tus propias palabras — nunca algo que detectemos. Puede decidir cuánto se paga por este enlace.',
    'pt-BR': 'Como você registra o tráfego deste link, com suas próprias palavras — nunca algo que detectemos. Pode decidir quanto este link paga.',
  },
  'link.field.channel.none': {
    en: 'Not filed under a channel',
    es: 'Sin canal asignado',
    'pt-BR': 'Sem canal atribuído',
  },

  /* §11.5 — revoking, which has no undo. */
  'link.revoke.title': { en: 'Revoke this link?', es: '¿Revocar este enlace?', 'pt-BR': 'Revogar este link?' },
  // Item 2 — irreversible, and it did not say which link. Named before the
  // consequence, so the reader checks it is the right one before reading on.
  'link.revoke.which': {
    en: 'This revokes the link for {campaign}{label}.',
    es: 'Esto revoca el enlace de {campaign}{label}.',
    'pt-BR': 'Isso revoga o link de {campaign}{label}.',
  },
  'link.revoke.warning': {
    en:
      'This link stops accepting new clicks immediately, and cannot be un-revoked. ' +
      'Traffic already sent through it keeps its attribution — replace the link ' +
      'wherever it is still live.',
    es:
      'Este enlace deja de aceptar clics nuevos de inmediato, y no se puede ' +
      'des-revocar. El tráfico que ya pasó por él conserva su atribución: reemplaza ' +
      'el enlace allí donde siga publicado.',
    'pt-BR':
      'Este link para de aceitar cliques novos imediatamente, e não dá para desfazer. ' +
      'O tráfego que já passou por ele mantém a atribuição — substitua o link onde ' +
      'ele ainda estiver no ar.',
  },
  'link.revoke': { en: 'Revoke link', es: 'Revocar enlace', 'pt-BR': 'Revogar link' },

  /* ---------------- the Links screen ---------------- */
  'links.title': { en: 'Links', es: 'Enlaces', 'pt-BR': 'Links' },
  'links.sections': { en: 'Links sections', es: 'Secciones de Enlaces', 'pt-BR': 'Seções de Links' },
  'links.section.list': { en: 'Your links', es: 'Tus enlaces', 'pt-BR': 'Seus links' },
  'links.section.featured': { en: 'Featured', es: 'Destacadas', 'pt-BR': 'Destaques' },
  'links.section.clickReport': { en: 'Click report', es: 'Informe de clics', 'pt-BR': 'Relatório de cliques' },
  'links.section.promos': { en: 'Promo codes', es: 'Códigos promocionales', 'pt-BR': 'Códigos promocionais' },
  'links.section.creatives': { en: 'Creatives', es: 'Creatividades', 'pt-BR': 'Criativos' },
  'links.section.media': { en: 'Creative performance', es: 'Rendimiento de creatividades', 'pt-BR': 'Desempenho dos criativos' },
  'links.section.groups': { en: 'Your groups', es: 'Tus grupos', 'pt-BR': 'Seus grupos' },
  'links.section.ask': { en: 'Ask for a campaign', es: 'Pedir una campaña', 'pt-BR': 'Pedir uma campanha' },
  'ask.lead': {
    en: 'Your operator makes campaigns, because one carries a territory, the products it covers, its goals and the deal that pays you. Tell them what you want and why, and it lands in their queue rather than in an inbox.',
    es: 'Tu operador crea las campañas, porque una lleva un territorio, los productos que cubre, sus objetivos y el acuerdo que te paga. Diles qué quieres y por qué, y llegará a su cola en vez de a un correo.',
    'pt-BR': 'Seu operador cria as campanhas, porque uma carrega um território, os produtos que cobre, suas metas e o acordo que paga você. Diga o que quer e por quê, e isso entra na fila deles em vez de um e-mail.',
  },
  'ask.pickBrand': { en: 'Which brand', es: 'Qué marca', 'pt-BR': 'Qual marca' },
  'ask.name': { en: 'What you would call it', es: 'Cómo la llamarías', 'pt-BR': 'Como você chamaria' },
  'ask.name.placeholder': { en: 'Autumn sports push', es: 'Impulso deportivo de otoño', 'pt-BR': 'Campanha de esportes no outono' },
  'ask.reason': { en: 'What you want it for', es: 'Para qué la quieres', 'pt-BR': 'Para que você quer' },
  'ask.reason.hint': {
    en: 'The name is a label. This is the part they decide on.',
    es: 'El nombre es una etiqueta. Esto es lo que ellos deciden.',
    'pt-BR': 'O nome é um rótulo. Esta é a parte que eles decidem.',
  },
  'ask.send': { en: 'Ask', es: 'Pedir', 'pt-BR': 'Pedir' },
  'ask.empty': { en: 'You have asked for nothing yet.', es: 'Aún no has pedido nada.', 'pt-BR': 'Você ainda não pediu nada.' },
  'ask.status.OPEN': { en: 'Waiting on them', es: 'Esperando a ellos', 'pt-BR': 'Aguardando eles' },
  'ask.status.FULFILLED': { en: 'Made', es: 'Creada', 'pt-BR': 'Criada' },
  'ask.status.DECLINED': { en: 'Not this time', es: 'Esta vez no', 'pt-BR': 'Desta vez não' },
  'groups.name': { en: 'Group', es: 'Grupo', 'pt-BR': 'Grupo' },
  'groups.name.hint': {
    en: 'Your own name for a set of links — a site, a season, a placement. Reports split by it.',
    es: 'Tu propio nombre para un conjunto de enlaces: un sitio, una temporada, un emplazamiento. Los informes se dividen por él.',
    'pt-BR': 'Seu próprio nome para um conjunto de links — um site, uma temporada, um espaço. Os relatórios se dividem por ele.',
  },
  'groups.placeholder': { en: 'Summer on the blog', es: 'Verano en el blog', 'pt-BR': 'Verão no blog' },
  'groups.spend': { en: 'What it cost you', es: 'Lo que te costó', 'pt-BR': 'O que custou a você' },
  'groups.noSpend': { en: 'No spend', es: 'Sin gasto', 'pt-BR': 'Sem gasto' },
  'groups.links': { en: 'Links', es: 'Enlaces', 'pt-BR': 'Links' },
  'groups.earned': { en: 'Earned', es: 'Ganado', 'pt-BR': 'Ganho' },
  'groups.create': { en: 'Add group', es: 'Añadir grupo', 'pt-BR': 'Adicionar grupo' },
  'groups.rename': { en: 'Rename', es: 'Renombrar', 'pt-BR': 'Renomear' },
  'groups.none': { en: 'No group', es: 'Sin grupo', 'pt-BR': 'Sem grupo' },
  'customerGroup.col': { en: 'Customer group', es: 'Grupo de clientes', 'pt-BR': 'Grupo de clientes' },
  'customerGroup.none': { en: 'Default', es: 'Predeterminado', 'pt-BR': 'Padrão' },
  'groups.empty': {
    en: 'No groups yet. Make one, then put links in it to read them together.',
    es: 'Aún no hay grupos. Crea uno y añade enlaces para leerlos juntos.',
    'pt-BR': 'Nenhum grupo ainda. Crie um e coloque links nele para lê-los juntos.',
  },
  'media.lead': {
    en: 'How each banner performed. Two banners with the same clicks are not the same banner if one was shown ten times as often.',
    es: 'Cómo rindió cada banner. Dos banners con los mismos clics no son iguales si uno se mostró diez veces más.',
    'pt-BR': 'Como cada banner se saiu. Dois banners com os mesmos cliques não são iguais se um apareceu dez vezes mais.',
  },
  'media.empty': {
    en: 'Nothing recorded against a creative in this window. Codes copied from Creative performance carry the banner id; ones pasted without it still count for the campaign.',
    es: 'Nada registrado contra una creatividad en este periodo. Los códigos copiados desde aquí llevan el id del banner; los pegados sin él siguen contando para la campaña.',
    'pt-BR': 'Nada registrado para um criativo neste período. Os códigos copiados daqui levam o id do banner; os colados sem ele ainda contam para a campanha.',
  },
  /* #46 — one address, several campaigns. */
  'links.section.smart': { en: 'Smart links', es: 'Enlaces inteligentes', 'pt-BR': 'Links inteligentes' },
  // §6.16 — only offered as a tab when the account is an agency.
  'links.section.subAccounts': { en: 'Sub-accounts', es: 'Subcuentas', 'pt-BR': 'Subcontas' },
  'subAccounts.panel': { en: 'Your sub-accounts', es: 'Tus subcuentas', 'pt-BR': 'Suas subcontas' },
  'subAccounts.empty': {
    en: 'None yet. Ask your account manager to name one.',
    es: 'Ninguna todavía. Pide a tu gestor de cuenta que nombre una.',
    'pt-BR': 'Nenhuma ainda. Peça ao seu gerente de contas para nomear uma.',
  },
  'subAccounts.col.code': { en: 'Code', es: 'Código', 'pt-BR': 'Código' },
  'subAccounts.col.name': { en: 'Name', es: 'Nombre', 'pt-BR': 'Nome' },
  'subAccounts.col.status': { en: 'Status', es: 'Estado', 'pt-BR': 'Status' },
  'subAccounts.active': { en: 'Active', es: 'Activa', 'pt-BR': 'Ativa' },
  'subAccounts.archived': { en: 'Archived', es: 'Archivada', 'pt-BR': 'Arquivada' },
  'subAccounts.report.panel': {
    en: 'Commission by sub-account',
    es: 'Comisión por subcuenta',
    'pt-BR': 'Comissão por subconta',
  },
  'subAccounts.report.col.subAccount': { en: 'Sub-account', es: 'Subcuenta', 'pt-BR': 'Subconta' },
  'subAccounts.report.col.lines': { en: 'Lines', es: 'Líneas', 'pt-BR': 'Linhas' },
  'subAccounts.report.col.amount': { en: 'Amount', es: 'Importe', 'pt-BR': 'Valor' },
  'subAccounts.report.empty': {
    en: 'No commission in this range.',
    es: 'Sin comisión en este período.',
    'pt-BR': 'Sem comissão neste período.',
  },
  'subAccounts.report.unfiled': {
    en: 'Not filed under a sub-account',
    es: 'No registrado bajo una subcuenta',
    'pt-BR': 'Não registrado sob uma subconta',
  },
  'subAccounts.report.note': {
    en:
      'A flat fee, per-click, per-impression or spend-share line is not tied to any ' +
      'one player or click, so it is never filed under a sub-account — it always ' +
      'falls in "Not filed under a sub-account", together with any player-level line ' +
      'whose own link named none.',
    es:
      'Una línea de comisión fija, por clic, por impresión o de reparto de gasto no ' +
      'está vinculada a ningún jugador ni clic, así que nunca se registra bajo una ' +
      'subcuenta: siempre cae en "No registrado bajo una subcuenta", junto con ' +
      'cualquier línea a nivel de jugador cuyo propio enlace no nombró ninguna.',
    'pt-BR':
      'Uma linha de taxa fixa, por clique, por impressão ou de participação em gasto ' +
      'não está vinculada a nenhum jogador ou clique, então nunca é registrada sob ' +
      'uma subconta — ela sempre cai em "Não registrado sob uma subconta", junto com ' +
      'qualquer linha de nível de jogador cujo próprio link não nomeou nenhuma.',
  },
  'smart.panel': { en: 'Smart links', es: 'Enlaces inteligentes', 'pt-BR': 'Links inteligentes' },
  'smart.new': { en: 'New smart link', es: 'Nuevo enlace inteligente', 'pt-BR': 'Novo link inteligente' },
  'smart.empty': {
    en: 'None yet. A smart link is one address over several campaigns — each click goes to the first campaign in your list that will take it, so a campaign that closes costs you nothing to replace.',
    es: 'Ninguno todavía. Un enlace inteligente es una sola dirección para varias campañas: cada clic va a la primera campaña de tu lista que lo acepte, así que una campaña que cierra no te cuesta nada reemplazar.',
    'pt-BR': 'Nenhum ainda. Um link inteligente é um único endereço para várias campanhas: cada clique vai para a primeira campanha da sua lista que o aceitar, então uma campanha que fecha não custa nada para substituir.',
  },
  'smart.intro': {
    en: 'The address never changes when the list does. Anywhere you have already put it keeps working while what is behind it changes.',
    es: 'La dirección no cambia cuando cambia la lista. Donde ya la hayas puesto sigue funcionando mientras cambia lo que hay detrás.',
    'pt-BR': 'O endereço não muda quando a lista muda. Onde você já o colocou continua funcionando enquanto o que está por trás muda.',
  },
  'smart.col.name': { en: 'Name', es: 'Nombre', 'pt-BR': 'Nome' },
  'smart.col.pool': { en: 'Goes to', es: 'Va a', 'pt-BR': 'Vai para' },
  'smart.col.chooses': { en: 'Chooses', es: 'Elige', 'pt-BR': 'Escolhe' },
  'smart.col.clicks': { en: 'Clicks (30 days)', es: 'Clics (30 días)', 'pt-BR': 'Cliques (30 dias)' },
  'smart.col.unmatched': { en: 'Unmatched', es: 'Sin destino', 'pt-BR': 'Sem destino' },
  'smart.chooses.priority': { en: 'First that will take it', es: 'La primera que lo acepte', 'pt-BR': 'A primeira que aceitar' },
  'smart.chooses.spread': { en: 'Spread across those that will', es: 'Repartido entre las que lo acepten', 'pt-BR': 'Distribuído entre as que aceitarem' },
  'smart.unmatched.hint': {
    en: 'Clicks no campaign in the list would take. They still reached the brand’s own page — nothing was lost, but nothing was attributed either.',
    es: 'Clics que ninguna campaña de la lista aceptó. Llegaron igualmente a la página de la marca: no se perdió nada, pero tampoco se atribuyó nada.',
    'pt-BR': 'Cliques que nenhuma campanha da lista aceitou. Chegaram mesmo assim à página da marca: nada se perdeu, mas nada foi atribuído.',
  },
  'smart.name.label': { en: 'Name', es: 'Nombre', 'pt-BR': 'Nome' },
  'smart.pool.label': { en: 'Campaigns', es: 'Campañas', 'pt-BR': 'Campanhas' },
  'smart.selection.label': { en: 'When more than one will take a click', es: 'Cuando más de una acepte un clic', 'pt-BR': 'Quando mais de uma aceitar um clique' },
  'smart.notOn': { en: 'You are not on this campaign yet', es: 'Todavía no estás en esta campaña', 'pt-BR': 'Você ainda não está nesta campanha' },
  'smart.notOn.hint': {
    en: 'It stays in the list and is skipped until you are added — nothing to regenerate then.',
    es: 'Permanece en la lista y se omite hasta que te añadan; entonces no hay nada que regenerar.',
    'pt-BR': 'Permanece na lista e é ignorada até você ser adicionado; então não há nada para regenerar.',
  },
  'smart.disable': { en: 'Turn off', es: 'Desactivar', 'pt-BR': 'Desativar' },
  'smart.disabled': { en: 'Off', es: 'Desactivado', 'pt-BR': 'Desativado' },
  'smart.save': { en: 'Save', es: 'Guardar', 'pt-BR': 'Salvar' },
  'smart.create': { en: 'Create', es: 'Crear', 'pt-BR': 'Criar' },
  'smart.cancel': { en: 'Cancel', es: 'Cancelar', 'pt-BR': 'Cancelar' },
  'smart.edit': { en: 'Edit', es: 'Editar', 'pt-BR': 'Editar' },

  'featured.panel': { en: 'Featured campaigns', es: 'Campañas destacadas', 'pt-BR': 'Campanhas em destaque' },
  'featured.empty': {
    en: 'Nothing featured right now. Everything you can promote is under Your links.',
    es: 'Nada destacado ahora mismo. Todo lo que puedes promocionar está en Tus enlaces.',
    'pt-BR': 'Nada em destaque agora. Tudo o que você pode promover está em Seus links.',
  },
  'featured.col.sampling': { en: 'Sampling', es: 'Muestreo', 'pt-BR': 'Amostragem' },
  'featured.sampling.none': { en: 'None', es: 'Ninguno', 'pt-BR': 'Nenhum' },
  // Featured changes where a campaign appears and nothing about the terms, and
  // the sentence exists so nobody reads a promotion as a better rate.
  'featured.note': {
    en:
      'Highlighted by the operator. These are the same campaigns you can already ' +
      'build links for — being featured changes nothing about your terms.',
    es:
      'Destacadas por el operador. Son las mismas campañas para las que ya puedes ' +
      'crear enlaces: estar destacada no cambia nada de tus condiciones.',
    'pt-BR':
      'Destacadas pelo operador. São as mesmas campanhas para as quais você já pode ' +
      'criar links — estar em destaque não muda nada nas suas condições.',
  },

  'links.panel': { en: 'Your links', es: 'Tus enlaces', 'pt-BR': 'Seus links' },
  'links.panel.brand': { en: 'Your links — {brand}', es: 'Tus enlaces — {brand}', 'pt-BR': 'Seus links — {brand}' },
  'links.empty': { en: 'No links yet.', es: 'Todavía no hay enlaces.', 'pt-BR': 'Ainda não há links.' },
  'links.chip.revoked': { en: 'Revoked', es: 'Revocado', 'pt-BR': 'Revogado' },
  // §10.12c — this one comes back on its own when the affiliate is added again,
  // which is why it is not the revoked wording in any language.
  'links.chip.notOnCampaign': {
    en: 'Not on this campaign',
    es: 'No estás en esta campaña',
    'pt-BR': 'Você não está nesta campanha',
  },
  'links.revokeAction': { en: 'Revoke', es: 'Revocar', 'pt-BR': 'Revogar' },
  'links.search.placeholder': {
    en: 'Search by campaign or label',
    es: 'Buscar por campaña o etiqueta',
    'pt-BR': 'Buscar por campanha ou rótulo',
  },
  'links.search.empty': {
    en: 'No links match “{query}”.',
    es: 'Ningún enlace coincide con “{query}”.',
    'pt-BR': 'Nenhum link corresponde a “{query}”.',
  },
  'links.search.clear': { en: 'Clear search', es: 'Borrar búsqueda', 'pt-BR': 'Limpar busca' },
  /* One link, in the forms somebody actually publishes it in. */
  'linkTools.open': { en: 'Get code', es: 'Obtener código', 'pt-BR': 'Obter código' },
  'linkTools.title': { en: 'Publish this link', es: 'Publicar este enlace', 'pt-BR': 'Publicar este link' },
  'linkTools.anchor': { en: 'Link text', es: 'Texto del enlace', 'pt-BR': 'Texto do link' },
  'linkTools.anchor.hint': {
    en: 'The words people will click. Leave it blank to use the campaign name.',
    es: 'Las palabras en las que la gente hará clic. Déjalo en blanco para usar el nombre de la campaña.',
    'pt-BR': 'As palavras em que as pessoas vão clicar. Deixe em branco para usar o nome da campanha.',
  },
  'linkTools.html': { en: 'Ready to paste', es: 'Listo para pegar', 'pt-BR': 'Pronto para colar' },
  'linkTools.s2sRequest': { en: 'Your request ID', es: 'Tu ID de solicitud', 'pt-BR': 'Seu ID de requisição' },
  'linkTools.s2sPublisher': { en: 'Your publisher ID', es: 'Tu ID de publisher', 'pt-BR': 'Seu ID de publisher' },
  'linkTools.s2s.hint': {
    en: 'Optional. Both come back to you on the postback, so your own system can match the conversion to the click it sent.',
    es: 'Opcional. Ambos vuelven en el postback, para que tu sistema empareje la conversión con el clic que la envió.',
    'pt-BR': 'Opcional. Ambos voltam no postback, para o seu sistema casar a conversão com o clique que a gerou.',
  },
  'linkTools.qr': { en: 'QR code', es: 'Código QR', 'pt-BR': 'Código QR' },
  'linkTools.qr.hint': {
    en: 'The same link as a square, for print and for screens people photograph.',
    es: 'El mismo enlace como un cuadro, para impresión y para pantallas que la gente fotografía.',
    'pt-BR': 'O mesmo link como um quadrado, para impressão e para telas que as pessoas fotografam.',
  },
  'linkTools.qr.alt': {
    en: 'QR code for {campaign}',
    es: 'Código QR para {campaign}',
    'pt-BR': 'Código QR para {campaign}',
  },
  'linkTools.qr.download': { en: 'Download', es: 'Descargar', 'pt-BR': 'Baixar' },
  'linkTools.pixel': { en: 'Impression pixel', es: 'Píxel de impresión', 'pt-BR': 'Pixel de impressão' },
  'linkTools.pixel.hint': {
    en: 'Put this beside a banner so the times it is shown are counted, not just the clicks.',
    es: 'Ponlo junto a un banner para que se cuenten las veces que se muestra, no solo los clics.',
    'pt-BR': 'Coloque isto ao lado de um banner para que as vezes em que ele aparece sejam contadas, não só os cliques.',
  },
  'linkTools.unavailable': {
    en: 'This link has an address we do not recognise, so we cannot build a QR code or a pixel for it. Copy the link itself from the list.',
    es: 'Este enlace tiene una dirección que no reconocemos, así que no podemos crear un código QR ni un píxel. Copia el enlace desde la lista.',
    'pt-BR': 'Este link tem um endereço que não reconhecemos, então não podemos criar um código QR nem um pixel. Copie o link na lista.',
  },
  'links.createModal': { en: 'Create a link', es: 'Crear un enlace', 'pt-BR': 'Criar um link' },
  'links.createModal.brand': {
    en: 'Create a link — {brand}',
    es: 'Crear un enlace — {brand}',
    'pt-BR': 'Criar um link — {brand}',
  },

  'clickReport.panel': { en: 'Click report', es: 'Informe de clics', 'pt-BR': 'Relatório de cliques' },
  'clickReport.empty': {
    en: 'No clicks on any link in this period.',
    es: 'No hubo clics en ningún enlace en este periodo.',
    'pt-BR': 'Não houve cliques em nenhum link neste período.',
  },

  'promos.panel': { en: 'Promo codes', es: 'Códigos promocionales', 'pt-BR': 'Códigos promocionais' },
  'promos.panel.brand': {
    en: 'Promo codes — {brand}',
    es: 'Códigos promocionales — {brand}',
    'pt-BR': 'Códigos promocionais — {brand}',
  },
  'promos.empty': {
    en: 'No promo codes assigned yet.',
    es: 'Todavía no tienes códigos promocionales asignados.',
    'pt-BR': 'Ainda não há códigos promocionais atribuídos.',
  },
  // A promo code with no end date. "Open" as in open-ended, not as in available.
  'promos.openEnded': { en: 'Open', es: 'Sin fecha de fin', 'pt-BR': 'Sem data de fim' },

  'creatives.panel': { en: 'Creatives', es: 'Creatividades', 'pt-BR': 'Criativos' },
  'creatives.panel.brand': {
    en: 'Creatives — {brand}',
    es: 'Creatividades — {brand}',
    'pt-BR': 'Criativos — {brand}',
  },
  'creativeType.BANNER': { en: 'Banner', es: 'Banner', 'pt-BR': 'Banner' },
  'creativeType.HTML5': { en: 'HTML5 unit', es: 'Unidad HTML5', 'pt-BR': 'Unidade HTML5' },
  'creativeType.VIDEO': { en: 'Video', es: 'Vídeo', 'pt-BR': 'Vídeo' },
  'creativeType.TEXT_LINK': { en: 'Text link', es: 'Enlace de texto', 'pt-BR': 'Link de texto' },
  'creativeType.EMAIL_TEMPLATE': { en: 'Mailer', es: 'Plantilla de correo', 'pt-BR': 'Modelo de e-mail' },
  'creativeType.LANDING_PAGE': { en: 'Landing page', es: 'Página de destino', 'pt-BR': 'Página de destino' },
  'creativeType.COUPON': { en: 'Coupon', es: 'Cupón', 'pt-BR': 'Cupom' },
  'creativeType.FEED': { en: 'Feed', es: 'Feed', 'pt-BR': 'Feed' },
  'creativeType.DEEP_LINK': { en: 'Deep link', es: 'Enlace profundo', 'pt-BR': 'Deep link' },
  'creativeType.APP_LINK': { en: 'App link', es: 'Enlace de app', 'pt-BR': 'Link de app' },
  'creatives.filter.size': { en: 'Size', es: 'Tamaño', 'pt-BR': 'Tamanho' },
  'creatives.filter.type': { en: 'Kind', es: 'Tipo', 'pt-BR': 'Tipo' },
  'creatives.filter.product': { en: 'Product', es: 'Producto', 'pt-BR': 'Produto' },
  'creatives.filter.language': { en: 'Language', es: 'Idioma', 'pt-BR': 'Idioma' },
  'creatives.filter.geo': { en: 'Market', es: 'Mercado', 'pt-BR': 'Mercado' },
  'creatives.link.choose': { en: 'Your link', es: 'Tu enlace', 'pt-BR': 'Seu link' },
  'creatives.link.default': { en: 'Default landing page', es: 'Página de destino predeterminada', 'pt-BR': 'Página de destino padrão' },
  'creatives.filter.all': { en: 'All', es: 'Todos', 'pt-BR': 'Todos' },
  'creatives.page': { en: 'Page {page} of {pages}', es: 'Página {page} de {pages}', 'pt-BR': 'Página {page} de {pages}' },
  'creatives.prev': { en: 'Previous', es: 'Anterior', 'pt-BR': 'Anterior' },
  'creatives.next': { en: 'Next', es: 'Siguiente', 'pt-BR': 'Próxima' },
  'creatives.info': { en: 'Details', es: 'Detalles', 'pt-BR': 'Detalhes' },
  'creatives.noPreview': {
    en: 'No preview — this one is not a picture',
    es: 'Sin vista previa: este no es una imagen',
    'pt-BR': 'Sem prévia — este não é uma imagem',
  },
  'creatives.noLink': {
    en: 'Create a link for this campaign first — the code has to carry your link, or the traffic is credited to nobody.',
    es: 'Crea primero un enlace para esta campaña: el código debe llevar tu enlace, o el tráfico no se acredita a nadie.',
    'pt-BR': 'Crie primeiro um link para esta campanha — o código precisa levar o seu link, senão o tráfego não é creditado a ninguém.',
  },
  'creatives.emptyForFilter': {
    en: 'No creatives match these filters.',
    es: 'Ninguna creatividad coincide con estos filtros.',
    'pt-BR': 'Nenhum criativo corresponde a estes filtros.',
  },
  'creatives.empty': {
    en: 'No published creatives for your campaigns yet.',
    es: 'Todavía no hay creatividades publicadas para tus campañas.',
    'pt-BR': 'Ainda não há criativos publicados para as suas campanhas.',
  },

  /* ---------------- terms proposed, and terms in force (§7.8) ---------------- */
  'proposals.panel': {
    en: 'Terms awaiting your answer',
    es: 'Condiciones a la espera de tu respuesta',
    'pt-BR': 'Condições aguardando sua resposta',
  },
  'proposals.when': {
    en: 'Proposed on {proposed} · in force from {from} if you accept.',
    es: 'Propuestas el {proposed} · en vigor desde el {from} si las aceptas.',
    'pt-BR': 'Propostas em {proposed} · em vigor a partir de {from} se você aceitar.',
  },
  // The right rail is 320px wide; a proposal's full criteria and deduction
  // stack ran the card past 400px there, so they sit behind this disclosure.
  // Accept and Reject never do — only the reasoning behind them collapses.
  'proposals.showTerms': {
    en: 'Show full terms',
    es: 'Ver condiciones completas',
    'pt-BR': 'Ver condições completas',
  },
  // §20.4a — the order is part of the deal. GGR − fee% − bonus and
  // GGR − bonus − fee% are different numbers, so the heading says "in this order".
  'terms.deductions': {
    en: 'Deducted before your share, in this order',
    es: 'Se deduce antes de tu parte, en este orden',
    'pt-BR': 'Deduzido antes da sua parte, nesta ordem',
  },
  'proposals.accepting': { en: 'Accepting…', es: 'Aceptando…', 'pt-BR': 'Aceitando…' },
  'proposals.accept': { en: 'Accept terms', es: 'Aceptar condiciones', 'pt-BR': 'Aceitar condições' },
  'proposals.reject': { en: 'Reject', es: 'Rechazar', 'pt-BR': 'Recusar' },

  'dealHistory.loading': { en: 'Loading…', es: 'Cargando…', 'pt-BR': 'Carregando…' },
  'dealHistory.view': { en: 'View history', es: 'Ver historial', 'pt-BR': 'Ver histórico' },
  'dealHistory.onlyVersion': {
    en: 'No earlier versions — this is the first.',
    es: 'No hay versiones anteriores: esta es la primera.',
    'pt-BR': 'Não há versões anteriores — esta é a primeira.',
  },
  'dealHistory.col.terms': { en: 'Terms', es: 'Condiciones', 'pt-BR': 'Condições' },
  'dealHistory.col.inForce': { en: 'In force', es: 'En vigor', 'pt-BR': 'Em vigor' },
  'dealHistory.col.accepted': { en: 'Accepted', es: 'Aceptadas', 'pt-BR': 'Aceitas' },

  'terms.title': { en: 'Your terms', es: 'Tus condiciones', 'pt-BR': 'Suas condições' },
  'terms.empty': {
    en:
      'No terms are in force yet: you have no deal of your own and take part in no campaign ' +
      'that pays you. Nothing accrues until you join a campaign or accept terms — your ' +
      'account manager can tell you where yours are.',
    es:
      'Todavía no hay condiciones en vigor: no tienes acuerdo propio ni participas en ninguna ' +
      'campaña que te pague. No se acumula nada hasta que te unas a una campaña o aceptes ' +
      'condiciones; tu gestor de cuenta puede decirte dónde están las tuyas.',
    'pt-BR':
      'Ainda não há condições em vigor: você não tem acordo próprio nem participa de nenhuma ' +
      'campanha que pague você. Nada é acumulado até você entrar em uma campanha ou aceitar ' +
      'condições — seu gerente de conta pode dizer onde estão as suas.',
  },
  'terms.since': { en: 'In force since {date}.', es: 'En vigor desde el {date}.', 'pt-BR': 'Em vigor desde {date}.' },

  // §26.2a — without this, withheld months look like the operator simply did
  // not pay.
  'recoupment.panel': {
    en: 'CPA recoupment by player',
    es: 'Recuperación de CPA por jugador',
    'pt-BR': 'Recuperação de CPA por jogador',
  },
  'recoupment.empty': {
    en: 'Nothing to recoup. A balance appears here only on a deal that recovers a CPA already paid from your revenue share on the same player. A CPA reversed after a chargeback shows under Commission as a clawback line, not here.',
    es: 'Nada que recuperar. Aquí aparece un saldo solo en un acuerdo que recupera de tu reparto de ingresos una CPA ya pagada por el mismo jugador. Una CPA revertida tras un contracargo aparece en Comisión como una línea de recuperación, no aquí.',
    'pt-BR': 'Nada a recuperar. Um saldo aparece aqui apenas em um acordo que recupera da sua participação na receita uma CPA já paga pelo mesmo jogador. Uma CPA revertida após um estorno de pagamento aparece em Comissão como uma linha de estorno, não aqui.',
  },

  /* ---------------- click discrepancy (§12.5c) ---------------- */
  'discrepancy.panel': { en: 'Click discrepancy', es: 'Discrepancia de clics', 'pt-BR': 'Divergência de cliques' },
  'discrepancy.allCampaigns': { en: 'All campaigns', es: 'Todas las campañas', 'pt-BR': 'Todas as campanhas' },
  'discrepancy.product': { en: 'Product', es: 'Producto', 'pt-BR': 'Produto' },
  'discrepancy.allProducts': { en: 'All products', es: 'Todos los productos', 'pt-BR': 'Todos os produtos' },
  'discrepancy.yourTotal': {
    en: 'Your tracker total (optional)',
    es: 'Total de tu tracker (opcional)',
    'pt-BR': 'Total do seu tracker (opcional)',
  },
  'discrepancy.yourTotal.hint': {
    en: 'Type your tracker’s click count for this same date range, then Compare.',
    es: 'Escribe el número de clics de tu tracker para este mismo rango de fechas y pulsa Comparar.',
    'pt-BR': 'Digite a contagem de cliques do seu tracker para este mesmo intervalo e clique em Comparar.',
  },
  'discrepancy.yourTotal.placeholder': { en: 'e.g. 1240', es: 'p. ej. 1240', 'pt-BR': 'ex.: 1240' },
  // Says what is wrong with it, because the reader's own grouping is accepted
  // — "1,240" in English, "1.240" in Spanish — and what is refused is a
  // decimal, a minus or stray letters, not the separator they are used to.
  'discrepancy.yourTotal.notANumber': {
    en: 'A whole number of clicks, please — digits, with or without your usual grouping.',
    es: 'Un número entero de clics, por favor: cifras, con o sin tu separador habitual.',
    'pt-BR': 'Um número inteiro de cliques, por favor: algarismos, com ou sem o separador que você usa.',
  },
  'discrepancy.compare': { en: 'Compare', es: 'Comparar', 'pt-BR': 'Comparar' },
  'discrepancy.oursValid': { en: 'Ours valid', es: 'Válidos según nosotros', 'pt-BR': 'Válidos segundo nós' },
  'discrepancy.yourTracker': { en: 'Your tracker', es: 'Tu tracker', 'pt-BR': 'Seu tracker' },
  'discrepancy.gap': { en: 'Gap vs valid', es: 'Diferencia frente a válidos', 'pt-BR': 'Diferença frente aos válidos' },
  'discrepancy.explained': { en: 'Explained (rejected)', es: 'Explicados (rechazados)', 'pt-BR': 'Explicados (rejeitados)' },
  'discrepancy.unexplained': { en: 'Unexplained gap', es: 'Diferencia sin explicar', 'pt-BR': 'Diferença sem explicação' },
  'discrepancy.bot': { en: 'Bot', es: 'Bot', 'pt-BR': 'Bot' },
  'discrepancy.duplicate': { en: 'Duplicate', es: 'Duplicados', 'pt-BR': 'Duplicados' },
  'discrepancy.geoBlocked': { en: 'Geo-blocked', es: 'Bloqueados por país', 'pt-BR': 'Bloqueados por país' },
  'discrepancy.osBlocked': { en: 'OS-blocked', es: 'Bloqueados por SO', 'pt-BR': 'Bloqueados por SO' },
  'discrepancy.deviceBlocked': {
    en: 'Device-blocked',
    es: 'Bloqueados por dispositivo',
    'pt-BR': 'Bloqueados por dispositivo',
  },
  'discrepancy.targetingBlocked': {
    en: 'Targeting-blocked',
    es: 'Bloqueados por segmentación',
    'pt-BR': 'Bloqueados por segmentação',
  },
  'discrepancy.noSubIds': {
    en: 'No traffic broken down by sub-ID yet.',
    es: 'Todavía no hay tráfico desglosado por sub-ID.',
    'pt-BR': 'Ainda não há tráfego detalhado por sub-ID.',
  },
  'discrepancy.raised': {
    en: 'Raised. Its id is',
    es: 'Presentada. Su id es',
    'pt-BR': 'Aberta. O id dela é',
  },
  'discrepancy.raised.keep': {
    en: '— you will need it to track this later.',
    es: '— lo necesitarás para hacer seguimiento más adelante.',
    'pt-BR': '— você vai precisar dele para acompanhar isto depois.',
  },
  'dispute.field.what': {
    en: 'What happened, in your words',
    es: 'Qué pasó, en tus palabras',
    'pt-BR': 'O que aconteceu, nas suas palavras',
  },
  'discrepancy.raisedAgainst': {
    en: 'Raised against {campaign}',
    es: 'Presentada contra {campaign}',
    'pt-BR': 'Aberta contra {campaign}',
  },
  'discrepancy.noCampaign': {
    en: 'Raised with no specific campaign — pick one above to narrow it.',
    es: 'Presentada sin campaña concreta: elige una arriba para acotarla.',
    'pt-BR': 'Aberta sem campanha específica — escolha uma acima para restringir.',
  },
  'discrepancy.placeholder': {
    en: 'My tracker shows {total}, {gap} more than valid clicks explain.',
    es: 'Mi tracker muestra {total}, {gap} más de lo que explican los clics válidos.',
    'pt-BR': 'Meu tracker mostra {total}, {gap} a mais do que os cliques válidos explicam.',
  },
  'discrepancy.raising': { en: 'Raising…', es: 'Presentando…', 'pt-BR': 'Abrindo…' },
  'discrepancy.raiseIt': { en: 'Raise it', es: 'Presentarla', 'pt-BR': 'Abrir' },
  'discrepancy.raiseAbout': {
    en: 'Raise a dispute about this',
    es: 'Presentar una disputa sobre esto',
    'pt-BR': 'Abrir uma contestação sobre isto',
  },

  /* ---------------- Sources (§39.6) ---------------- */
  'sources.title': { en: 'Sources', es: 'Fuentes', 'pt-BR': 'Origens' },
  'sources.sections': { en: 'Source dimensions', es: 'Dimensiones de origen', 'pt-BR': 'Dimensões de origem' },
  /* The five reports this screen holds. Named for what each one answers, not
     for the panel it used to be stacked in. */
  'sources.views': { en: 'Source reports', es: 'Informes de origen', 'pt-BR': 'Relatórios de origem' },
  'sources.view.dimension': { en: 'By source', es: 'Por origen', 'pt-BR': 'Por origem' },
  'sources.view.campaign': { en: 'By campaign', es: 'Por campaña', 'pt-BR': 'Por campanha' },
  'sources.exportCsv': { en: 'Export CSV', es: 'Exportar CSV', 'pt-BR': 'Exportar CSV' },
  /* Heads the three tick boxes that say what a download carries, so three
     loose checkboxes above a table cannot be read as filters on the table. */
  'sources.exportOptions': {
    en: 'In the download', es: 'En la descarga', 'pt-BR': 'No download',
  },
  'sources.exportXml': { en: 'Export XML', es: 'Exportar XML', 'pt-BR': 'Exportar XML' },
  'sources.empty': {
    en: 'No traffic on this dimension in the period.',
    es: 'No hubo tráfico en esta dimensión durante el periodo.',
    'pt-BR': 'Não houve tráfego nesta dimensão no período.',
  },
  'sources.byCampaign': { en: 'By campaign (overview)', es: 'Por campaña (resumen)', 'pt-BR': 'Por campanha (visão geral)' },
  'sources.byCampaign.empty': {
    en: 'No campaign traffic in this period.',
    es: 'No hubo tráfico de campañas en este periodo.',
    'pt-BR': 'Não houve tráfego de campanhas neste período.',
  },
  'benchmark.panel': { en: 'Peer comparison', es: 'Comparación con pares', 'pt-BR': 'Comparação com pares' },
  'benchmark.yourClicks': { en: 'Your clicks', es: 'Tus clics', 'pt-BR': 'Seus cliques' },
  'benchmark.peerMedian': { en: 'Peer median', es: 'Mediana de los pares', 'pt-BR': 'Mediana dos pares' },
  'benchmark.peers': { en: 'Peers', es: 'Pares', 'pt-BR': 'Pares' },
  'benchmark.empty': {
    en: 'No benchmark for this period.',
    es: 'No hay comparativa para este periodo.',
    'pt-BR': 'Não há comparativo para este período.',
  },

  /* ---------------- Commission ---------------- */
  'commission.title': { en: 'Commission', es: 'Comisión', 'pt-BR': 'Comissão' },
  // §39 — the report leaves the screen, because reconciling a month by copying
  // figures by hand is how a transcription error becomes a dispute about the
  // platform's arithmetic.
  'commission.downloadCsv': { en: 'Download CSV', es: 'Descargar CSV', 'pt-BR': 'Baixar CSV' },
  'commission.sections': { en: 'Commission sections', es: 'Secciones de Comisión', 'pt-BR': 'Seções de Comissão' },
  /*
   * T22 — the owner's word for this screen, chosen 2026-09-25 over "Held
   * before payable". It is one concept with one screen: the money is in
   * quarantine until its hold expires, and the rows underneath still say
   * "held" per line because that is what each one is.
   */
  'commission.section.held': {
    en: 'Quarantine',
    es: 'Cuarentena',
    'pt-BR': 'Quarentena',
  },
  'commission.quarantine.lead': {
    en: 'Commission that is earned and not yet payable, and what each line is waiting on.',
    es: 'Comisión ganada que aún no es pagadera, y qué está esperando cada línea.',
    'pt-BR': 'Comissão ganha que ainda não é pagável, e o que cada linha está aguardando.',
  },
  'commission.section.periods': {
    en: 'By period and brand',
    es: 'Por periodo y marca',
    'pt-BR': 'Por período e marca',
  },
  'commission.section.trace': { en: 'Calculation trace', es: 'Traza del cálculo', 'pt-BR': 'Rastro do cálculo' },
  'commission.empty': {
    en: 'No commission in this period.',
    es: 'No hay comisión en este periodo.',
    'pt-BR': 'Não há comissão neste período.',
  },
  // §39.4 — judged on this line's own period, not the range's.
  'commission.provisional': { en: 'Provisional', es: 'Provisional', 'pt-BR': 'Provisória' },
  'commission.locked': { en: 'Locked', es: 'Cerrada', 'pt-BR': 'Fechada' },
  'commission.trace': { en: 'Trace', es: 'Trazar', 'pt-BR': 'Rastrear' },
  'trace.panel': { en: 'Calculation trace', es: 'Traza del cálculo', 'pt-BR': 'Rastro do cálculo' },
  'trace.field.lineId': {
    en: 'Commission line id',
    es: 'Id de la línea de comisión',
    'pt-BR': 'Id da linha de comissão',
  },
  'trace.loading': { en: 'Loading…', es: 'Cargando…', 'pt-BR': 'Carregando…' },
  // §23.8 — the trace, one row per fact. Never the rule ids: they are the
  // server's, and a number from a document the affiliate has never seen
  // answers nothing.
  'trace.row.type': { en: 'Type', es: 'Tipo', 'pt-BR': 'Tipo' },
  'trace.row.player': { en: 'Player', es: 'Jugador', 'pt-BR': 'Jogador' },
  'trace.row.country': { en: 'Country', es: 'País', 'pt-BR': 'País' },
  'trace.row.product': { en: 'Product', es: 'Producto', 'pt-BR': 'Produto' },
  'trace.row.dealVersion': { en: 'Deal version', es: 'Versión del acuerdo', 'pt-BR': 'Versão do acordo' },
  'trace.row.period': { en: 'Period', es: 'Periodo', 'pt-BR': 'Período' },
  'trace.row.basis': { en: 'Basis', es: 'Base', 'pt-BR': 'Base' },
  'trace.row.rate': { en: 'Rate', es: 'Tasa', 'pt-BR': 'Taxa' },
  'trace.row.priceRow': { en: 'Price row applied', es: 'Fila de precio aplicada', 'pt-BR': 'Linha de preço aplicada' },
  'trace.row.amount': { en: 'Amount', es: 'Importe', 'pt-BR': 'Valor' },
  'trace.row.computedAt': { en: 'Computed at', es: 'Calculado el', 'pt-BR': 'Calculado em' },
  'trace.dealVersion': { en: 'Version {n}', es: 'Versión {n}', 'pt-BR': 'Versão {n}' },
  'trace.priceRow.base': {
    en: 'Base rate — no price row matched',
    es: 'Tarifa base: ninguna fila de precio coincidió',
    'pt-BR': 'Taxa base — nenhuma linha de preço correspondeu',
  },
  'trace.steps': { en: 'Steps', es: 'Pasos', 'pt-BR': 'Etapas' },
  'trace.step.computePeriod': {
    en: 'Calculated in the period\'s commission run',
    es: 'Calculado en la ejecución de comisiones del periodo',
    'pt-BR': 'Calculado na execução de comissões do período',
  },
  'trace.breakdown': { en: 'Deductions from the basis', es: 'Deducciones de la base', 'pt-BR': 'Deduções da base' },

  /* ---------------- Disputes (Ch. 33) ---------------- */
  'disputeCat.COMMISSION_FIGURE': {
    en: 'A commission figure',
    es: 'Una cifra de comisión',
    'pt-BR': 'Um valor de comissão',
  },
  'disputeCat.ATTRIBUTION': {
    en: 'A player attributed to someone else',
    es: 'Un jugador atribuido a otra persona',
    'pt-BR': 'Um jogador atribuído a outra pessoa',
  },
  'disputeCat.DEDUCTION': { en: 'A deduction', es: 'Una deducción', 'pt-BR': 'Uma dedução' },
  'disputeCat.CLAWBACK': { en: 'A clawback', es: 'Una recuperación', 'pt-BR': 'Um estorno' },
  'disputeCat.HOLD': { en: 'An amount being held', es: 'Un importe retenido', 'pt-BR': 'Um valor retido' },
  'disputeCat.SETTLEMENT': {
    en: 'A payment marked as made',
    es: 'Un pago marcado como realizado',
    'pt-BR': 'Um pagamento marcado como feito',
  },
  'disputeCat.STATUS_CHANGE': {
    en: 'A change to my account',
    es: 'Un cambio en mi cuenta',
    'pt-BR': 'Uma mudança na minha conta',
  },
  'disputes.title': { en: 'Disputes', es: 'Disputas', 'pt-BR': 'Contestações' },
  /* §3.11 / E40 — authority support asks for, and only this affiliate can give.
     Translated in full: the reader is being asked to let somebody outside
     their company act in their name, and a half-English consent form is not
     consent. */
  'mandates.title': {
    en: 'Acting on your behalf',
    es: 'Actuar en su nombre',
    'pt-BR': 'Agir em seu nome',
  },
  'mandates.none': {
    en: 'Nobody has asked to act for you.',
    es: 'Nadie ha pedido actuar en su nombre.',
    'pt-BR': 'Ninguém pediu para agir em seu nome.',
  },
  'mandates.col.what': { en: 'What for', es: 'Para qué', 'pt-BR': 'Para quê' },
  'mandates.col.why': { en: 'Why they asked', es: 'Por qué lo pidieron', 'pt-BR': 'Por que pediram' },
  'mandates.col.who': { en: 'Who asked', es: 'Quién lo pidió', 'pt-BR': 'Quem pediu' },
  // AF-01 WP9 (owner, 2026-10-16) — who did it, when it was AffDice platform
  // staff: the team, never the staff account's name or address.
  'actor.affdiceTeam': { en: 'KD Affiliate team', es: 'Equipo de KD Affiliate', 'pt-BR': 'Equipe KD Affiliate' },
  'mandates.col.state': { en: 'State', es: 'Estado', 'pt-BR': 'Estado' },
  // Item 4 — the raw mandate state code, in words.
  'mandate.state.REQUESTED': { en: 'Requested', es: 'Solicitado', 'pt-BR': 'Solicitado' },
  'mandate.state.GRANTED': { en: 'Granted', es: 'Concedido', 'pt-BR': 'Concedido' },
  'mandate.state.USED': { en: 'Used', es: 'Usado', 'pt-BR': 'Usado' },
  'mandate.state.REVOKED': { en: 'Taken back', es: 'Retirado', 'pt-BR': 'Retirado' },
  'mandate.state.EXPIRED': { en: 'Expired', es: 'Vencido', 'pt-BR': 'Expirado' },
  'mandates.act.dispute': {
    en: 'Raising a dispute for you',
    es: 'Presentar una disputa por usted',
    'pt-BR': 'Abrir uma contestação por você',
  },
  'mandates.grant': { en: 'Allow, once', es: 'Permitir, una vez', 'pt-BR': 'Permitir, uma vez' },
  'mandates.revoke': { en: 'Take it back', es: 'Retirarlo', 'pt-BR': 'Retirar' },
  'mandates.explain': {
    en: 'Platform support can read your account and cannot change it. Raising a '
      + 'dispute is the one thing they can do for you, because a dispute is '
      + 'against your operator and your operator cannot approve it on your '
      + 'behalf. Allowing it covers one dispute and lasts seven days; you can '
      + 'take it back at any time until it is used, and you will be told what '
      + 'was done with it.',
    es: 'El soporte de la plataforma puede leer su cuenta y no puede modificarla. '
      + 'Presentar una disputa es lo único que pueden hacer por usted, porque una '
      + 'disputa es contra su operador y su operador no puede aprobarla en su '
      + 'nombre. Permitirlo cubre una sola disputa y dura siete días; puede '
      + 'retirarlo en cualquier momento hasta que se use, y le diremos qué se hizo con él.',
    'pt-BR': 'O suporte da plataforma pode ler sua conta e não pode alterá-la. '
      + 'Abrir uma contestação é a única coisa que podem fazer por você, porque '
      + 'uma contestação é contra o seu operador e o seu operador não pode '
      + 'aprová-la em seu nome. Permitir cobre uma contestação e dura sete dias; '
      + 'você pode retirar a qualquer momento até que seja usada, e diremos o que foi feito com ela.',
  },
  'disputes.sections': { en: 'Disputes sections', es: 'Secciones de Disputas', 'pt-BR': 'Seções de Contestações' },
  'disputes.section.raise': { en: 'Raise a dispute', es: 'Presentar una disputa', 'pt-BR': 'Abrir uma contestação' },
  'disputes.section.list': { en: 'Your disputes', es: 'Tus disputas', 'pt-BR': 'Suas contestações' },
  'disputes.section.detail': { en: 'Dispute detail', es: 'Detalle de la disputa', 'pt-BR': 'Detalhe da contestação' },
  'disputes.field.about': { en: 'What is this about?', es: '¿Sobre qué es?', 'pt-BR': 'Sobre o que é?' },

  /* ---------------- raising and reading a dispute ---------------- */
  // §33.5 — said before the button, because an affiliate who thinks disputing
  // one line freezes the whole statement will not dispute the line.
  'disputes.notDelaying': {
    en: 'Raising this does not delay the money you are not disputing. Only the amounts you name are held.',
    es: 'Presentar esto no retrasa el dinero que no estás disputando. Solo se retienen los importes que nombres.',
    'pt-BR': 'Abrir isto não atrasa o dinheiro que você não está contestando. Só os valores que você citar ficam retidos.',
  },
  'disputes.empty': {
    en: 'You have not raised anything.',
    es: 'No has presentado nada.',
    'pt-BR': 'Você não abriu nada.',
  },
  // Item 8 — "Raise it" going disabled with nothing on screen saying why.
  'disputes.raiseIt.hint': {
    en: 'Choose a brand and say what happened before you can raise it.',
    es: 'Elige una marca y describe qué pasó antes de poder presentarla.',
    'pt-BR': 'Escolha uma marca e diga o que aconteceu antes de poder abri-la.',
  },
  'disputes.notAnswered': { en: 'Not yet answered', es: 'Todavía sin respuesta', 'pt-BR': 'Ainda sem resposta' },
  'disputes.detail.panel': { en: 'Dispute detail', es: 'Detalle de la disputa', 'pt-BR': 'Detalhe da contestação' },
  'disputes.field.id': { en: 'Dispute id', es: 'Id de la disputa', 'pt-BR': 'Id da contestação' },
  'disputes.open': { en: 'Open', es: 'Abrir', 'pt-BR': 'Abrir' },
  'disputes.foundSummary': {
    en: 'Summary of what was found',
    es: 'Resumen de lo que se encontró',
    'pt-BR': 'Resumo do que foi apurado',
  },
  'disputes.proof': { en: 'Proof', es: 'Prueba', 'pt-BR': 'Comprovação' },
  'disputes.viewAttachment': { en: 'View attachment', es: 'Ver adjunto', 'pt-BR': 'Ver anexo' },
  'disputes.reply': {
    en: 'Your reply — what they asked for',
    es: 'Tu respuesta: lo que te pidieron',
    'pt-BR': 'Sua resposta — o que pediram',
  },
  'disputes.sending': { en: 'Sending…', es: 'Enviando…', 'pt-BR': 'Enviando…' },
  'disputes.sendReply': { en: 'Send reply', es: 'Enviar respuesta', 'pt-BR': 'Enviar resposta' },
  // Item 13 — a decision the affiliate does not accept, reopened once.
  'disputes.reopenPrompt': {
    en: 'Do not accept this? Reopen it, and say why',
    es: '¿No estás de acuerdo? Reábrela e indica por qué',
    'pt-BR': 'Não concorda? Reabra e diga por quê',
  },
  'disputes.reopening': { en: 'Reopening…', es: 'Reabriendo…', 'pt-BR': 'Reabrindo…' },
  'disputes.reopen': { en: 'Reopen', es: 'Reabrir', 'pt-BR': 'Reabrir' },
  'disputes.timeline': { en: 'Timeline', es: 'Cronología', 'pt-BR': 'Linha do tempo' },
  'disputes.by.you': { en: 'You', es: 'Tú', 'pt-BR': 'Você' },
  'disputes.by.operator': { en: 'Your operator', es: 'Tu operador', 'pt-BR': 'Seu operador' },
  // Every dispute names the brand it is about.
  'disputes.field.brand': {
    en: 'Which brand is this about?',
    es: '¿Sobre qué marca es?',
    'pt-BR': 'Sobre qual marca é?',
  },
  'disputes.chooseBrand': { en: 'Choose a brand', es: 'Elige una marca', 'pt-BR': 'Escolha uma marca' },
  'disputes.field.campaign': {
    en: 'Which campaign? (optional)',
    es: '¿Qué campaña? (opcional)',
    'pt-BR': 'Qual campanha? (opcional)',
  },
  'disputes.field.period': {
    en: 'Which statement? (optional)',
    es: '¿Qué extracto? (opcional)',
    'pt-BR': 'Qual extrato? (opcional)',
  },
  'disputes.notOne': {
    en: 'Not about one in particular',
    es: 'No es sobre uno en concreto',
    'pt-BR': 'Não é sobre um específico',
  },
  'disputes.noBrands': {
    en: 'You do not work with any brand here yet, so there is nobody to raise a dispute with.',
    es: 'Todavía no trabajas con ninguna marca aquí, así que no hay a quién presentar una disputa.',
    'pt-BR': 'Você ainda não trabalha com nenhuma marca aqui, então não há a quem abrir uma contestação.',
  },
  // §33.4 — files on a dispute, from either side.
  'disputes.field.attachments': {
    en: 'Screenshots or files (optional)',
    es: 'Capturas o archivos (opcional)',
    'pt-BR': 'Capturas de tela ou arquivos (opcional)',
  },
  'disputes.replyFiles': {
    en: 'Files for your reply (optional)',
    es: 'Archivos para tu respuesta (opcional)',
    'pt-BR': 'Arquivos para sua resposta (opcional)',
  },
  'disputes.attachedCount': { en: '{n} attached', es: '{n} adjunto(s)', 'pt-BR': '{n} anexo(s)' },
  'disputes.whatYouSaid': { en: 'What you told us', es: 'Lo que nos dijiste', 'pt-BR': 'O que você nos disse' },
  'disputes.files': { en: 'Files', es: 'Archivos', 'pt-BR': 'Arquivos' },
  'disputes.file.fromYou': { en: 'From you', es: 'Tuyo', 'pt-BR': 'Seu' },
  'disputes.file.fromOperator': {
    en: 'From your operator',
    es: 'De tu operador',
    'pt-BR': 'Do seu operador',
  },

  /* ---------------- history (§42.7) ---------------- */
  'history.empty': {
    en: 'Nothing has changed on your account yet.',
    es: 'Todavía no ha cambiado nada en tu cuenta.',
    'pt-BR': 'Ainda não mudou nada na sua conta.',
  },
  'history.caption': { en: 'Your history', es: 'Tu historial', 'pt-BR': 'Seu histórico' },

  /* ---------------- statements and payments (Ch. 30, §31) ---------------- */
  'statements.caption': { en: 'Statements', es: 'Estados de cuenta', 'pt-BR': 'Extratos' },
  'statements.periodRange': { en: '{from} to {to}', es: '{from} a {to}', 'pt-BR': '{from} a {to}' },
  'statements.details': { en: 'Details', es: 'Detalles', 'pt-BR': 'Detalhes' },
  // §39.13 — a conversion that was sampled away qualified and was not paid,
  // which is a different thing from one that failed to qualify.
  'statements.sampledPanel': {
    en: 'Conversions not paid for this period',
    es: 'Conversiones no pagadas en este periodo',
    'pt-BR': 'Conversões não pagas neste período',
  },

  'payments.title': { en: 'Invoices', es: 'Facturas', 'pt-BR': 'Faturas' },
  'payments.col.payment': { en: 'Payment', es: 'Pago', 'pt-BR': 'Pagamento' },
  'payments.invoices.empty': {
    en: 'No invoices yet. Every period gets a statement (see Statements); an invoice is raised only when the payable balance clears the minimum payout — below it the balance carries forward.',
    es: 'Todavía no hay facturas. Cada periodo recibe un estado de cuenta (ver Estados de cuenta); la factura se emite solo cuando el saldo pagadero supera el pago mínimo — por debajo, el saldo se arrastra.',
    'pt-BR': 'Ainda não há faturas. Todo período recebe um extrato (ver Extratos); a fatura é emitida apenas quando o saldo pagável atinge o pagamento mínimo — abaixo dele, o saldo é transportado.',
  },
  'payments.detail': { en: 'Detail', es: 'Detalle', 'pt-BR': 'Detalhe' },
  'invoice.modal': { en: 'Invoice {number}', es: 'Factura {number}', 'pt-BR': 'Fatura {number}' },
  'invoice.modal.plain': { en: 'Invoice', es: 'Factura', 'pt-BR': 'Fatura' },
  'invoice.issuedDue': {
    en: 'Issued {issued}; due {due}.',
    es: 'Emitida el {issued}; vence el {due}.',
    'pt-BR': 'Emitida em {issued}; vence em {due}.',
  },
  // Item 4 — the invoice's own status code, in words. Status words only, not
  // a sentence: the same three words sit in a table column and a Chip.
  'invoice.status.ISSUED': { en: 'Issued', es: 'Emitida', 'pt-BR': 'Emitida' },
  'invoice.status.PARTIALLY_SETTLED': {
    en: 'Partially settled',
    es: 'Liquidada parcialmente',
    'pt-BR': 'Parcialmente quitada',
  },
  'invoice.status.SETTLED': { en: 'Settled', es: 'Liquidada', 'pt-BR': 'Quitada' },
  // The rest of 0040's invoice vocabulary. Without these a paid invoice read
  // "MARKED_PAID" in capitals, in every language.
  'invoice.status.GENERATED': { en: 'Prepared', es: 'Preparada', 'pt-BR': 'Preparada' },
  'invoice.status.PENDING_REVIEW': { en: 'Under review', es: 'En revisión', 'pt-BR': 'Em revisão' },
  'invoice.status.APPROVED': { en: 'Approved for payment', es: 'Aprobada para pago', 'pt-BR': 'Aprovada para pagamento' },
  'invoice.status.MARKED_PAID': {
    en: 'Marked paid by the operator',
    es: 'Marcada como pagada por el operador',
    'pt-BR': 'Marcada como paga pelo operador',
  },
  'invoice.status.DISPUTED': { en: 'Disputed', es: 'En disputa', 'pt-BR': 'Em disputa' },
  'invoice.status.CANCELLED': { en: 'Cancelled', es: 'Cancelada', 'pt-BR': 'Cancelada' },

  /* ---------------- Players (§17.8a) ---------------- */
  'players.title': { en: 'Players', es: 'Jugadores', 'pt-BR': 'Jogadores' },
  'players.byCohort': { en: 'By cohort', es: 'Por cohorte', 'pt-BR': 'Por coorte' },
  'players.sections': { en: 'Players sections', es: 'Secciones de Jugadores', 'pt-BR': 'Seções de Jogadores' },
  'players.section.qualification': { en: 'Qualification', es: 'Calificación', 'pt-BR': 'Qualificação' },
  'players.section.activity': { en: 'By cohort', es: 'Por cohorte', 'pt-BR': 'Por coorte' },
  'players.section.attributed': { en: 'Attributed players', es: 'Jugadores atribuidos', 'pt-BR': 'Jogadores atribuídos' },
  'players.months.one': { en: '{n} month', es: '{n} mes', 'pt-BR': '{n} mês' },
  'players.months.other': { en: '{n} months', es: '{n} meses', 'pt-BR': '{n} meses' },
  'players.cohorts.empty': {
    en: 'No cohorts in this period.',
    es: 'No hay cohortes en este periodo.',
    'pt-BR': 'Não há coortes neste período.',
  },
  // §17.8b — the brand's minimum cohort size, on the affiliate's tables.
  'cohort.pooled': {
    en: 'Other (small cohorts)',
    es: 'Otras (cohortes pequeñas)',
    'pt-BR': 'Outras (coortes pequenas)',
  },
  'cohort.withheld': {
    en: 'Insufficient cohort size',
    es: 'Tamaño de cohorte insuficiente',
    'pt-BR': 'Tamanho de coorte insuficiente',
  },
  // R2 — while a brand protects small cohorts, the cohort reports have one shape.
  'cohort.breakdownsOff': {
    en: 'Breakdowns are off. A brand you promote protects small groups of players: a cohort smaller than the minimum size of {n} shows no figures. So these cohort reports cannot be split or filtered by campaign, sub-ID, country, product or brand. They show whole months by registration date, for all your brands together, and your dates are widened to whole months.',
    es: 'Los desgloses están desactivados. Una marca que usted promociona protege los grupos pequeños de jugadores: una cohorte por debajo del tamaño mínimo de {n} no muestra cifras. Por eso estos informes de cohortes no se pueden dividir ni filtrar por campaña, sub-ID, país, producto o marca. Muestran meses completos por fecha de registro, para todas sus marcas juntas, y sus fechas se amplían a meses completos.',
    'pt-BR': 'Os detalhamentos estão desativados. Uma marca que você promove protege grupos pequenos de jogadores: uma coorte abaixo do tamanho mínimo de {n} não mostra valores. Por isso estes relatórios de coortes não podem ser divididos nem filtrados por campanha, sub-ID, país, produto ou marca. Eles mostram meses inteiros pela data de cadastro, para todas as suas marcas juntas, e as suas datas são ampliadas para meses inteiros.',
  },
  // Owner 2026-10-08 — while breakdowns are off, the open month is not shown.
  'cohort.currentMonthHidden': {
    en: 'The current month appears once it has closed.',
    es: 'El mes en curso aparece cuando haya cerrado.',
    'pt-BR': 'O mês atual aparece depois de encerrado.',
  },

  /* ---------------- §17.8b/§35.3 — the by-cohort report ---------------- */
  'players.section.cohortAnalysis': {
    en: 'Cohort analysis',
    es: 'Análisis de cohortes',
    'pt-BR': 'Análise de coortes',
  },
  'cohort.totals': {
    en: 'By registration month',
    es: 'Por mes de registro',
    'pt-BR': 'Por mês de registro',
  },
  'cohort.byAge': {
    en: 'By age of cohort',
    es: 'Por antigüedad de la cohorte',
    'pt-BR': 'Por idade da coorte',
  },
  'cohort.byAgeWhy': {
    en: 'Every cohort measured at the same ages, so a column compares like with like. A month that has not reached a checkpoint yet is left blank rather than shown as zero.',
    es: 'Cada cohorte se mide a las mismas edades, de modo que una columna compara lo comparable. Un mes que aún no ha alcanzado un punto de control se deja en blanco en lugar de mostrarse como cero.',
    'pt-BR': 'Cada coorte é medida nas mesmas idades, de modo que uma coluna compara o comparável. Um mês que ainda não atingiu um ponto de verificação fica em branco em vez de aparecer como zero.',
  },
  'cohort.notAssessable': {
    en: 'Not assessable yet — this cohort has not lived through this checkpoint.',
    es: 'Todavía no evaluable: esta cohorte no ha alcanzado este punto de control.',
    'pt-BR': 'Ainda não avaliável — esta coorte não atingiu este ponto de verificação.',
  },
  'cohort.ftdRate': { en: 'FTD rate', es: 'Tasa de FTD', 'pt-BR': 'Taxa de FTD' },
  'cohort.retention': { en: 'Retention', es: 'Retención', 'pt-BR': 'Retenção' },
  'cohort.retentionWhy': {
    en: 'Players who came back after the month they registered in.',
    es: 'Jugadores que volvieron después del mes en que se registraron.',
    'pt-BR': 'Jogadores que voltaram depois do mês em que se registraram.',
  },
  'cohort.earned': { en: 'You earned', es: 'Has ganado', 'pt-BR': 'Você ganhou' },
  'cohort.outside': {
    en: 'Earned outside the cohorts',
    es: 'Ganado fuera de las cohortes',
    'pt-BR': 'Ganho fora das coortes',
  },
  'cohort.show': { en: 'Show', es: 'Mostrar', 'pt-BR': 'Mostrar' },
  'cohort.stillActive': { en: 'Still active', es: 'Siguen activos', 'pt-BR': 'Ainda ativos' },
  'cohort.revenuePerPlayer': {
    en: 'Revenue / player',
    es: 'Ingresos / jugador',
    'pt-BR': 'Receita / jogador',
  },
  'cohort.depositsPerPlayer': {
    en: 'Deposits / player',
    es: 'Depósitos / jugador',
    'pt-BR': 'Depósitos / jogador',
  },
  'cohort.monthShort': { en: 'M{n}', es: 'M{n}', 'pt-BR': 'M{n}' },
  // §17.8a — deposits without withdrawals is half a number.
  'col.withdrawals': { en: 'Withdrawals', es: 'Retiros', 'pt-BR': 'Saques' },
  'col.grossRevenue': { en: 'Gross revenue', es: 'Ingresos brutos', 'pt-BR': 'Receita bruta' },
  'players.attributed': { en: 'Attributed players', es: 'Jugadores atribuidos', 'pt-BR': 'Jogadores atribuídos' },
  'players.count.one': { en: '{n} player', es: '{n} jugador', 'pt-BR': '{n} jogador' },
  'players.count.other': { en: '{n} players', es: '{n} jugadores', 'pt-BR': '{n} jogadores' },
  'players.attributed.empty': {
    en: 'No attributed players in this period.',
    es: 'No hay jugadores atribuidos en este periodo.',
    'pt-BR': 'Não há jogadores atribuídos neste período.',
  },
  'qualification.panel': { en: 'Qualification', es: 'Calificación', 'pt-BR': 'Qualificação' },
  // Item 4 — the raw QUALIFIED / NOT_QUALIFIED / PENDING code, in words.
  'qualification.status.QUALIFIED': { en: 'Qualified', es: 'Calificado', 'pt-BR': 'Qualificado' },
  'qualification.status.NOT_QUALIFIED': {
    en: 'Not qualified',
    es: 'No calificado',
    'pt-BR': 'Não qualificado',
  },
  'qualification.status.PENDING': { en: 'Pending', es: 'Pendiente', 'pt-BR': 'Pendente' },
  'qualification.empty': {
    en: 'No qualification rows in this period.',
    es: 'No hay filas de calificación en este periodo.',
    'pt-BR': 'Não há linhas de qualificação neste período.',
  },

  /* ---------------- getting paid (§31.1, §31.1b) ---------------- */
  'payout.method': { en: 'How you want to be paid', es: 'Cómo quieres cobrar', 'pt-BR': 'Como você quer receber' },
  'payout.label': {
    en: 'A note to yourself — optional',
    es: 'Una nota para ti — opcional',
    'pt-BR': 'Uma nota para você — opcional',
  },
  'payout.label.hint': {
    en: 'Something like "Revolut, business". Not an account number.',
    es: 'Algo como «Revolut, empresa». No un número de cuenta.',
    'pt-BR': 'Algo como “Revolut, empresa”. Não um número de conta.',
  },
  'payout.label.placeholder': {
    en: 'e.g. Revolut, business',
    es: 'p. ej. Revolut, empresa',
    'pt-BR': 'ex.: Revolut, empresa',
  },
  // §31.1b — said before the box, not after a rejection. An affiliate who has
  // already typed their IBAN has put it in a request log and a browser
  // autofill store, whatever the server then does with it.
  'payout.noCredentials': {
    en:
      'Do not enter bank details here. The platform holds no payment credentials ' +
      'at all — it records only how you prefer to be paid, so your operator knows ' +
      'which arrangement to use. Send the actual details to them directly.',
    es:
      'No introduzcas datos bancarios aquí. La plataforma no guarda ninguna ' +
      'credencial de pago: solo registra cómo prefieres cobrar, para que tu ' +
      'operador sepa qué arreglo usar. Envíales los datos reales directamente a ellos.',
    'pt-BR':
      'Não digite dados bancários aqui. A plataforma não guarda nenhuma credencial ' +
      'de pagamento — ela registra apenas como você prefere receber, para que seu ' +
      'operador saiba qual arranjo usar. Envie os dados reais diretamente a eles.',
  },
  'payout.link': {
    en: 'A link to where your details are kept — optional',
    es: 'Un enlace a donde guardas tus datos — opcional',
    'pt-BR': 'Um link para onde seus dados ficam — opcional',
  },
  'payout.link.hint': {
    en: 'Your billing portal, a shared folder. The page, never the details.',
    es: 'Tu portal de facturación, una carpeta compartida. La página, nunca los datos.',
    'pt-BR': 'Seu portal de faturamento, uma pasta compartilhada. A página, nunca os dados.',
  },
  'payout.linkLabel': { en: 'What to call it', es: 'Cómo llamarlo', 'pt-BR': 'Como chamá-lo' },
  'payout.linkLabel.hint': {
    en: 'What your operator will see, e.g. “Our billing portal”.',
    es: 'Lo que verá tu operador, p. ej. «Nuestro portal de facturación».',
    'pt-BR': 'O que seu operador vai ver, ex.: “Nosso portal de faturamento”.',
  },
  'payout.linkLabel.placeholder': {
    en: 'Our billing portal',
    es: 'Nuestro portal de facturación',
    'pt-BR': 'Nosso portal de faturamento',
  },
  /* §30.4 — the affiliate's own threshold, on top of the operator's. */
  'payout.minimum': { en: 'Do not pay me under', es: 'No me pagues menos de', 'pt-BR': 'Não me pague menos que' },
  'payout.minimum.hint': {
    en: 'Optional. Hold my money until it reaches this, so I am paid less often and lose less to fees. It can only raise your operator’s own threshold, never lower it.',
    es: 'Opcional. Retén mi dinero hasta que llegue a esta cifra, para cobrar menos veces y perder menos en comisiones. Solo puede subir el umbral de tu operador, nunca bajarlo.',
    'pt-BR': 'Opcional. Segure meu dinheiro até chegar a este valor, para eu receber menos vezes e perder menos em taxas. Só pode aumentar o limite do seu operador, nunca reduzi-lo.',
  },
  'payout.minimum.placeholder': { en: '500', es: '500', 'pt-BR': '500' },
  'payout.minimum.currency': { en: 'In', es: 'En', 'pt-BR': 'Em' },
  'payout.minimum.noCurrency': { en: 'No minimum', es: 'Sin mínimo', 'pt-BR': 'Sem mínimo' },
  'payout.save': { en: 'Save preference', es: 'Guardar preferencia', 'pt-BR': 'Salvar preferência' },

  /* ---------------- brands, and applying to another (§6.15) ---------------- */
  'brands.title': { en: 'Brands', es: 'Marcas', 'pt-BR': 'Marcas' },
  'brands.youWorkWith': {
    en: 'Brands you work with',
    es: 'Marcas con las que trabajas',
    'pt-BR': 'Marcas com que você trabalha',
  },
  'brands.empty': {
    en:
      'No brands are linked to your account yet. If you joined through an ' +
      'invitation, ask your operator contact to confirm your approval — brands ' +
      'appear here once the relationship is active.',
    es:
      'Todavía no hay marcas vinculadas a tu cuenta. Si entraste por invitación, ' +
      'pide a tu contacto del operador que confirme tu aprobación: las marcas ' +
      'aparecen aquí en cuanto la relación está activa.',
    'pt-BR':
      'Ainda não há marcas ligadas à sua conta. Se você entrou por convite, peça ao ' +
      'seu contato no operador para confirmar sua aprovação — as marcas aparecem ' +
      'aqui assim que a relação estiver ativa.',
  },
  'brands.underReview': {
    en:
      'You have joined this operator, but they have not approved you yet — ' +
      'tracking links stay unavailable until then. The brands above are the ones ' +
      'you were invited to promote.',
    es:
      'Te has unido a este operador, pero todavía no te ha aprobado: los enlaces de ' +
      'seguimiento seguirán sin estar disponibles hasta entonces. Las marcas de ' +
      'arriba son las que te invitaron a promocionar.',
    'pt-BR':
      'Você entrou neste operador, mas ele ainda não aprovou você — os links de ' +
      'rastreamento seguem indisponíveis até lá. As marcas acima são as que você foi ' +
      'convidado a promover.',
  },
  'brands.leaving': {
    en:
      'To stop working with this operator altogether, contact them — your account ' +
      'here cannot be closed brand-by-brand once you have joined.',
    es:
      'Para dejar de trabajar con este operador por completo, contáctalos: tu cuenta ' +
      'aquí no se puede cerrar marca por marca una vez que te has unido.',
    'pt-BR':
      'Para parar de trabalhar com este operador por completo, entre em contato com ' +
      'ele — sua conta aqui não pode ser encerrada marca a marca depois que você entrou.',
  },
  'brands.openToApplications': {
    en: 'Open to applications',
    es: 'Abiertas a solicitudes',
    'pt-BR': 'Abertas a inscrições',
  },
  'brands.openEmpty': {
    en:
      'No brands are listed at the moment. Operators choose whether to appear ' +
      'here, so a programme you know of may still be open — ask them for their link.',
    es:
      'Ahora mismo no hay marcas listadas. Los operadores eligen si aparecer aquí, ' +
      'así que un programa que conozcas puede seguir abierto: pídeles su enlace.',
    'pt-BR':
      'No momento não há marcas listadas. Os operadores escolhem se aparecem aqui, ' +
      'então um programa que você conhece pode continuar aberto — peça o link a eles.',
  },
  'brands.alreadyApplied': { en: 'Already applied', es: 'Ya solicitado', 'pt-BR': 'Já inscrito' },
  'brands.sent': { en: 'Sent', es: 'Enviada', 'pt-BR': 'Enviada' },
  'brands.open': { en: 'Open', es: 'Abierta', 'pt-BR': 'Aberta' },
  'brands.apply': { en: 'Apply', es: 'Solicitar', 'pt-BR': 'Inscrever-se' },
  'brands.applyModal': { en: 'Apply to {brands}', es: 'Solicitar a {brands}', 'pt-BR': 'Inscrever-se em {brands}' },
  'brands.applyingAs': {
    en: 'You are applying as {name} ({email}), registered in {country}. Nothing is copied from your other programmes.',
    es: 'Estás solicitando como {name} ({email}), registrado en {country}. No se copia nada de tus otros programas.',
    'pt-BR': 'Você está se inscrevendo como {name} ({email}), registrado em {country}. Nada é copiado dos seus outros programas.',
  },
  'brands.scope.contact': {
    en: 'They will see the contact details above.',
    es: 'Verán los datos de contacto de arriba.',
    'pt-BR': 'Eles verão os dados de contato acima.',
  },
  'brands.scope.traffic': {
    en: 'They will see your traffic, registrations and commission with them only.',
    es: 'Verán tu tráfico, registros y comisión con ellos únicamente.',
    'pt-BR': 'Eles verão seu tráfego, registros e comissão apenas com eles.',
  },
  'brands.sending': { en: 'Sending…', es: 'Enviando…', 'pt-BR': 'Enviando…' },
  'brands.sendApplication': { en: 'Send application', es: 'Enviar solicitud', 'pt-BR': 'Enviar inscrição' },
  'brands.notOnAny': {
    en: 'You are not on any brand right now.',
    es: 'Ahora mismo no estás en ninguna marca.',
    'pt-BR': 'No momento você não está em nenhuma marca.',
  },

  /* ---------------- Account ---------------- */
  'account.title': { en: 'Account', es: 'Cuenta', 'pt-BR': 'Conta' },
  'account.sections': { en: 'Account sections', es: 'Secciones de Cuenta', 'pt-BR': 'Seções da Conta' },
  'account.section.profile': { en: 'Profile', es: 'Perfil', 'pt-BR': 'Perfil' },
  'account.section.security': { en: 'Security', es: 'Seguridad', 'pt-BR': 'Segurança' },
  'account.section.mail': { en: 'Emails we sent', es: 'Correos que enviamos', 'pt-BR': 'E-mails que enviamos' },
  'account.section.api': { en: 'API access', es: 'Acceso por API', 'pt-BR': 'Acesso por API' },
  'api.off': {
    en: 'API access is off for your account. Ask your operator to turn it on — they decide per affiliate.',
    es: 'El acceso por API está desactivado en tu cuenta. Pídeselo a tu operador: lo deciden por afiliado.',
    'pt-BR': 'O acesso por API está desligado na sua conta. Peça ao seu operador — eles decidem por afiliado.',
  },
  'api.label': { en: 'What it is for', es: 'Para qué es', 'pt-BR': 'Para que serve' },
  'api.label.hint': {
    en: 'Your own name for it, so you can revoke the right one later.',
    es: 'Tu propio nombre, para poder revocar el correcto más adelante.',
    'pt-BR': 'Seu próprio nome, para revogar o certo depois.',
  },
  'api.label.placeholder': { en: 'My reporting box', es: 'Mi servidor de informes', 'pt-BR': 'Meu servidor de relatórios' },
  'api.create': { en: 'Create credentials', es: 'Crear credenciales', 'pt-BR': 'Criar credenciais' },
  'api.clientId': { en: 'Client ID', es: 'ID de cliente', 'pt-BR': 'ID do cliente' },
  'api.secret': { en: 'Secret', es: 'Secreto', 'pt-BR': 'Segredo' },
  'api.copyNow': {
    en: 'Copy the secret now — we keep only a hash of it and cannot show it again.',
    es: 'Copia el secreto ahora: solo guardamos un hash y no podemos volver a mostrarlo.',
    'pt-BR': 'Copie o segredo agora — guardamos apenas um hash e não podemos mostrá-lo de novo.',
  },
  'api.lastUsed': { en: 'Last used', es: 'Último uso', 'pt-BR': 'Último uso' },
  'api.revoke': { en: 'Revoke', es: 'Revocar', 'pt-BR': 'Revogar' },
  'api.empty': { en: 'None yet.', es: 'Ninguna todavía.', 'pt-BR': 'Nenhuma ainda.' },
  'api.guide.title': {
    en: 'How to use these credentials',
    es: 'Cómo usar estas credenciales',
    'pt-BR': 'Como usar estas credenciais',
  },
  'api.guide.intro': {
    en: 'Use them from your server, reporting job, Postman, or BI connector. They are not entered anywhere else in this portal.',
    es: 'Úsalas desde tu servidor, tarea de informes, Postman o conector de BI. No se introducen en ninguna otra parte de este portal.',
    'pt-BR': 'Use-as no seu servidor, rotina de relatórios, Postman ou conector de BI. Elas não são inseridas em nenhum outro lugar deste portal.',
  },
  'api.guide.base': { en: 'API base URL', es: 'URL base de la API', 'pt-BR': 'URL base da API' },
  'api.guide.step1': {
    en: '1. Exchange the client ID and secret for a token',
    es: '1. Canjea el ID de cliente y el secreto por un token',
    'pt-BR': '1. Troque o ID do cliente e o segredo por um token',
  },
  'api.guide.step1.body': {
    en: 'Send JSON to the token endpoint. The returned Bearer token lasts one hour; request a new one when it expires.',
    es: 'Envía JSON al endpoint de token. El token Bearer devuelto dura una hora; solicita uno nuevo cuando caduque.',
    'pt-BR': 'Envie JSON ao endpoint de token. O token Bearer retornado dura uma hora; solicite outro quando expirar.',
  },
  'api.guide.step2': {
    en: '2. Send the token with every API request',
    es: '2. Envía el token con cada solicitud a la API',
    'pt-BR': '2. Envie o token em cada solicitação à API',
  },
  'api.guide.step2.body': {
    en: 'Put the token in the Authorization header as “Bearer <token>”. API tokens are read-only: GET is allowed and every write is refused.',
    es: 'Pon el token en la cabecera Authorization como “Bearer <token>”. Los tokens de API son de solo lectura: se permite GET y se rechaza toda escritura.',
    'pt-BR': 'Coloque o token no cabeçalho Authorization como “Bearer <token>”. Tokens de API são somente leitura: GET é permitido e toda gravação é recusada.',
  },
  'api.guide.endpoints': {
    en: '3. Available endpoints',
    es: '3. Endpoints disponibles',
    'pt-BR': '3. Endpoints disponíveis',
  },
  'api.guide.endpoints.body': {
    en: 'All results are restricted to your own affiliate account and the operator that enabled these credentials. Dates use YYYY-MM-DD.',
    es: 'Todos los resultados se limitan a tu propia cuenta de afiliado y al operador que habilitó estas credenciales. Las fechas usan AAAA-MM-DD.',
    'pt-BR': 'Todos os resultados ficam restritos à sua conta de afiliado e ao operador que habilitou estas credenciais. As datas usam AAAA-MM-DD.',
  },
  'api.guide.account': { en: 'Account and programme', es: 'Cuenta y programa', 'pt-BR': 'Conta e programa' },
  'api.guide.reporting': { en: 'Reporting', es: 'Informes', 'pt-BR': 'Relatórios' },
  'api.guide.money': { en: 'Money and documents', es: 'Dinero y documentos', 'pt-BR': 'Valores e documentos' },
  'api.guide.endpoint': { en: 'Endpoint', es: 'Endpoint', 'pt-BR': 'Endpoint' },
  'api.guide.purpose': { en: 'Returns', es: 'Devuelve', 'pt-BR': 'Retorna' },
  'api.guide.me': { en: 'Your profile and status', es: 'Tu perfil y estado', 'pt-BR': 'Seu perfil e status' },
  'api.guide.terms': { en: 'Your effective commission terms', es: 'Tus condiciones de comisión vigentes', 'pt-BR': 'Seus termos de comissão vigentes' },
  'api.guide.campaigns': { en: 'Campaigns available to you', es: 'Campañas disponibles para ti', 'pt-BR': 'Campanhas disponíveis para você' },
  'api.guide.links': { en: 'Your tracking links', es: 'Tus enlaces de seguimiento', 'pt-BR': 'Seus links de rastreamento' },
  'api.guide.performance': { en: 'Performance grouped by the requested dimension', es: 'Rendimiento agrupado por la dimensión solicitada', 'pt-BR': 'Desempenho agrupado pela dimensão solicitada' },
  'api.guide.funnel': { en: 'Clicks-to-registration and deposit funnel', es: 'Embudo de clics, registros y depósitos', 'pt-BR': 'Funil de cliques, cadastros e depósitos' },
  'api.guide.commission': { en: 'Commission earned in the date range', es: 'Comisión obtenida en el período', 'pt-BR': 'Comissão obtida no período' },
  'api.guide.players': { en: 'Pseudonymous player performance', es: 'Rendimiento seudónimo de jugadores', 'pt-BR': 'Desempenho pseudônimo de jogadores' },
  'api.guide.cohorts': { en: 'Registration-month cohorts', es: 'Cohortes por mes de registro', 'pt-BR': 'Coortes por mês de cadastro' },
  'api.guide.qualification': { en: 'Player qualification status and failed criterion', es: 'Estado de calificación y criterio fallido', 'pt-BR': 'Status de qualificação e critério não atendido' },
  'api.guide.clicks': { en: 'Click log and rejection reasons', es: 'Registro de clics y motivos de rechazo', 'pt-BR': 'Registro de cliques e motivos de rejeição' },
  'api.guide.discrepancy': { en: 'Your counts compared with platform counts', es: 'Tus recuentos comparados con los de la plataforma', 'pt-BR': 'Suas contagens comparadas às da plataforma' },
  'api.guide.statements': { en: 'Statements by accounting period', es: 'Extractos por período contable', 'pt-BR': 'Extratos por período contábil' },
  'api.guide.statement': { en: 'One itemised statement; replace {period}', es: 'Un extracto detallado; reemplaza {period}', 'pt-BR': 'Um extrato detalhado; substitua {period}' },
  'api.guide.invoices': { en: 'Your invoices', es: 'Tus facturas', 'pt-BR': 'Suas faturas' },
  'api.guide.payments': { en: 'Payment and settlement history', es: 'Historial de pagos y liquidaciones', 'pt-BR': 'Histórico de pagamentos e liquidações' },
  'api.guide.trace': { en: 'Calculation trace; replace {id}', es: 'Rastro del cálculo; reemplaza {id}', 'pt-BR': 'Rastro do cálculo; substitua {id}' },
  'api.guide.parameters': {
    en: 'Common report parameters: from and to (YYYY-MM-DD), optional brand_id, and dimension on performance (campaign, creative, landing_page, sub_id_1–10, geo, device, or product).',
    es: 'Parámetros comunes de informes: from y to (AAAA-MM-DD), brand_id opcional y dimension en performance (campaign, creative, landing_page, sub_id_1–10, geo, device o product).',
    'pt-BR': 'Parâmetros comuns de relatórios: from e to (AAAA-MM-DD), brand_id opcional e dimension em performance (campaign, creative, landing_page, sub_id_1–10, geo, device ou product).',
  },
  'api.guide.security': {
    en: 'Keep the secret on a server or in a secret manager—never browser JavaScript, a public repository, screenshots, or support messages. The secret is shown only once. Revoke a credential here immediately if it may have leaked.',
    es: 'Guarda el secreto en un servidor o gestor de secretos; nunca en JavaScript del navegador, un repositorio público, capturas ni mensajes de soporte. Solo se muestra una vez. Revoca aquí la credencial inmediatamente si pudo filtrarse.',
    'pt-BR': 'Guarde o segredo em um servidor ou gerenciador de segredos; nunca em JavaScript do navegador, repositório público, capturas ou mensagens de suporte. Ele aparece apenas uma vez. Revogue a credencial aqui imediatamente se houver suspeita de vazamento.',
  },
  'mail.subject': { en: 'Subject', es: 'Asunto', 'pt-BR': 'Assunto' },
  'mail.empty': { en: 'We have not emailed you yet.', es: 'Aún no te hemos escrito.', 'pt-BR': 'Ainda não enviamos e-mails a você.' },
  // WP11 — the subject of a notice the operator has not reworded, in the
  // reader's language rather than as the template's code. Neutral on purpose.
  'mail.template.COMMISSION_UNDER_REVIEW': {
    en: 'Activity on a referred player is under review',
    es: 'La actividad de un jugador referido está en revisión',
    'pt-BR': 'A atividade de um jogador indicado está em análise',
  },
  'mail.status.QUEUED': { en: 'Queued', es: 'En cola', 'pt-BR': 'Na fila' },
  'mail.status.SENT': { en: 'Sent', es: 'Enviado', 'pt-BR': 'Enviado' },
  'mail.status.DELIVERED': { en: 'Delivered', es: 'Entregado', 'pt-BR': 'Entregue' },
  'mail.status.BOUNCED': { en: 'Bounced', es: 'Rebotado', 'pt-BR': 'Devolvido' },
  'mail.status.FAILED': { en: 'Failed', es: 'Falló', 'pt-BR': 'Falhou' },
  'account.section.brands': { en: 'Brands', es: 'Marcas', 'pt-BR': 'Marcas' },
  'account.section.language': { en: 'Language', es: 'Idioma', 'pt-BR': 'Idioma' },
  'account.section.currency': { en: 'Report currency', es: 'Moneda de los informes', 'pt-BR': 'Moeda dos relatórios' },
  'account.section.paid': { en: 'Getting paid', es: 'Cómo cobras', 'pt-BR': 'Como você recebe' },
  'account.changePassword.title': {
    en: 'Change your password',
    es: 'Cambia tu contraseña',
    'pt-BR': 'Altere sua senha',
  },
  'account.changePassword.intro': {
    en: 'Enter your current password and choose a new one.',
    es: 'Introduce tu contraseña actual y elige una nueva.',
    'pt-BR': 'Digite sua senha atual e escolha uma nova.',
  },
  'account.enrolMfa.title': {
    en: 'Set up an authenticator app',
    es: 'Configura una app de autenticación',
    'pt-BR': 'Configure um app autenticador',
  },
  'account.enrolMfa.intro': {
    en: 'Enter your password to begin. You will scan a QR code, then confirm one code from the app.',
    es: 'Introduce tu contraseña para empezar. Escanearás un código QR y luego confirmarás un código de la app.',
    'pt-BR': 'Digite sua senha para começar. Você vai escanear um QR code e depois confirmar um código do app.',
  },
  // §3.7 for an affiliate: a second factor is mandatory before a contact-address
  // change rather than outright, and there is deliberately no switch to remove it.
  'account.mfa.set': {
    en:
      'Your authenticator app is set up. To replace it — a new phone, a lost ' +
      'device — ask your account manager to reset it.',
    es:
      'Tu app de autenticación está configurada. Para reemplazarla — un teléfono ' +
      'nuevo, un dispositivo perdido — pide a tu gestor de cuenta que la restablezca.',
    'pt-BR':
      'Seu app autenticador está configurado. Para trocá-lo — um celular novo, um ' +
      'aparelho perdido — peça ao seu gerente de conta para redefini-lo.',
  },
  'account.mfa.why': {
    en:
      'An authenticator app means your password alone is not enough to sign in as ' +
      'you. Your account holds your statements and the links your traffic is paid on.',
    es:
      'Una app de autenticación significa que tu contraseña por sí sola no basta para ' +
      'iniciar sesión como tú. Tu cuenta guarda tus estados de cuenta y los enlaces ' +
      'por los que se paga tu tráfico.',
    'pt-BR':
      'Um app autenticador significa que só a sua senha não basta para entrar como ' +
      'você. Sua conta guarda seus extratos e os links pelos quais seu tráfego é pago.',
  },
  'account.mfa.setUp': {
    en: 'Set up authenticator app',
    es: 'Configurar app de autenticación',
    'pt-BR': 'Configurar app autenticador',
  },
  /* Said only where the operator's TOTP flag is on. It does not gate an
     affiliate's sign-in — §3.7 keeps that voluntary — so this says expected,
     not required, which is the true state of it. */
  'account.mfa.expected': {
    en:
      'Your operator expects everyone on this programme to use one. Setting it up now ' +
      'is quicker than being asked for it at the moment you need to change something.',
    es:
      'Tu operador espera que todos en este programa usen una. Configurarla ahora es más ' +
      'rápido que tener que hacerlo justo cuando necesites cambiar algo.',
    'pt-BR':
      'Sua operadora espera que todos neste programa usem um. Configurar agora é mais ' +
      'rápido do que ter de fazê-lo no momento em que você precisar alterar algo.',
  },
  /* §3.7a — the sign-ins on this account. */
  /* Support — a question that is not a claim about money. */
  'support.title': { en: 'Support', es: 'Soporte', 'pt-BR': 'Suporte' },
  'support.panel.open': { en: 'Open tickets', es: 'Tickets abiertos', 'pt-BR': 'Tickets abertos' },
  'support.panel.resolved': { en: 'Resolved tickets', es: 'Tickets resueltos', 'pt-BR': 'Tickets resolvidos' },
  'support.new': { en: 'Ask a question', es: 'Hacer una pregunta', 'pt-BR': 'Fazer uma pergunta' },
  'support.open': { en: 'Open', es: 'Abrir', 'pt-BR': 'Abrir' },
  'support.showResolved': { en: 'Show resolved', es: 'Ver resueltos', 'pt-BR': 'Ver resolvidos' },
  'support.showOpen': { en: 'Show open', es: 'Ver abiertos', 'pt-BR': 'Ver abertos' },
  'support.col.subject': { en: 'Subject', es: 'Asunto', 'pt-BR': 'Assunto' },
  'support.col.priority': { en: 'Urgency', es: 'Urgencia', 'pt-BR': 'Urgência' },
  'support.body': { en: 'What is happening', es: 'Qué está pasando', 'pt-BR': 'O que está acontecendo' },
  'support.thread': { en: 'Conversation', es: 'Conversación', 'pt-BR': 'Conversa' },
  'support.reply': { en: 'Reply', es: 'Responder', 'pt-BR': 'Responder' },
  'support.send': { en: 'Send', es: 'Enviar', 'pt-BR': 'Enviar' },
  'support.subject.hint': {
    en: 'One line your operator can find this by later.',
    es: 'Una línea por la que tu operador pueda encontrarlo después.',
    'pt-BR': 'Uma linha pela qual seu operador possa encontrar isto depois.',
  },
  'support.subject.placeholder': {
    en: 'My tracking link goes nowhere',
    es: 'Mi enlace de seguimiento no lleva a ninguna parte',
    'pt-BR': 'Meu link de rastreamento não leva a lugar nenhum',
  },
  'support.body.hint': {
    en: 'What you did, what happened, and what you expected instead.',
    es: 'Qué hiciste, qué pasó y qué esperabas en su lugar.',
    'pt-BR': 'O que você fez, o que aconteceu e o que esperava no lugar.',
  },
  'support.priority.hint': {
    en: 'How urgent this is for you. It orders your operator’s queue; it is not a promise about when they answer.',
    es: 'Qué urgencia tiene para ti. Ordena la cola de tu operador; no es una promesa sobre cuándo responderá.',
    'pt-BR': 'Qual a urgência para você. Ordena a fila do seu operador; não é uma promessa sobre quando ele responde.',
  },
  'support.notDisputes': {
    en: 'For a question — a link that will not work, a banner you need, when a payment run goes out. If you disagree with a figure you have been paid, raise it in Disputes instead, where it is answered with evidence and an outcome.',
    es: 'Para una pregunta: un enlace que no funciona, un banner que necesitas, cuándo sale un pago. Si no estás de acuerdo con una cifra que te han pagado, plantéalo en Disputas, donde se responde con pruebas y una resolución.',
    'pt-BR': 'Para uma pergunta — um link que não funciona, um banner de que você precisa, quando sai um pagamento. Se você discorda de um valor que recebeu, abra em Contestações, onde é respondido com provas e um desfecho.',
  },
  'support.empty.open': {
    en: 'Nothing open. Ask a question if something is not working.',
    es: 'Nada abierto. Haz una pregunta si algo no funciona.',
    'pt-BR': 'Nada aberto. Faça uma pergunta se algo não estiver funcionando.',
  },
  'support.empty.resolved': {
    en: 'Nothing resolved yet.',
    es: 'Nada resuelto todavía.',
    'pt-BR': 'Nada resolvido ainda.',
  },
  'support.closed': {
    en: 'This ticket is closed. Ask a new question rather than reopening a settled thread.',
    es: 'Este ticket está cerrado. Haz una nueva pregunta en lugar de reabrir un hilo resuelto.',
    'pt-BR': 'Este ticket está fechado. Faça uma nova pergunta em vez de reabrir um tópico resolvido.',
  },
  'support.status.OPEN': { en: 'Waiting on them', es: 'Esperando a ellos', 'pt-BR': 'Aguardando eles' },
  'support.status.ANSWERED': { en: 'Answered', es: 'Respondido', 'pt-BR': 'Respondido' },
  'support.status.RESOLVED': { en: 'Resolved', es: 'Resuelto', 'pt-BR': 'Resolvido' },
  'support.status.CLOSED': { en: 'Closed', es: 'Cerrado', 'pt-BR': 'Fechado' },
  'support.priority.TRIVIAL': { en: 'Whenever', es: 'Cuando sea', 'pt-BR': 'Quando der' },
  'support.priority.LOW': { en: 'Low', es: 'Baja', 'pt-BR': 'Baixa' },
  'support.priority.NORMAL': { en: 'Normal', es: 'Normal', 'pt-BR': 'Normal' },
  'support.priority.HIGH': { en: 'High', es: 'Alta', 'pt-BR': 'Alta' },
  'support.priority.CRITICAL': { en: 'Blocking me', es: 'Me bloquea', 'pt-BR': 'Está me travando' },
  /* §2.3a/§6.7 — the profile, edited by the person it describes. */
  'account.edit': { en: 'Edit profile', es: 'Editar perfil', 'pt-BR': 'Editar perfil' },
  'account.contact': { en: 'How to reach you', es: 'Cómo contactarte', 'pt-BR': 'Como falar com você' },
  'account.contact.lead': {
    en: 'These save straight away.',
    es: 'Estos se guardan al momento.',
    'pt-BR': 'Estes são salvos na hora.',
  },
  'account.phone': { en: 'Phone / WhatsApp', es: 'Teléfono / WhatsApp', 'pt-BR': 'Telefone / WhatsApp' },
  'account.company': { en: 'Company', es: 'Empresa', 'pt-BR': 'Empresa' },
  'account.telegram': { en: 'Telegram', es: 'Telegram', 'pt-BR': 'Telegram' },
  'account.teams': { en: 'Microsoft Teams', es: 'Microsoft Teams', 'pt-BR': 'Microsoft Teams' },
  'account.websites': { en: 'Your sites', es: 'Tus sitios', 'pt-BR': 'Seus sites' },
  'account.websites.hint': {
    en: 'One or several. Nothing here parses them — it is so your operator knows where the traffic comes from.',
    es: 'Uno o varios. Aquí nada los analiza: es para que tu operador sepa de dónde viene el tráfico.',
    'pt-BR': 'Um ou vários. Nada aqui os analisa — é para o seu operador saber de onde vem o tráfego.',
  },
  'account.marketing': { en: 'How you promote', es: 'Cómo promocionas', 'pt-BR': 'Como você divulga' },
  'account.marketing.hint': {
    en: 'In your own words. The channel each link is filed under is set on the link itself.',
    es: 'En tus palabras. El canal de cada enlace se define en el propio enlace.',
    'pt-BR': 'Nas suas palavras. O canal de cada link é definido no próprio link.',
  },
  'account.newsletter': {
    en: 'Send me your newsletter',
    es: 'Envíame tu boletín',
    'pt-BR': 'Envie-me sua newsletter',
  },
  'account.registered': { en: 'Registered details', es: 'Datos registrados', 'pt-BR': 'Dados registrados' },
  'account.registered.lead': {
    en: 'Your operator knows you by these, and tax and licensing read them — so a change here is a request they decide on. Until they do, they still see the old one.',
    es: 'Tu operador te conoce por estos datos, y los usan los impuestos y las licencias, así que un cambio aquí es una solicitud que ellos deciden. Hasta entonces, siguen viendo los anteriores.',
    'pt-BR': 'Seu operador conhece você por estes dados, e eles alimentam impostos e licenciamento — então mudá-los é um pedido que ele decide. Até lá, ele continua vendo os antigos.',
  },
  'account.pending': {
    en: 'Waiting on your operator: {value}',
    es: 'Esperando a tu operador: {value}',
    'pt-BR': 'Aguardando seu operador: {value}',
  },
  'account.email.hint': {
    en: 'Where we reach you, and where a password reset goes.',
    es: 'Donde te contactamos y adonde va un restablecimiento de contraseña.',
    'pt-BR': 'Onde falamos com você e para onde vai a redefinição de senha.',
  },
  'account.email.needsMfa': {
    en: 'Set up an authenticator app before changing this. It is the one thing an account takeover goes after, and we hold no payment details for it to take instead.',
    es: 'Configura una app de autenticación antes de cambiarlo. Es lo que busca el robo de una cuenta, y no guardamos datos de pago que puedan llevarse en su lugar.',
    'pt-BR': 'Configure um app autenticador antes de mudar isto. É o que um roubo de conta busca, e não guardamos dados de pagamento para levarem no lugar.',
  },
  'account.logins': { en: 'Recent sign-ins', es: 'Inicios de sesión recientes', 'pt-BR': 'Entradas recentes' },
  'account.logins.from': { en: 'From', es: 'Desde', 'pt-BR': 'De' },
  'account.changePassword': { en: 'Change password', es: 'Cambiar contraseña', 'pt-BR': 'Alterar senha' },
  'account.profile': { en: 'Profile', es: 'Perfil', 'pt-BR': 'Perfil' },
  'account.paid': { en: 'Getting paid', es: 'Cómo cobras', 'pt-BR': 'Como você recebe' },

  /* ---------------- postbacks (§41.7) ---------------- */
  // The screen's own heading says "Postbacks"; the panel says whose, the way
  // History's does, rather than repeating the heading one line below it.
  'postbacks.panel': { en: 'Your postbacks', es: 'Tus postbacks', 'pt-BR': 'Seus postbacks' },
  // The macro names themselves are identifiers and are not translated — they
  // are what the affiliate types into their own URL.
  'postbacks.macros': {
    en: "Your URL may include macros — they're filled in from the conversion that triggered it:",
    es: 'Tu URL puede incluir macros: se rellenan a partir de la conversión que la disparó:',
    'pt-BR': 'Sua URL pode incluir macros — elas são preenchidas a partir da conversão que a disparou:',
  },
  'postbacks.signed': {
    en: 'The same fields are also sent as a signed JSON body (header {header}), so you can read either.',
    es: 'Los mismos campos también se envían como cuerpo JSON firmado (cabecera {header}), así que puedes leer cualquiera de los dos.',
    'pt-BR': 'Os mesmos campos também são enviados como corpo JSON assinado (cabeçalho {header}), então você pode ler qualquer um dos dois.',
  },
  'postbacks.event.REGISTRATION': { en: 'Registration', es: 'Registro', 'pt-BR': 'Registro' },
  'postbacks.event.FTD': { en: 'First deposit', es: 'Primer depósito', 'pt-BR': 'Primeiro depósito' },
  'postbacks.event.DEPOSIT': { en: 'Every deposit', es: 'Cada depósito', 'pt-BR': 'Cada depósito' },
  'postbacks.event.WITHDRAWAL': { en: 'Withdrawal', es: 'Retiro', 'pt-BR': 'Saque' },
  'postbacks.event.WITHDRAWAL_REVERSED': { en: 'Withdrawal reversed', es: 'Retiro revertido', 'pt-BR': 'Saque revertido' },
  'postbacks.event.REVENUE': { en: 'Player revenue (NGR)', es: 'Ingresos del jugador (NGR)', 'pt-BR': 'Receita do jogador (NGR)' },
  // What each event reports. Registration, first deposit and deposit go out
  // whatever the deal pays; qualification is the one that carries a commission.
  'postbacks.field.event.hint': {
    en: 'Registration, first deposit and every deposit are sent for each of your players whatever your deal pays, and carry no commission; a deposit carries its amount in {deposit_amount}. Withdrawal and withdrawal reversed work the same way, with the amount in {withdrawal_amount}. Player revenue is sent when a period is locked, once per player, with your share of that player\'s NGR for the period in {ngr}; if the period is later corrected, it is sent again with status restated. Qualification is sent when you earn a commission and carries it in {payout}.',
    es: 'Registro, primer depósito y cada depósito se envían por cada uno de tus jugadores sea cual sea tu acuerdo, y no llevan comisión; un depósito lleva su importe en {deposit_amount}. Retiro y retiro revertido funcionan igual, con el importe en {withdrawal_amount}. Ingresos del jugador se envía al cerrar definitivamente un periodo, una vez por jugador, con tu parte del NGR de ese jugador en el periodo en {ngr}; si el periodo se corrige después, se envía de nuevo con estado restated. Calificación se envía cuando ganas una comisión y la lleva en {payout}.',
    'pt-BR': 'Cadastro, primeiro depósito e cada depósito são enviados para cada um dos seus jogadores seja qual for o seu acordo, e não levam comissão; um depósito leva o valor em {deposit_amount}. Saque e saque revertido funcionam do mesmo jeito, com o valor em {withdrawal_amount}. Receita do jogador é enviada quando um período é fechado em definitivo, uma vez por jogador, com a sua parte do NGR desse jogador no período em {ngr}; se o período for corrigido depois, é enviada de novo com status restated. Qualificação é enviada quando você ganha uma comissão e a leva em {payout}.',
  },
  'postbacks.event.QUALIFICATION': { en: 'Qualification', es: 'Calificación', 'pt-BR': 'Qualificação' },
  'postbacks.field.url': { en: 'HTTPS URL', es: 'URL HTTPS', 'pt-BR': 'URL HTTPS' },
  'postbacks.field.secret': { en: 'Shared secret', es: 'Secreto compartido', 'pt-BR': 'Segredo compartilhado' },
  'postbacks.register': { en: 'Register', es: 'Registrar', 'pt-BR': 'Registrar' },
  'postbacks.registered': { en: 'Registered as {id}.', es: 'Registrado como {id}.', 'pt-BR': 'Registrado como {id}.' },
  'postbacks.noDeliveries': {
    en: 'No deliveries yet for this postback.',
    es: 'Todavía no hay entregas para este postback.',
    'pt-BR': 'Ainda não há entregas para este postback.',
  },
  // §41.7 — complete self-service: a list, an add form behind an action, and
  // per-row edit/pause/resume/test/remove.
  'postbacks.add': { en: 'Add postback', es: 'Añadir postback', 'pt-BR': 'Adicionar postback' },
  'postbacks.empty': {
    en: "You haven't registered a postback yet. Add one to be notified — at your own URL — the moment an event happens.",
    es: 'Todavía no has registrado ningún postback. Añade uno para recibir un aviso, en tu propia URL, en el momento en que ocurra un evento.',
    'pt-BR': 'Você ainda não registrou nenhum postback. Adicione um para ser notificado — na sua própria URL — no momento em que um evento acontecer.',
  },
  'postbacks.col.url': { en: 'URL', es: 'URL', 'pt-BR': 'URL' },
  'postbacks.col.pending': { en: 'Pending', es: 'Pendiente', 'pt-BR': 'Pendente' },
  'postbacks.col.failed': { en: 'Failed', es: 'Fallidos', 'pt-BR': 'Falhas' },
  'postbacks.col.withheld': { en: 'Withheld', es: 'Retenidos', 'pt-BR': 'Retidos' },
  'postbacks.col.lastAttempt': { en: 'Last attempt', es: 'Último intento', 'pt-BR': 'Última tentativa' },
  'postbacks.col.httpCode': { en: 'HTTP code', es: 'Código HTTP', 'pt-BR': 'Código HTTP' },
  'postbacks.col.error': { en: 'Error', es: 'Error', 'pt-BR': 'Erro' },
  'postbacks.col.conversionStatus': {
    en: 'Conversion status', es: 'Estado de la conversión', 'pt-BR': 'Status da conversão',
  },
  'postbacks.col.goal': { en: 'Goal', es: 'Objetivo', 'pt-BR': 'Meta' },
  'postbacks.col.clickId': { en: 'Click ID', es: 'ID de clic', 'pt-BR': 'ID do clique' },
  'postbacks.state.active': { en: 'Active', es: 'Activo', 'pt-BR': 'Ativo' },
  'postbacks.state.paused': { en: 'Paused', es: 'Pausado', 'pt-BR': 'Pausado' },
  'postbacks.action.sendTest': { en: 'Send test', es: 'Enviar prueba', 'pt-BR': 'Enviar teste' },
  'postbacks.action.sending': { en: 'Sending…', es: 'Enviando…', 'pt-BR': 'Enviando…' },
  'postbacks.action.edit': { en: 'Edit', es: 'Editar', 'pt-BR': 'Editar' },
  'postbacks.action.pause': { en: 'Pause', es: 'Pausar', 'pt-BR': 'Pausar' },
  'postbacks.action.resume': { en: 'Resume', es: 'Reanudar', 'pt-BR': 'Retomar' },
  'postbacks.action.remove': { en: 'Remove', es: 'Quitar', 'pt-BR': 'Remover' },
  'postbacks.edit.title': { en: 'Edit — {event}', es: 'Editar — {event}', 'pt-BR': 'Editar — {event}' },
  'postbacks.edit.save': { en: 'Save', es: 'Guardar', 'pt-BR': 'Salvar' },
  'postbacks.deliveries.heading': {
    en: 'Delivery history', es: 'Historial de entregas', 'pt-BR': 'Histórico de entregas',
  },
  'postbacks.remove.title': { en: 'Remove this postback?', es: '¿Quitar este postback?', 'pt-BR': 'Remover este postback?' },
  'postbacks.remove.body': {
    en: 'No more requests will be sent to {url}.',
    es: 'No se enviarán más solicitudes a {url}.',
    'pt-BR': 'Nenhuma solicitação a mais será enviada para {url}.',
  },
  'postbacks.field.secret.generate': { en: 'Generate', es: 'Generar', 'pt-BR': 'Gerar' },
  // Optional since the form generates one on save when this is blank — the
  // affiliate no longer has to invent a secret to register a postback.
  'postbacks.field.secret.hint': {
    en: "Optional — leave blank and we'll generate one and show it once after you register. 16 to 256 characters.",
    es: 'Opcional: déjalo en blanco y generaremos uno que se mostrará una sola vez tras registrarlo. De 16 a 256 caracteres.',
    'pt-BR': 'Opcional — deixe em branco e geraremos um, mostrado uma única vez após o registro. De 16 a 256 caracteres.',
  },
  'postbacks.field.secret.keepHint': {
    en: 'Leave blank to keep the current secret. A new one is 16 to 256 characters.',
    es: 'Déjalo en blanco para mantener el secreto actual. Uno nuevo debe tener de 16 a 256 caracteres.',
    'pt-BR': 'Deixe em branco para manter o segredo atual. Um novo deve ter de 16 a 256 caracteres.',
  },
  // A new registration's secret comes back once more, in the panel's
  // "Registered as …" note; an edited one does not — hence two notes.
  'postbacks.field.secret.note': {
    en: "Copy it now — after you save it's shown once more, then never again.",
    es: 'Cópialo ahora: después de guardar se muestra una vez más y luego nunca más.',
    'pt-BR': 'Copie agora — depois de salvar, ele é mostrado mais uma vez e depois nunca mais.',
  },
  'postbacks.field.secret.editNote': {
    en: "Copy it now — once you save, it isn't shown again.",
    es: 'Cópialo ahora: una vez guardado, no volverá a mostrarse.',
    'pt-BR': 'Copie agora — depois de salvar, ele não será mostrado novamente.',
  },
  'postbacks.delivery.status.DELIVERED': { en: 'Delivered', es: 'Entregado', 'pt-BR': 'Entregue' },
  'postbacks.delivery.status.FAILED': { en: 'Failed', es: 'Fallido', 'pt-BR': 'Falhou' },
  'postbacks.delivery.status.EXHAUSTED': {
    en: 'Retries exhausted', es: 'Reintentos agotados', 'pt-BR': 'Tentativas esgotadas',
  },
  'postbacks.delivery.status.WITHHELD': { en: 'Withheld', es: 'Retenido', 'pt-BR': 'Retido' },
  'postbacks.delivery.status.PENDING': { en: 'Pending', es: 'Pendiente', 'pt-BR': 'Pendente' },
  // The conversion's own status at send time — the only four values the
  // sender writes (LINE_STATUS_TO_POSTBACK_STATUS). Not the delivery status
  // above; a delivery can fail regardless of which of these the conversion
  // carried.
  'postbacks.conversionStatus.pending': { en: 'Pending', es: 'Pendiente', 'pt-BR': 'Pendente' },
  'postbacks.conversionStatus.confirmed': { en: 'Confirmed', es: 'Confirmado', 'pt-BR': 'Confirmado' },
  'postbacks.conversionStatus.hold': { en: 'On hold', es: 'En espera', 'pt-BR': 'Em espera' },
  'postbacks.conversionStatus.rejected': { en: 'Rejected', es: 'Rechazado', 'pt-BR': 'Rejeitado' },
  'postbacks.paused.note': {
    en: 'Paused — nothing is sent. Events are kept and sent when you resume.',
    es: 'Pausado: no se envía nada. Los eventos se conservan y se envían cuando reanudas.',
    'pt-BR': 'Pausado — nada é enviado. Os eventos são mantidos e enviados quando você retoma.',
  },
  // Replaces the server's own `note` on the list (English-only, third
  // person) — see the comment where this renders.
  'postbacks.list.note': {
    en:
      'Every attempt is signed and retried with backoff; delivery status is ' +
      'visible to you at every step. A postback is sent once the conversion ' +
      'behind it has been priced, which happens after its period\u2019s window ' +
      'ends — so traffic sent today is delivered against, and counted here, ' +
      'from the next day. Send test goes now, and does not wait.',
    es:
      'Cada intento está firmado y se reintenta con espera creciente; el estado ' +
      'de la entrega es visible para ti en todo momento. Un postback se envía ' +
      'cuando la conversión que lo origina ya ha sido calculada, lo que ocurre ' +
      'tras cerrarse la ventana de su periodo: el tráfico de hoy se entrega, y ' +
      'se cuenta aquí, a partir del día siguiente. «Enviar prueba» se envía al ' +
      'momento y no espera.',
    'pt-BR':
      'Cada tentativa é assinada e reenviada com espera progressiva; o status da ' +
      'entrega fica visível para você a cada etapa. Um postback é enviado quando ' +
      'a conversão por trás dele foi calculada, o que acontece depois que a ' +
      'janela do período termina — portanto o tráfego de hoje é entregue, e ' +
      'contado aqui, a partir do dia seguinte. «Enviar teste» vai na hora e não ' +
      'espera.',
  },
  // Paste-and-autofill: the URL exactly as the affiliate's tracker hands it
  // out, one row per query parameter, a placeholder's row picking what we
  // put there.
  'postbacks.fill.intro': {
    en: 'Paste the URL exactly as your tracker gives it. For each placeholder, pick what we put there; fixed values are sent as typed.',
    es: 'Pega la URL tal como te la da tu rastreador. En cada marcador, elige qué ponemos; los valores fijos se envían tal cual.',
    'pt-BR': 'Cole a URL exatamente como o seu rastreador a fornece. Em cada marcador, escolha o que colocamos; valores fixos são enviados como digitados.',
  },
  'postbacks.fill.col.param': { en: 'Parameter', es: 'Parámetro', 'pt-BR': 'Parâmetro' },
  'postbacks.fill.col.typed': { en: 'In your URL', es: 'En tu URL', 'pt-BR': 'Na sua URL' },
  'postbacks.fill.col.carries': { en: 'Will carry', es: 'Llevará', 'pt-BR': 'Vai levar' },
  'postbacks.fill.fixed': { en: 'Fixed — sent as typed', es: 'Fijo: se envía tal cual', 'pt-BR': 'Fixo — enviado como digitado' },
  // A fixed value with one of our macros inside it (`aff_{sub_id_1}`): never
  // rewritten, but not sent as typed either — the sender fills the macro.
  'postbacks.fill.embedded': {
    en: 'Sent with {macros} filled in', es: 'Se envía rellenando {macros}', 'pt-BR': 'Enviado preenchendo {macros}',
  },
  'postbacks.fill.choose': { en: 'Choose…', es: 'Elegir…', 'pt-BR': 'Escolher…' },
  'postbacks.fill.keep': { en: 'Leave as typed', es: 'Dejar tal cual', 'pt-BR': 'Deixar como digitado' },
  'postbacks.fill.unrecognised': {
    en: "We don't recognise this name — choose what to send.",
    es: 'No reconocemos este nombre: elige qué enviar.',
    'pt-BR': 'Não reconhecemos este nome — escolha o que enviar.',
  },
  'postbacks.fill.preview': { en: 'Saved as', es: 'Se guardará como', 'pt-BR': 'Será salva como' },
  'postbacks.fill.incomplete': {
    en: 'Choose what goes in every placeholder, or Leave as typed, before saving.',
    es: 'Elige qué va en cada marcador, o «Dejar tal cual», antes de guardar.',
    'pt-BR': 'Escolha o que vai em cada marcador, ou «Deixar como digitado», antes de salvar.',
  },
  // `cid` is read as a click ID. Some trackers mean a campaign ID by it, so the
  // row says which it carries and how to pick the other.
  'postbacks.fill.cid': {
    en: 'We read cid as your click ID: sub ID 1 from your tracking link. If your tracker uses cid for a campaign ID, choose {offer_id} instead.',
    es: 'Leemos cid como tu ID de clic: el sub ID 1 de tu enlace de seguimiento. Si tu rastreador usa cid para un ID de campaña, elige {offer_id}.',
    'pt-BR': 'Lemos cid como o seu ID de clique: o sub ID 1 do seu link de rastreamento. Se o seu rastreador usa cid para um ID de campanha, escolha {offer_id}.',
  },
  // The URL as it would be saved is past the length the platform stores.
  'postbacks.fill.tooLong': {
    en: 'With the placeholders filled in, this URL is {length} characters, and the limit is {max}. Shorten it before saving.',
    es: 'Con los marcadores rellenados, esta URL tiene {length} caracteres y el límite es {max}. Acórtala antes de guardar.',
    'pt-BR': 'Com os marcadores preenchidos, esta URL tem {length} caracteres e o limite é {max}. Encurte-a antes de salvar.',
  },
  'postbacks.advanced': {
    en: 'Advanced — shared secret', es: 'Avanzado: secreto compartido', 'pt-BR': 'Avançado — segredo compartilhado',
  },
  'postbacks.registered.secret': {
    en: 'Its shared secret, shown only this once — keep it if you check the signature on each postback:',
    es: 'Su secreto compartido, que solo se muestra esta vez; guárdalo si verificas la firma de cada postback:',
    'pt-BR': 'O segredo compartilhado, mostrado só desta vez — guarde-o se você verifica a assinatura de cada postback:',
  },
  // What each macro carries, beside its name in the autofill dropdown. The
  // `{name}` itself is an identifier and is rendered outside these strings.
  'postbacks.macro.event': {
    en: 'Event: REGISTRATION, FTD, DEPOSIT or QUALIFICATION',
    es: 'Evento: REGISTRATION, FTD, DEPOSIT o QUALIFICATION',
    'pt-BR': 'Evento: REGISTRATION, FTD, DEPOSIT ou QUALIFICATION',
  },
  'postbacks.macro.id': { en: 'Conversion ID', es: 'ID de la conversión', 'pt-BR': 'ID da conversão' },
  'postbacks.macro.status': {
    en: 'Status: pending, confirmed, hold, rejected, or restated for a corrected revenue figure',
    es: 'Estado: pending, confirmed, hold, rejected, o restated para un ingreso corregido',
    'pt-BR': 'Status: pending, confirmed, hold, rejected, ou restated para uma receita corrigida',
  },
  'postbacks.macro.goal': { en: 'Goal key', es: 'Clave del objetivo', 'pt-BR': 'Chave da meta' },
  'postbacks.macro.revenue': {
    en: 'Commission in minor units, e.g. 50000 for 500.00',
    es: 'Comisión en unidades menores, p. ej. 50000 por 500.00',
    'pt-BR': 'Comissão em unidades menores, ex. 50000 para 500.00',
  },
  'postbacks.macro.payout': {
    en: 'Commission, e.g. 500.00 (qualification postbacks)',
    es: 'Comisión, p. ej. 500.00 (postbacks de calificación)',
    'pt-BR': 'Comissão, ex. 500.00 (postbacks de qualificação)',
  },
  'postbacks.macro.currency': { en: 'Currency code, e.g. USD', es: 'Código de moneda, p. ej. USD', 'pt-BR': 'Código da moeda, ex. USD' },
  'postbacks.macro.deposit_amount': {
    en: 'Deposit amount, e.g. 25.00 (first deposit and deposit postbacks)',
    es: 'Importe del depósito, p. ej. 25.00 (postbacks de primer depósito y de depósito)',
    'pt-BR': 'Valor do depósito, ex. 25.00 (postbacks de primeiro depósito e de depósito)',
  },
  'postbacks.macro.withdrawal_amount': {
    en: 'Withdrawal amount, e.g. 25.00 (withdrawal postbacks)',
    es: 'Importe del retiro, p. ej. 25.00 (postbacks de retiro)',
    'pt-BR': 'Valor do saque, ex. 25.00 (postbacks de saque)',
  },
  'postbacks.macro.ngr': {
    en: 'Your share of the player\'s NGR for the period, e.g. 120.50 or -40.00 (player revenue postbacks)',
    es: 'Tu parte del NGR del jugador en el periodo, p. ej. 120.50 o -40.00 (postbacks de ingresos del jugador)',
    'pt-BR': 'A sua parte do NGR do jogador no período, ex. 120.50 ou -40.00 (postbacks de receita do jogador)',
  },
  'postbacks.macro.period_start': {
    en: 'When the period began (UTC; player revenue postbacks)',
    es: 'Cuándo empezó el periodo (UTC; postbacks de ingresos del jugador)',
    'pt-BR': 'Quando o período começou (UTC; postbacks de receita do jogador)',
  },
  'postbacks.macro.period_end': {
    en: 'When the period ended (UTC; player revenue postbacks)',
    es: 'Cuándo terminó el periodo (UTC; postbacks de ingresos del jugador)',
    'pt-BR': 'Quando o período terminou (UTC; postbacks de receita do jogador)',
  },
  'postbacks.macro.player_ref': { en: 'Player reference', es: 'Referencia del jugador', 'pt-BR': 'Referência do jogador' },
  'postbacks.macro.click_id': { en: 'Our click ID', es: 'Nuestro ID de clic', 'pt-BR': 'Nosso ID do clique' },
  'postbacks.macro.brand_url': {
    en: "The brand's web app address",
    es: 'La dirección de la aplicación web de la marca',
    'pt-BR': 'O endereço do aplicativo web da marca',
  },
  'postbacks.macro.tid': {
    en: "The brand's transaction ID, when it sends one",
    es: 'El ID de transacción de la marca, si lo envía',
    'pt-BR': 'O ID de transação da marca, quando ela envia',
  },
  'postbacks.macro.advertiser_id': { en: 'Brand ID', es: 'ID de la marca', 'pt-BR': 'ID da marca' },
  'postbacks.macro.partner_id': { en: 'Your affiliate ID', es: 'Tu ID de afiliado', 'pt-BR': 'Seu ID de afiliado' },
  'postbacks.macro.offer_id': { en: 'Campaign ID', es: 'ID de la campaña', 'pt-BR': 'ID da campanha' },
  'postbacks.macro.country': { en: 'Country code, e.g. NG', es: 'Código de país, p. ej. NG', 'pt-BR': 'Código do país, ex. NG' },
  // The time the commission for the conversion was calculated, not the
  // player's own registration or deposit time: that is what the sender has.
  'postbacks.macro.conversion_date': {
    en: 'When the event happened, or for a qualification when the commission was calculated (UTC)',
    es: 'Cuándo ocurrió el evento o, en una calificación, cuándo se calculó la comisión (UTC)',
    'pt-BR': 'Quando o evento ocorreu ou, numa qualificação, quando a comissão foi calculada (UTC)',
  },
  'postbacks.macro.sub_id': {
    en: 'Sub ID {n} from your tracking link',
    es: 'Sub ID {n} de tu enlace de seguimiento',
    'pt-BR': 'Sub ID {n} do seu link de rastreamento',
  },

  /* ---------------- the two switchers in the chrome (§2.5c) ---------------- */
  'switcher.allBrands': {
    en: 'Every brand you work with on this programme.',
    es: 'Todas las marcas con las que trabajas en este programa.',
    'pt-BR': 'Todas as marcas com que você trabalha neste programa.',
  },
  'switcher.oneBrand': {
    en: 'Only this brand on every screen below.',
    es: 'Solo esta marca en todas las pantallas de abajo.',
    'pt-BR': 'Apenas esta marca em todas as telas abaixo.',
  },
  // "Showing", not "Programme": the control decides which company's figures
  // every other screen is about, and naming the effect is the part somebody
  // needs. Both translations name the effect too.
  'switcher.showing': { en: 'Showing', es: 'Mostrando', 'pt-BR': 'Mostrando' },
  'switcher.onlyBrand': {
    en: 'The one brand you work with on this programme.',
    es: 'La única marca con la que trabajas en este programa.',
    'pt-BR': 'A única marca com que você trabalha neste programa.',
  },
  'switcher.company': { en: 'Company', es: 'Empresa', 'pt-BR': 'Empresa' },
  'switcher.switching': { en: 'Switching…', es: 'Cambiando…', 'pt-BR': 'Trocando…' },
  'brands.applicationSent': { en: 'Sent.', es: 'Enviada.', 'pt-BR': 'Enviada.' },
  'statements.noneIssued': {
    en: 'No statement has been issued yet. One is issued for every period once it is locked, whether or not anything was payable.',
    es: 'Todavía no se ha emitido ningún estado de cuenta. Se emite uno por cada periodo en cuanto se bloquea, haya o no algo pagadero.',
    'pt-BR': 'Ainda não foi emitido nenhum extrato. Um é emitido para cada período assim que ele é travado, havendo ou não algo pagável.',
  },

  /* -------------------------------------------------------------- *
   * One campaign, and which terms pay you on it — §7.12d
   * -------------------------------------------------------------- */
  'campaignDetail.open': { en: 'Details and terms', es: 'Detalles y condiciones', 'pt-BR': 'Detalhes e condições' },
  'campaignDetail.whatItPays': {
    en: 'What this campaign pays you',
    es: 'Lo que te paga esta campaña',
    'pt-BR': 'O que esta campanha paga a você',
  },
  'campaignDetail.landingPages': { en: 'Landing pages', es: 'Páginas de destino', 'pt-BR': 'Páginas de destino' },
  'campaignTerms.title': {
    en: 'Your terms on this campaign',
    es: 'Tus condiciones en esta campaña',
    'pt-BR': 'Suas condições nesta campanha',
  },
  // Which of the three pays here, said before the figures: the same table
  // under a different heading is a different deal.
  'campaignTerms.applies': { en: 'Paid on: {source}', es: 'Se te paga según: {source}', 'pt-BR': 'Você recebe conforme: {source}' },
  'campaignTerms.none': {
    en: 'No terms pay you on this campaign yet. Your account manager can tell you what is planned.',
    es: 'Todavía no hay condiciones que te paguen en esta campaña. Tu gestor de cuenta puede decirte qué está previsto.',
    'pt-BR': 'Ainda não há condições que paguem você nesta campanha. Seu gerente de conta pode dizer o que está previsto.',
  },
  'campaignTerms.unreadable': {
    en: 'These terms could not be read just now. Try again in a moment, or ask your account manager.',
    es: 'No se han podido leer estas condiciones ahora mismo. Vuelve a intentarlo en un momento o pregunta a tu gestor de cuenta.',
    'pt-BR': 'Não foi possível ler estas condições agora. Tente de novo em instantes ou pergunte ao seu gerente de conta.',
  },
  'campaignTerms.plan': { en: 'Plan', es: 'Plan', 'pt-BR': 'Plano' },
  'campaignTerms.cpaTrigger': { en: 'CPA pays on', es: 'El CPA se paga por', 'pt-BR': 'O CPA é pago por' },
  'campaignTerms.cpaAmount': { en: 'CPA amount', es: 'Importe del CPA', 'pt-BR': 'Valor do CPA' },
  'campaignTerms.revshareOf': {
    en: 'Revenue share is of',
    es: 'El reparto de ingresos es sobre',
    'pt-BR': 'A participação na receita é sobre',
  },
  'campaignTerms.revshare.varies': {
    en: 'Varies by product or tier',
    es: 'Varía según el producto o el nivel',
    'pt-BR': 'Varia conforme o produto ou o nível',
  },
  'campaignTerms.cumulativeDeposit': {
    en: 'Deposits must reach',
    es: 'Los depósitos deben alcanzar',
    'pt-BR': 'Os depósitos precisam chegar a',
  },
  'campaignTerms.cumulativeWagering': {
    en: 'Wagering must reach',
    es: 'Las apuestas deben alcanzar',
    'pt-BR': 'As apostas precisam chegar a',
  },
  'campaignTerms.coverage.cpa': { en: 'Where the CPA pays', es: 'Dónde se paga el CPA', 'pt-BR': 'Onde o CPA é pago' },
  'campaignTerms.coverage.revshare': {
    en: 'Where the revenue share pays',
    es: 'Dónde se paga el reparto de ingresos',
    'pt-BR': 'Onde a participação na receita é paga',
  },
  'campaignTerms.bonus': { en: 'Bonus payout', es: 'Pago de bonificación', 'pt-BR': 'Pagamento de bônus' },
  'nav.campaigns': { en: 'Campaigns', es: 'Campañas', 'pt-BR': 'Campanhas' },
  'campaignsDirectory.empty': {
    en: 'No campaigns yet — nothing you are on, nothing offered, nothing open to ask for.',
    es: 'Aún no hay campañas: ninguna en la que estés, ninguna ofrecida, ninguna abierta para solicitar.',
    'pt-BR': 'Ainda não há campanhas — nenhuma em que você esteja, nenhuma oferecida, nenhuma aberta para solicitar.',
  },
  'campaignsDirectory.on': {
    en: 'Campaigns you are on',
    es: 'Campañas en las que estás',
    'pt-BR': 'Campanhas em que você está',
  },
  'campaignsDirectory.invited': {
    en: 'Offered to you',
    es: 'Ofrecidas a ti',
    'pt-BR': 'Oferecidas a você',
  },
  'campaignsDirectory.canAskFor': {
    en: 'Open to ask for',
    es: 'Abiertas para solicitar',
    'pt-BR': 'Abertas para solicitar',
  },
  'campaignsDirectory.lead': {
    en: 'Every campaign open to you, and what each one pays. Open a row for the full terms and what the payout has been.',
    es: 'Todas las campañas abiertas para ti y lo que paga cada una. Abre una fila para ver las condiciones completas y el historial del pago.',
    'pt-BR': 'Todas as campanhas abertas para você e quanto cada uma paga. Abra uma linha para ver os termos completos e o histórico do pagamento.',
  },
  'campaignsDirectory.colPaidOn': { en: 'Paid on', es: 'Se paga según', 'pt-BR': 'Pago conforme' },
  'campaignsDirectory.colPlan': { en: 'Plan', es: 'Plan', 'pt-BR': 'Plano' },
  'campaignsDirectory.colRate': { en: 'Rate', es: 'Tarifa', 'pt-BR': 'Taxa' },
  'campaignsDirectory.colProducts': { en: 'Products', es: 'Productos', 'pt-BR': 'Produtos' },
  'campaignsDirectory.colGeos': { en: 'Where', es: 'Dónde', 'pt-BR': 'Onde' },
  'campaignsDirectory.notYet': {
    en: 'Nothing pays you here until the operator accepts.',
    es: 'Aquí no se te paga nada hasta que el operador acepte.',
    'pt-BR': 'Nada é pago aqui até que o operador aceite.',
  },
  'campaignTerms.history': {
    en: 'What this campaign has paid',
    es: 'Lo que ha pagado esta campaña',
    'pt-BR': 'O que esta campanha já pagou',
  },
  'campaignTerms.historyNote': {
    en: 'You keep the terms a player was attributed under, so a campaign repriced partway through pays two rates at once. These are the rates, and the days each one ran.',
    es: 'Conservas las condiciones con las que se te atribuyó un jugador, así que una campaña cuyo precio cambió a mitad de camino paga dos tarifas a la vez. Estas son las tarifas y los días que estuvo vigente cada una.',
    'pt-BR': 'Você mantém os termos sob os quais um jogador foi atribuído, portanto uma campanha reprecificada no meio do caminho paga duas taxas ao mesmo tempo. Estas são as taxas e os dias em que cada uma vigorou.',
  },
  'campaignTerms.historyLive': {
    en: 'in force',
    es: 'vigente',
    'pt-BR': 'em vigor',
  },
  'campaignTerms.offer.title': { en: 'Offer for this campaign', es: 'Oferta para esta campaña', 'pt-BR': 'Oferta para esta campanha' },
  // §7.11 — nothing changes until they answer, and saying so is what makes
  // declining a safe thing to press.
  'campaignTerms.offer.note': {
    en: 'Terms for this campaign alone. They pay only once you accept; until then, what is shown above still applies.',
    es: 'Condiciones solo para esta campaña. Solo pagan cuando las aceptas; hasta entonces, sigue aplicándose lo que se muestra arriba.',
    'pt-BR': 'Condições só para esta campanha. Só pagam depois que você aceitar; até lá, continua valendo o que aparece acima.',
  },
  'campaignTerms.offer.when': {
    en: 'In force from {date} if you accept.',
    es: 'En vigor desde el {date} si las aceptas.',
    'pt-BR': 'Em vigor a partir de {date} se você aceitar.',
  },
  'source.CAMPAIGN': { en: 'This campaign’s payout', es: 'El pago propio de esta campaña', 'pt-BR': 'O pagamento próprio desta campanha' },
  'source.DEFAULT': { en: 'Your default deal with {brand}', es: 'Tu acuerdo por defecto con {brand}', 'pt-BR': 'Seu acordo padrão com {brand}' },
  'source.CUSTOM': {
    en: 'Terms agreed for this campaign alone',
    es: 'Condiciones acordadas solo para esta campaña',
    'pt-BR': 'Condições acordadas só para esta campanha',
  },
  'plan.CPA': { en: 'Cost per acquisition (CPA)', es: 'Coste por adquisición (CPA)', 'pt-BR': 'Custo por aquisição (CPA)' },
  'plan.REVSHARE': { en: 'Revenue share', es: 'Reparto de ingresos', 'pt-BR': 'Participação na receita' },
  'plan.HYBRID': {
    en: 'Hybrid — CPA and revenue share',
    es: 'Híbrido: CPA y reparto de ingresos',
    'pt-BR': 'Híbrido — CPA e participação na receita',
  },
  'trigger.FTD': { en: 'First deposit', es: 'Primer depósito', 'pt-BR': 'Primeiro depósito' },
  'trigger.REGISTRATION': { en: 'Registration', es: 'Registro', 'pt-BR': 'Cadastro' },
  'trigger.CUMULATIVE_DEPOSIT': { en: 'Cumulative deposit', es: 'Depósito acumulado', 'pt-BR': 'Depósito acumulado' },
  'trigger.DEPOSIT': { en: 'Every deposit', es: 'Cada depósito', 'pt-BR': 'Cada depósito' },
  'basis.NGR': { en: 'Commissionable NGR (net gaming revenue after deductions)', es: 'NGR comisionable (ingresos netos de juego tras deducciones)', 'pt-BR': 'NGR comissionável (receita líquida de jogo após deduções)' },
  'basis.GGR': { en: 'GGR (gross gaming revenue)', es: 'GGR (ingresos brutos de juego)', 'pt-BR': 'GGR (receita bruta de jogo)' },
  'basis.FTD_VALUE': { en: 'First deposit value', es: 'Valor del primer depósito', 'pt-BR': 'Valor do primeiro depósito' },
  'basis.FTD_COUNT': {
    en: 'Qualified first deposits, counted',
    es: 'Primeros depósitos calificados, contados',
    'pt-BR': 'Primeiros depósitos qualificados, contados',
  },
  'basis.DEPOSITS': { en: 'Every settled deposit', es: 'Cada depósito liquidado', 'pt-BR': 'Cada depósito liquidado' },
  'coverage.everywhere': { en: 'Everywhere', es: 'En todas partes', 'pt-BR': 'Em todos os lugares' },
  'coverage.in': { en: 'Pays in: {list}', es: 'Paga en: {list}', 'pt-BR': 'Paga em: {list}' },
  'coverage.notIn': { en: 'Not in: {list}', es: 'Excepto en: {list}', 'pt-BR': 'Exceto em: {list}' },
  // §7.12 — on the Terms screen and the offers panel, so terms for one
  // campaign alone are never mistaken for the deal that pays everywhere else.
  'terms.forCampaign': { en: 'For campaign: {name}', es: 'Para la campaña: {name}', 'pt-BR': 'Para a campanha: {name}' },
  'terms.defaultDeal': { en: 'Default deal', es: 'Acuerdo por defecto', 'pt-BR': 'Acordo padrão' },
  /*
   * §7.12d (owner, 2026-10-07) — the Terms page lists a campaign's own payout
   * where it is what prices the affiliate, so each panel says whose terms it
   * shows: their own for one campaign, or the campaign's standard payout.
   */
  'terms.scope.personalCampaign': {
    en: 'Your personal deal · {name}',
    es: 'Tu acuerdo personal · {name}',
    'pt-BR': 'Seu acordo pessoal · {name}',
  },
  'terms.scope.campaignPayout': {
    en: 'Campaign standard payout · {name}',
    es: 'Pago estándar de la campaña · {name}',
    'pt-BR': 'Pagamento padrão da campanha · {name}',
  },
  'terms.campaignPayout.note': {
    en:
      'You are paid this because you take part in {name}. The operator sets it for everyone on ' +
      'the campaign, and you are notified when it changes.',
    es:
      'Se te paga esto porque participas en {name}. El operador lo fija para todos los de la ' +
      'campaña, y se te avisa cuando cambia.',
    'pt-BR':
      'Você recebe isto porque participa de {name}. O operador o define para todos na ' +
      'campanha, e você é avisado quando ele muda.',
  },
  'terms.campaigns.title': { en: '{brand} — your campaigns', es: '{brand}: tus campañas', 'pt-BR': '{brand} — suas campanhas' },
  'terms.campaign.left': {
    en: 'You left this campaign — players you brought through it are still paid on it',
    es: 'Dejaste esta campaña: los jugadores que trajiste por ella se siguen pagando según ella',
    'pt-BR': 'Você saiu desta campanha — os jogadores que trouxe por ela continuam sendo pagos por ela',
  },
  'terms.campaigns.paidOn': { en: 'What pays you there', es: 'Qué te paga allí', 'pt-BR': 'O que paga você lá' },
  'terms.paidOn.none': {
    en: 'No terms in force yet — nothing accrues here until your account manager sets them',
    es: 'Aún no hay condiciones en vigor: no se acumula nada aquí hasta que tu gestor de cuenta las fije',
    'pt-BR': 'Ainda não há condições em vigor — nada é acumulado aqui até seu gerente de conta defini-las',
  },
  /* §26.2 — how the two halves of a hybrid deal combine. */
  'terms.hybridMode': { en: 'CPA and revenue share', es: 'CPA y reparto de ingresos', 'pt-BR': 'CPA e participação na receita' },
  'terms.hybridMode.INDEPENDENT': {
    en: 'Paid separately — each in full',
    es: 'Se pagan por separado, cada uno completo',
    'pt-BR': 'Pagos separadamente — cada um integralmente',
  },
  'terms.hybridMode.CPA_RECOUPABLE': {
    en: 'Revenue share is held back per player until it covers the CPA already paid for them',
    es: 'El reparto se retiene por jugador hasta cubrir el CPA ya pagado por él',
    'pt-BR': 'A participação é retida por jogador até cobrir o CPA já pago por ele',
  },
  'terms.hybridMode.FIRST_PERIOD_EXCLUSION': {
    en: 'No revenue share in the period a player\'s CPA is paid',
    es: 'Sin reparto de ingresos en el periodo en que se paga el CPA del jugador',
    'pt-BR': 'Sem participação na receita no período em que o CPA do jogador é pago',
  },

  /*
   * §24.7b — the acquisition price list, read out to the affiliate. The
   * wildcard needs words on both keys: a row shown as "* / *" asks the reader
   * to know what a star means in a table they did not write. `elsewhere` is
   * the row that stops the list reading as exhaustive — everything no row
   * matches is still paid, at the deal's own amount.
   */
  'terms.cpaPriceList': { en: 'CPA by country', es: 'CPA por país', 'pt-BR': 'CPA por país' },
  'terms.cpaPriceList.anyJurisdiction': { en: 'Anywhere', es: 'En cualquier lugar', 'pt-BR': 'Em qualquer lugar' },
  'terms.cpaPriceList.anyChannel': { en: 'any channel', es: 'cualquier canal', 'pt-BR': 'qualquer canal' },
  'terms.cpaPriceList.elsewhere': { en: 'Everywhere else', es: 'En todo lo demás', 'pt-BR': 'Em todo o restante' },

  /*
   * §14.6d — a component's own attribution window. Never-expire is a word
   * rather than a number because there is no number: rendering it as 0 is the
   * sentinel this platform refuses by name, and rendering it as blank reads
   * as "not set" when it is very much set.
   */
  'terms.componentWindow.neverExpires': { en: 'Never expires', es: 'Nunca caduca', 'pt-BR': 'Nunca expira' },
  'terms.componentWindow.value.one': { en: '{days} day', es: '{days} día', 'pt-BR': '{days} dia' },
  'terms.componentWindow.value.other': { en: '{days} days', es: '{days} días', 'pt-BR': '{days} dias' },

  /*
   * QA round 2 F1 — the tier, product and window tables, as headed sections.
   * The metric a tier table is keyed on reads as words inside the heading,
   * because "Revenue share tiers" over "0 - 1,000" leaves the reader to guess
   * whether that is players, deposits or naira. Lower case: it is spliced
   * into a sentence.
   */
  'terms.cpaTierBands': {
    en: 'CPA by number of qualifying players',
    es: 'CPA según el número de jugadores calificados',
    'pt-BR': 'CPA por número de jogadores qualificados',
  },
  'terms.revshareTierBands': { en: 'Revenue share tiers', es: 'Niveles de reparto de ingresos', 'pt-BR': 'Faixas de participação na receita' },
  'terms.revshareTierBands.by': {
    en: 'Revenue share tiers, by {metric}',
    es: 'Niveles de reparto de ingresos, según {metric}',
    'pt-BR': 'Faixas de participação na receita, por {metric}',
  },
  'terms.productTierTable': {
    en: 'Revenue share tiers for {product}',
    es: 'Niveles de reparto de ingresos de {product}',
    'pt-BR': 'Faixas de participação na receita de {product}',
  },
  'terms.productTierTable.by': {
    en: 'Revenue share tiers for {product}, by {metric}',
    es: 'Niveles de reparto de ingresos de {product}, según {metric}',
    'pt-BR': 'Faixas de participação na receita de {product}, por {metric}',
  },
  'terms.tierMetric.QFTD_COUNT': { en: 'qualifying first deposits', es: 'primeros depósitos calificados', 'pt-BR': 'primeiros depósitos qualificados' },
  'terms.tierMetric.NGR_VOLUME': { en: 'Commissionable NGR', es: 'NGR comisionable', 'pt-BR': 'NGR comissionável' },
  'terms.tierMetric.GGR_VOLUME': { en: 'gross gaming revenue', es: 'ingresos brutos de juego', 'pt-BR': 'receita bruta de jogo' },
  'terms.tierMetric.ACTIVE_PLAYERS': { en: 'active players', es: 'jugadores activos', 'pt-BR': 'jogadores ativos' },
  'terms.tierMetric.DEPOSITS': { en: 'deposits', es: 'depósitos', 'pt-BR': 'depósitos' },
  'terms.productRates': { en: 'Rates by product', es: 'Tarifas por producto', 'pt-BR': 'Taxas por produto' },
  'terms.productRates.revshare': { en: '{rate} revenue share', es: '{rate} de reparto de ingresos', 'pt-BR': '{rate} de participação na receita' },
  'terms.productRates.cpa': { en: '{amount} per qualifying player', es: '{amount} por jugador calificado', 'pt-BR': '{amount} por jogador qualificado' },
  'terms.componentWindows': { en: 'Attribution window by commission', es: 'Ventana de atribución por comisión', 'pt-BR': 'Janela de atribuição por comissão' },
  'terms.componentWindow.cpa': { en: 'CPA commission', es: 'Comisión CPA', 'pt-BR': 'Comissão CPA' },
  'terms.componentWindow.revshare': { en: 'Revenue share', es: 'Reparto de ingresos', 'pt-BR': 'Participação na receita' },
  'terms.componentWindow.source.COMPONENT': { en: 'set for this commission', es: 'fijada para esta comisión', 'pt-BR': 'definida para esta comissão' },
  'terms.componentWindow.source.DEAL': { en: 'the deal’s own window', es: 'la ventana del propio acuerdo', 'pt-BR': 'a janela do próprio acordo' },

  /*
   * QA round 2 F4 — the revenue deficit still carried. The figure is the size
   * of the deficit; the label says it is owed back, so no minus sign is
   * needed to say so.
   */
  'carryover.deficit.label': {
    en: 'Deficit to earn back · {brand}',
    es: 'Déficit por recuperar · {brand}',
    'pt-BR': 'Déficit a recuperar · {brand}',
  },
  'carryover.note': {
    en: 'Negative revenue carried in from earlier periods. Your revenue share pays this back before anything more is payable.',
    es: 'Ingresos negativos arrastrados de periodos anteriores. Tu reparto de ingresos lo recupera antes de que haya algo más por pagar.',
    'pt-BR': 'Receita negativa transportada de períodos anteriores. Sua participação na receita quita esse valor antes que algo mais seja pagável.',
  },

  /*
   * D3 — DB-IP's free IP database is licensed on condition that it is
   * credited, with a link, wherever its data is shown. The name is the
   * vendor's own and stays as it is in every language.
   */
  'clickStatus.VALID': { en: 'Counted', es: 'Contado', 'pt-BR': 'Contado' },
  'clickStatus.DUPLICATE': { en: 'Repeat click', es: 'Clic repetido', 'pt-BR': 'Clique repetido' },
  'clickStatus.BOT': { en: 'Automated traffic', es: 'Tráfico automatizado', 'pt-BR': 'Tráfego automatizado' },
  'clickStatus.INVALID_TOKEN': { en: 'Unknown link', es: 'Enlace desconocido', 'pt-BR': 'Link desconhecido' },
  'clickStatus.REVOKED': { en: 'Link switched off', es: 'Enlace desactivado', 'pt-BR': 'Link desativado' },
  'clickStatus.LINK_INACTIVE': { en: 'Link no longer active', es: 'Enlace ya no activo', 'pt-BR': 'Link não está mais ativo' },
  'clickStatus.CAMPAIGN_EXPIRED': { en: 'Campaign ended', es: 'Campaña finalizada', 'pt-BR': 'Campanha encerrada' },
  'clickStatus.AFFILIATE_BLOCKED': { en: 'Affiliate blocked', es: 'Afiliado bloqueado', 'pt-BR': 'Afiliado bloqueado' },
  'clickStatus.NOT_PARTICIPATING': {
    en: 'Affiliate no longer on this brand',
    es: 'El afiliado ya no está en esta marca',
    'pt-BR': 'O afiliado não está mais nesta marca',
  },
  'clickStatus.GEO_BLOCKED': {
    en: 'Outside the campaign\'s countries',
    es: 'Fuera de los países de la campaña',
    'pt-BR': 'Fora dos países da campanha',
  },
  'clickStatus.OS_BLOCKED': { en: 'Operating system not accepted', es: 'Sistema operativo no aceptado', 'pt-BR': 'Sistema operacional não aceito' },
  'clickStatus.DEVICE_BLOCKED': { en: 'Device not accepted', es: 'Dispositivo no aceptado', 'pt-BR': 'Dispositivo não aceito' },
  'clickStatus.TARGETING_BLOCKED': {
    en: 'Outside the campaign\'s targeting',
    es: 'Fuera de la segmentación de la campaña',
    'pt-BR': 'Fora da segmentação da campanha',
  },
  'clickStatus.NO_ELIGIBLE_CAMPAIGN': {
    en: 'No campaign in the smart link could take it',
    es: 'Ninguna campaña del enlace inteligente podía aceptarlo',
    'pt-BR': 'Nenhuma campanha do link inteligente podia aceitá-lo',
  },
  'attribution.ipGeolocation': {
    en: 'IP geolocation by DB-IP',
    es: 'Geolocalización IP por DB-IP',
    'pt-BR': 'Geolocalização de IP por DB-IP',
  },

  /*
   * §30.4d — two minimum-payout figures and the one that governs. The affiliate
   * needs the third label most: knowing their method states 20 and the general
   * figure is 50 is no use without being told which their own statement waits on.
   */
  'terms.minimumPayout.method': {
    en: 'Minimum for your payment method',
    es: 'Mínimo para tu método de pago',
    'pt-BR': 'Mínimo para o seu método de pagamento',
  },
  'terms.minimumPayout.applies': {
    en: 'What your statement waits for',
    es: 'Lo que espera tu estado de cuenta',
    'pt-BR': 'O que o seu extrato aguarda',
  },

  /* -------------------------------------------------------------- *
   * Sub-affiliates, self-service — §8.9, §12.3
   * -------------------------------------------------------------- */
  'subaffiliates.title': { en: 'Sub-affiliates', es: 'Subafiliados', 'pt-BR': 'Subafiliados' },
  'subaffiliates.link.panel': {
    en: 'Your referral link',
    es: 'Tu enlace de referido',
    'pt-BR': 'Seu link de indicação',
  },
  'subaffiliates.link.create': {
    en: 'Create my referral link',
    es: 'Crear mi enlace de referido',
    'pt-BR': 'Criar meu link de indicação',
  },
  'subaffiliates.link.creating': { en: 'Creating…', es: 'Creando…', 'pt-BR': 'Criando…' },
  // §8.9b — the last line of the link panel: how a recruit is actually taken on.
  'subaffiliates.link.applyNote': {
    en: 'They apply like anyone else — the company reviews every application.',
    es: 'Se postulan como cualquier otra persona: la empresa revisa cada solicitud.',
    'pt-BR': 'Eles se inscrevem como qualquer outra pessoa — a empresa analisa cada inscrição.',
  },
  // §8.9a — the three reasons, in plain words, never the code.
  'subaffiliates.whyNot.programmeOff': {
    en: "Recruiting sub-affiliates isn't switched on right now.",
    es: 'Reclutar subafiliados no está activado por el momento.',
    'pt-BR': 'Recrutar subafiliados não está ativado no momento.',
  },
  'subaffiliates.whyNot.notActive': {
    en: "Your account isn't active yet, so you can't recruit sub-affiliates.",
    es: 'Tu cuenta aún no está activa, así que no puedes reclutar subafiliados.',
    'pt-BR': 'Sua conta ainda não está ativa, então você não pode recrutar subafiliados.',
  },
  'subaffiliates.whyNot.depthLimit': {
    en: "You're already at the deepest level this programme allows, so you can't recruit further.",
    es: 'Ya estás en el nivel más profundo que permite este programa, así que no puedes reclutar más.',
    'pt-BR': 'Você já está no nível mais profundo que este programa permite, então não pode recrutar mais.',
  },
  // §8.9a — the operator has no public application page yet, so a referral
  // link would have nowhere to send anyone.
  'subaffiliates.whyNot.noApplicationPage': {
    en: "The company hasn't opened applications yet, so a referral link has nowhere to go.",
    es: 'La empresa aún no ha abierto las solicitudes, así que un enlace de referencia no tiene adónde ir.',
    'pt-BR': 'A empresa ainda não abriu as inscrições, então um link de indicação não tem para onde ir.',
  },
  // §8.2 — the basis, unmissable in the sentence it sits inside.
  'subaffiliates.basis.commission': {
    en: "of each sub-affiliate's commission",
    es: 'de la comisión de cada subafiliado',
    'pt-BR': 'da comissão de cada subafiliado',
  },
  'subaffiliates.basis.ngr': {
    en: 'of the Commissionable NGR (net gaming revenue after deductions) their players generate',
    es: 'del NGR comisionable (ingresos netos de juego tras deducciones) que generan sus jugadores',
    'pt-BR': 'do NGR comissionável (receita líquida de jogo após deduções) gerado pelos jogadores deles',
  },
  // §8.6 — the three lifetime policies, in words.
  'subaffiliates.lifetime.fixedTerm.one': {
    en: "for {months} month after they're approved",
    es: 'durante {months} mes después de que se les apruebe',
    'pt-BR': 'por {months} mês após serem aprovados',
  },
  'subaffiliates.lifetime.fixedTerm.other': {
    en: "for {months} months after they're approved",
    es: 'durante {months} meses después de que se les apruebe',
    'pt-BR': 'por {months} meses após serem aprovados',
  },
  'subaffiliates.lifetime.lifetime': {
    en: 'for as long as they stay',
    es: 'mientras permanezcan',
    'pt-BR': 'enquanto permanecerem',
  },
  'subaffiliates.lifetime.activeOnly': {
    en: 'while your own account is active',
    es: 'mientras tu propia cuenta esté activa',
    'pt-BR': 'enquanto sua própria conta estiver ativa',
  },
  // §8.2b — the sentence the three pieces above build. §8.2a's fact — paid on
  // top, never out of their earnings — is said every time, not left implied.
  'subaffiliates.terms.sentence': {
    en: 'You earn {rate} {basis}, {lifetime}, paid on top of theirs — it never comes out of their earnings.',
    es: 'Ganas {rate} {basis}, {lifetime}, que se paga aparte de lo suyo — nunca sale de sus ganancias.',
    'pt-BR': 'Você ganha {rate} {basis}, {lifetime}, pago à parte do deles — nunca sai dos ganhos deles.',
  },
  'subaffiliates.invite.panel': {
    en: 'Invite by email',
    es: 'Invitar por correo electrónico',
    'pt-BR': 'Convidar por e-mail',
  },
  'subaffiliates.invite.send': { en: 'Send', es: 'Enviar', 'pt-BR': 'Enviar' },
  // §8.9d — 20 a day; how many are left.
  'subaffiliates.invitationsLeft.one': {
    en: '{n} invitation left today',
    es: 'Queda {n} invitación hoy',
    'pt-BR': 'Resta {n} convite hoje',
  },
  'subaffiliates.invitationsLeft.other': {
    en: '{n} invitations left today',
    es: 'Quedan {n} invitaciones hoy',
    'pt-BR': 'Restam {n} convites hoje',
  },
  'subaffiliates.table.panel': {
    en: 'Your sub-affiliates',
    es: 'Tus subafiliados',
    'pt-BR': 'Seus subafiliados',
  },
  // §8.9e — no name, so the row identifies itself by when it joined instead.
  'subaffiliates.row.joined': { en: 'Joined {date}', es: 'Se unió el {date}', 'pt-BR': 'Entrou em {date}' },
  /*
   * §8.9c — approved, but the referral was never made. Distinct from the
   * applicant's words below: this person is on the platform, and telling
   * their recruiter to wait for an approval that already happened leaves
   * them waiting for a decision nobody is going to make.
   */
  'subaffiliates.row.notLinked': {
    en: 'Joined, but not linked to you yet — the company has to make the referral.',
    es: 'Se unió, pero aún no está vinculado a ti: la empresa tiene que hacer la referencia.',
    'pt-BR': 'Entrou, mas ainda não está vinculado a você — a empresa precisa fazer a indicação.',
  },
  'subaffiliates.row.pending': {
    en: "Waiting for the company's approval",
    es: 'Esperando la aprobación de la empresa',
    'pt-BR': 'Aguardando a aprovação da empresa',
  },
  'subaffiliates.empty': {
    en: 'No sub-affiliates yet. Share your link or invite someone.',
    es: 'Aún no tienes subafiliados. Comparte tu enlace o invita a alguien.',
    'pt-BR': 'Ainda sem subafiliados. Compartilhe seu link ou convide alguém.',
  },
  'subaffiliates.col.overrideEarned': {
    en: 'Your override earned',
    es: 'Tu comisión adicional generada',
    'pt-BR': 'Sua comissão adicional gerada',
  },
  // aria-label only — the row itself reads "Joined {date}" in place of a name.
  'subaffiliates.col.recruit': { en: 'Sub-affiliate', es: 'Subafiliado', 'pt-BR': 'Subafiliado' },
  'subaffiliates.col.sent': { en: 'Sent', es: 'Enviada', 'pt-BR': 'Enviado' },
  'subaffiliates.invitations.panel': { en: 'Invitations', es: 'Invitaciones', 'pt-BR': 'Convites' },
  'subaffiliates.invitations.empty': {
    en: 'No invitations sent yet.',
    es: 'Aún no has enviado invitaciones.',
    'pt-BR': 'Ainda não há convites enviados.',
  },
  'subaffiliates.invitations.joined': { en: 'Joined', es: 'Se unió', 'pt-BR': 'Entrou' },

  /* -------------------------------------------------------------- *
   * §26.12/§26.12a (task A8) — declaring media spend, and the state of
   * every claim. §6.7c: the platform verifies nothing declared here, which
   * is what the intro and every state chip below say plainly.
   * -------------------------------------------------------------- */
  'spend.intro': {
    en:
      'Declare what you spent on media for a spend-share deal and period. This is ' +
      'your own figure — the platform does not verify it. An operator reviews it, ' +
      'and only what they approve is ever paid; a claim nobody has decided on pays ' +
      'nothing.',
    es:
      'Declara lo que gastaste en medios para un contrato de reparto de gasto y un ' +
      'período. Esta es tu propia cifra — la plataforma no la verifica. Un operador ' +
      'la revisa, y solo se paga lo que aprueba; una solicitud que nadie ha decidido ' +
      'no paga nada.',
    'pt-BR':
      'Declare o que você gastou em mídia para um contrato de participação no gasto e ' +
      'um período. Este é o seu próprio número — a plataforma não o verifica. Um ' +
      'operador o revisa, e só é pago o que ele aprova; uma declaração que ninguém ' +
      'decidiu não paga nada.',
  },
  'spend.deal': { en: 'Deal', es: 'Contrato', 'pt-BR': 'Contrato' },
  'spend.period': { en: 'Period', es: 'Período', 'pt-BR': 'Período' },
  'spend.amount': { en: 'Amount spent', es: 'Importe gastado', 'pt-BR': 'Valor gasto' },
  'spend.amount.hint': {
    en: 'Minor units — e.g. 50000 for 500.00.',
    es: 'Unidades menores — p. ej. 50000 para 500,00.',
    'pt-BR': 'Unidades menores — ex.: 50000 para 500,00.',
  },
  'spend.amount.placeholder': {
    en: 'Minor units, e.g. 50000',
    es: 'Unidades menores, p. ej. 50000',
    'pt-BR': 'Unidades menores, ex.: 50000',
  },
  'spend.currency': { en: 'Currency', es: 'Moneda', 'pt-BR': 'Moeda' },
  'spend.evidenceUrl': { en: 'Evidence (optional)', es: 'Evidencia (opcional)', 'pt-BR': 'Evidência (opcional)' },
  'spend.evidenceUrl.hint': {
    en: 'A link to an invoice or ad-platform report, if you have one — this helps the operator decide.',
    es: 'Un enlace a una factura o informe de la plataforma publicitaria, si lo tienes — ayuda al operador a decidir.',
    'pt-BR': 'Um link para uma fatura ou relatório da plataforma de anúncios, se houver — ajuda o operador a decidir.',
  },
  'spend.note': { en: 'Note (optional)', es: 'Nota (opcional)', 'pt-BR': 'Nota (opcional)' },
  'spend.declare': { en: 'Declare spend', es: 'Declarar gasto', 'pt-BR': 'Declarar gasto' },
  'spend.noDeals': {
    en:
      'You have no spend-share deal to declare against yet. Once your account manager ' +
      'proposes one and you accept it, it will appear here.',
    es:
      'Aún no tienes un contrato de reparto de gasto para declarar. Cuando tu gestor de ' +
      'cuenta proponga uno y lo aceptes, aparecerá aquí.',
    'pt-BR':
      'Você ainda não tem um contrato de participação no gasto para declarar. Quando ' +
      'seu gerente de contas propuser um e você aceitar, ele aparecerá aqui.',
  },
  'spend.noPeriods': {
    en: 'No period is available to declare against yet.',
    es: 'Aún no hay ningún período disponible para declarar.',
    'pt-BR': 'Ainda não há nenhum período disponível para declarar.',
  },
  'spend.history': { en: 'Your declarations', es: 'Tus declaraciones', 'pt-BR': 'Suas declarações' },
  'spend.empty': {
    en: 'Nothing declared yet.',
    es: 'Nada declarado todavía.',
    'pt-BR': 'Nada declarado ainda.',
  },
  'spend.col.deal': { en: 'Deal', es: 'Contrato', 'pt-BR': 'Contrato' },
  'spend.col.period': { en: 'Period', es: 'Período', 'pt-BR': 'Período' },
  'spend.col.declared': { en: 'You declared', es: 'Declaraste', 'pt-BR': 'Você declarou' },
  'spend.col.state': { en: 'State', es: 'Estado', 'pt-BR': 'Status' },
  'spend.col.approved': { en: 'Approved', es: 'Aprobado', 'pt-BR': 'Aprovado' },
  'spend.col.reason': { en: 'Reason', es: 'Motivo', 'pt-BR': 'Motivo' },
  'spend.state.declared': { en: 'Awaiting decision', es: 'En espera de decisión', 'pt-BR': 'Aguardando decisão' },
  'spend.state.approved': { en: 'Approved', es: 'Aprobado', 'pt-BR': 'Aprovado' },
  'spend.state.rejected': { en: 'Rejected', es: 'Rechazado', 'pt-BR': 'Rejeitado' },

  /* -------------------------------------------------------------- *
   * 0332 — an account with no programme left to open. The page shown in
   * place of the console when the operator the session was on has closed
   * its programme, or there is no live programme at all.
   * -------------------------------------------------------------- */
  'closed.title.named': {
    en: '{operator} has closed its programme',
    es: '{operator} ha cerrado su programa',
    'pt-BR': '{operator} encerrou o programa',
  },
  // No operator named: the selection ended for another reason (a terminated
  // or deleted profile), so nothing is said to have closed.
  'closed.title.unnamed': {
    en: 'Your programme is no longer available',
    es: 'Tu programa ya no está disponible',
    'pt-BR': 'Seu programa não está mais disponível',
  },
  'closed.choose.lead.named': {
    en: "You can't open {operator} here any more. Your other programmes are not affected.",
    es: 'Ya no puedes abrir {operator} aquí. Tus otros programas no se ven afectados.',
    'pt-BR': 'Você não pode mais abrir {operator} aqui. Seus outros programas não são afetados.',
  },
  'closed.choose.lead.unnamed': {
    en: "You can't open that programme here any more. Your other programmes are not affected.",
    es: 'Ya no puedes abrir ese programa aquí. Tus otros programas no se ven afectados.',
    'pt-BR': 'Você não pode mais abrir esse programa aqui. Seus outros programas não são afetados.',
  },
  // Deleting an operator removes no money record (2.7b, 2.7e): commission,
  // statements and invoices stay. What stops is this person's access to them,
  // so the words promise the record and nothing about payment.
  'closed.records.named': {
    en: 'What you earned with {operator} stays on record, but it can no longer be viewed here.',
    es: 'Lo que ganaste con {operator} sigue registrado, pero ya no se puede ver aquí.',
    'pt-BR': 'O que você ganhou com {operator} continua registrado, mas não pode mais ser visto aqui.',
  },
  'closed.records.unnamed': {
    en: 'What you earned there stays on record, but it can no longer be viewed here.',
    es: 'Lo que ganaste allí sigue registrado, pero ya no se puede ver aquí.',
    'pt-BR': 'O que você ganhou lá continua registrado, mas não pode mais ser visto aqui.',
  },
  'closed.contact.named': {
    en: "For anything you're owed, contact {operator} directly.",
    es: 'Para cualquier importe pendiente, contacta directamente con {operator}.',
    'pt-BR': 'Para qualquer valor a receber, entre em contato diretamente com {operator}.',
  },
  'closed.contact.unnamed': {
    en: "For anything you're owed, contact the company you worked with directly.",
    es: 'Para cualquier importe pendiente, contacta directamente con la empresa con la que trabajabas.',
    'pt-BR': 'Para qualquer valor a receber, entre em contato diretamente com a empresa com que você trabalhava.',
  },
  'closed.choose.panel': {
    en: 'Choose another programme',
    es: 'Elige otro programa',
    'pt-BR': 'Escolha outro programa',
  },
  'closed.choose.open': { en: 'Open {operator}', es: 'Abrir {operator}', 'pt-BR': 'Abrir {operator}' },
  'closed.choose.opening': { en: 'Opening…', es: 'Abriendo…', 'pt-BR': 'Abrindo…' },
  'closed.choose.failed': {
    en: "That programme couldn't be opened. Try again, or choose another.",
    es: 'No se pudo abrir ese programa. Inténtalo de nuevo o elige otro.',
    'pt-BR': 'Não foi possível abrir esse programa. Tente novamente ou escolha outro.',
  },
  'closed.none.title': {
    en: "There's no active programme on this account",
    es: 'No hay ningún programa activo en esta cuenta',
    'pt-BR': 'Não há nenhum programa ativo nesta conta',
  },
  'closed.none.lead.named': {
    en: '{operator} has closed its affiliate programme, and this account has no other programme to open.',
    es: '{operator} ha cerrado su programa de afiliados y esta cuenta no tiene ningún otro programa que abrir.',
    'pt-BR': '{operator} encerrou o programa de afiliados e esta conta não tem outro programa para abrir.',
  },
  'closed.none.lead.unnamed': {
    en: 'The programme on this account is no longer open to you, and there is no other programme to open.',
    es: 'El programa de esta cuenta ya no está disponible para ti y no hay ningún otro programa que abrir.',
    'pt-BR': 'O programa desta conta não está mais disponível para você e não há outro programa para abrir.',
  },
  'closed.none.panel': { en: 'What you can do', es: 'Qué puedes hacer', 'pt-BR': 'O que você pode fazer' },
  // Signup is per programme, from the link that programme publishes; the
  // portal has no directory before sign-in. An existing address applies
  // with the same login (registration.ts, 0255).
  'closed.none.join': {
    en: "To join another brand's programme, apply through the sign-up link that brand gives you, using this email address.",
    es: 'Para unirte al programa de otra marca, solicítalo con el enlace de registro que te dé esa marca, usando esta dirección de correo.',
    'pt-BR': 'Para entrar no programa de outra marca, inscreva-se pelo link de cadastro que essa marca fornecer, usando este endereço de e-mail.',
  },
  'closed.lookup.failed': {
    en: "We couldn't check whether this account has another programme.",
    es: 'No pudimos comprobar si esta cuenta tiene otro programa.',
    'pt-BR': 'Não foi possível verificar se esta conta tem outro programa.',
  },
  'closed.lookup.retry': { en: 'Try again', es: 'Reintentar', 'pt-BR': 'Tentar novamente' },
} as const satisfies Catalogue;

export type AffiliateMessageKey = keyof typeof AFFILIATE_MESSAGES;

const cache = new Map<string, Translator<typeof AFFILIATE_MESSAGES>>();

/**
 * One string, in whatever language this tab is reading.
 *
 * Built through `tabTranslator` rather than from `displayLocale()` alone, so
 * the chain is chosen → base language → operator default → `en` (§2.8d). A
 * translator handed only the display locale had already folded the operator
 * default into it, and so never looked there for a message the chosen
 * language lacks: somebody reading `pt-PT` under a `pt-BR` operator got
 * English, with a complete Portuguese translation one hop away.
 */
export function t(key: AffiliateMessageKey, params?: Params): string {
  return tabTranslator(AFFILIATE_MESSAGES, cache)(key, params);
}
